<template>
  <v-dialog
    :model-value="modelValue"
    :max-width="view === 'editor' ? 1240 : 960"
    persistent
    class="tpsv2-overlay"
    :class="{ 'tpsv2-overlay--editor': view === 'editor' }"
    data-testid="tpsv2-dialog"
    @update:model-value="onDialogModel"
  >
    <!-- The card owns its own scrolling (see the flex notes in <style>) instead of `scrollable`,
         so the section rail, the save bar and the footer can stay put while the fields scroll. -->
    <v-card class="tpsv2" :class="{ 'tpsv2--editor': view === 'editor' }" @keydown.esc="close">
      <!-- ══════════════ title bar ══════════════ -->
      <div class="tpsv2-head bg-primary text-white">
        <!-- `icon` stays BOOLEAN: passing it the icon name makes VBtn render that icon INSTEAD of
             the default slot, which would silently drop the tooltip below. -->
        <v-btn
          v-if="view === 'editor'"
          icon
          variant="text"
          density="comfortable"
          color="white"
          data-testid="tpsv2-back-icon"
          aria-label="กลับไปหน้ารายการเวอร์ชัน"
          @click="backToList"
        >
          <v-icon>mdi-arrow-left</v-icon>
          <v-tooltip activator="parent" location="bottom">กลับไปรายการเวอร์ชัน</v-tooltip>
        </v-btn>
        <div v-else class="tpsv2-head-mark">
          <v-icon size="20">mdi-tune-variant</v-icon>
        </div>

        <div class="tpsv2-head-text">
          <h2 class="tpsv2-head-title">{{ headerTitle }}</h2>
          <p class="tpsv2-head-sub">{{ headerSubtitle }}</p>
        </div>

        <span
          v-if="view === 'editor' && editingIsActive"
          class="tpsv2-livetag"
          data-testid="tpsv2-editor-active-chip"
        >
          <span class="tpsv2-livedot" aria-hidden="true" />
          ใช้งานอยู่
        </span>

        <v-btn
          icon="mdi-close"
          variant="text"
          density="comfortable"
          color="white"
          data-testid="tpsv2-dialog-close-icon"
          aria-label="ปิดหน้าต่าง"
          @click="close"
        />
      </div>

      <!-- ══════════════ LEVEL 1 — version list ══════════════ -->
      <template v-if="view === 'list'">
        <div class="tpsv2-listbar">
          <div class="tpsv2-listmeta">
            <span class="tpsv2-listcount">
              <template v-if="listLoading">กำลังโหลด…</template>
              <template v-else>{{ versions.length }} เวอร์ชัน</template>
            </span>
            <span v-if="activeVersion" class="tpsv2-listactive">
              <span class="tpsv2-livedot tpsv2-livedot--solid" aria-hidden="true" />
              ใช้งานอยู่ · <strong>{{ activeVersion.versionCode || activeVersion.id }}</strong>
            </span>
            <span v-else-if="!listLoading && versions.length" class="tpsv2-listactive is-none">
              ยังไม่มีเวอร์ชันที่ใช้งาน
            </span>
          </div>
          <v-btn
            variant="outlined"
            prepend-icon="mdi-import"
            data-testid="tpsv2-import-btn"
            @click="openImport"
          >
            นำเข้า
          </v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            prepend-icon="mdi-plus"
            data-testid="tpsv2-add-version-btn"
            @click="openCreate"
          >
            เพิ่มเวอร์ชัน
          </v-btn>
        </div>

        <div class="tpsv2-listscroll">
          <p class="tpsv2-hintline">
            <v-icon size="15">mdi-information-outline</v-icon>
            <span>
              โหนด Tax Planning V2 และ Tax Playground ใช้ “เวอร์ชันที่ใช้งานอยู่”
              เว้นแต่จะปักเวอร์ชันไว้เอง
            </span>
          </p>

          <v-alert
            v-if="listError"
            type="error"
            variant="tonal"
            density="compact"
            class="tpsv2-alert mb-4"
            data-testid="tpsv2-list-error"
          >
            {{ listError }}
            <template #append>
              <v-btn size="small" variant="text" data-testid="tpsv2-list-reload-btn" @click="loadVersions">
                ลองใหม่
              </v-btn>
            </template>
          </v-alert>

          <!-- An import/restore that SUCCEEDED but has something the admin must read. Deliberately
               NOT the success channel: "นำเข้าแล้ว" next to "โมเดลหาไม่เจอ" would read as green. -->
          <v-alert
            v-if="listWarnings.length"
            type="warning"
            variant="tonal"
            density="compact"
            class="tpsv2-alert mb-4"
            data-testid="tpsv2-list-warning"
          >
            <div class="tpsv2-warntitle">{{ listWarningTitle }}</div>
            <ul class="tpsv2-warnlist">
              <li v-for="(w, i) in listWarnings" :key="`lw${i}`">{{ w }}</li>
            </ul>
            <template #append>
              <v-btn
                size="small"
                variant="text"
                aria-label="ปิดข้อความ"
                data-testid="tpsv2-list-warning-dismiss"
                @click="clearListWarnings"
              >
                ปิด
              </v-btn>
            </template>
          </v-alert>

          <v-alert
            v-if="listMessage"
            type="success"
            variant="tonal"
            density="compact"
            class="tpsv2-alert mb-4"
            data-testid="tpsv2-list-message"
          >
            {{ listMessage }}
          </v-alert>

          <!-- Skeleton rows, not a lone spinner: the table's shape stays on screen while it loads. -->
          <div v-if="listLoading" class="tpsv2-skeleton" data-testid="tpsv2-list-loading">
            <v-skeleton-loader type="table-heading, table-row@3" />
          </div>

          <!-- The table element stays mounted even with zero rows: `tpsv2-version-table` is the
               container selector the specs wait on before asserting the empty state. -->
          <v-table v-else density="comfortable" class="tpsv2-table" data-testid="tpsv2-version-table">
            <thead>
              <tr>
                <th class="text-start">เวอร์ชัน</th>
                <th class="text-start" style="width: 136px">สถานะ</th>
                <th class="text-start" style="width: 186px">แก้ไขล่าสุด</th>
                <th class="text-end" style="width: 250px">จัดการ</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!versions.length" class="tpsv2-emptyrow">
                <td colspan="4">
                  <div class="tpsv2-empty" data-testid="tpsv2-version-empty">
                    <v-icon size="30" class="tpsv2-empty-icon">mdi-layers-plus</v-icon>
                    <h3 class="tpsv2-empty-title">ยังไม่มีเวอร์ชันการตั้งค่า</h3>
                    <p class="tpsv2-empty-text">
                      สร้างเวอร์ชันแรกเพื่อเริ่มแก้ไขข้อความและ config ของ Tax Planning V2 —
                      ทุกช่องจะมีค่าเริ่มต้นของระบบใส่ไว้ให้
                    </p>
                    <v-btn color="primary" variant="elevated" prepend-icon="mdi-plus" @click="openCreate">
                      เพิ่มเวอร์ชันแรก
                    </v-btn>
                  </div>
                </td>
              </tr>
              <tr
                v-for="v in versions"
                :key="v.id"
                :class="{ 'tpsv2-row--active': v.isActive }"
                :data-testid="`tpsv2-version-row-${v.id}`"
              >
                <td>
                  <button
                    type="button"
                    class="tpsv2-vcode"
                    :data-testid="`tpsv2-open-btn-${v.id}`"
                    :aria-label="`แก้ไขเวอร์ชัน ${v.versionCode || v.id}`"
                    @click="openEditor(v)"
                  >
                    {{ v.versionCode || '-' }}
                  </button>
                  <div class="tpsv2-vdesc">{{ v.description || 'ไม่มีคำอธิบาย' }}</div>
                </td>
                <td>
                  <span
                    v-if="v.isActive"
                    class="tpsv2-statustag"
                    :data-testid="`tpsv2-active-chip-${v.id}`"
                  >
                    <span class="tpsv2-livedot tpsv2-livedot--solid" aria-hidden="true" />
                    ใช้งานอยู่
                  </span>
                  <span v-else class="tpsv2-muted">ไม่ได้ใช้งาน</span>
                </td>
                <td class="tpsv2-when">
                  <div>{{ formatDate(v.updatedDate) || '—' }}</div>
                  <div v-if="v.updatedBy" class="tpsv2-muted">โดย {{ v.updatedBy }}</div>
                </td>
                <td>
                  <div class="tpsv2-rowactions">
                    <v-btn
                      icon
                      variant="text"
                      size="small"
                      color="primary"
                      :data-testid="`tpsv2-edit-btn-${v.id}`"
                      :aria-label="`แก้ไขเวอร์ชัน ${v.versionCode || v.id}`"
                      @click="openEditor(v)"
                    >
                      <v-icon size="19">mdi-pencil-outline</v-icon>
                      <v-tooltip activator="parent" location="top">แก้ไข</v-tooltip>
                    </v-btn>
                    <v-btn
                      icon
                      variant="text"
                      size="small"
                      color="success"
                      :disabled="v.isActive || busyId !== null"
                      :loading="busyId === v.id"
                      :data-testid="`tpsv2-activate-btn-${v.id}`"
                      :aria-label="`ตั้งเวอร์ชัน ${v.versionCode || v.id} เป็นเวอร์ชันที่ใช้งาน`"
                      @click="activate(v)"
                    >
                      <v-icon size="19">mdi-check-decagram-outline</v-icon>
                      <v-tooltip activator="parent" location="top">
                        {{ v.isActive ? 'เวอร์ชันนี้ใช้งานอยู่แล้ว' : 'ตั้งเป็นเวอร์ชันที่ใช้งาน' }}
                      </v-tooltip>
                    </v-btn>
                    <span class="tpsv2-rowactions-sep" aria-hidden="true" />
                    <v-btn
                      icon
                      variant="text"
                      size="small"
                      :data-testid="`tpsv2-history-btn-${v.id}`"
                      :aria-label="`ประวัติการบันทึกของเวอร์ชัน ${v.versionCode || v.id}`"
                      @click="openHistory(v)"
                    >
                      <v-icon size="19">mdi-history</v-icon>
                      <v-tooltip activator="parent" location="top">ประวัติการบันทึก</v-tooltip>
                    </v-btn>
                    <v-btn
                      icon
                      variant="text"
                      size="small"
                      :disabled="exportingId !== null"
                      :loading="exportingId === v.id"
                      :data-testid="`tpsv2-export-btn-${v.id}`"
                      :aria-label="`ส่งออกเวอร์ชัน ${v.versionCode || v.id} เป็นไฟล์`"
                      @click="onExportVersion(v)"
                    >
                      <v-icon size="19">mdi-file-download-outline</v-icon>
                      <v-tooltip activator="parent" location="top">ส่งออกเป็นไฟล์</v-tooltip>
                    </v-btn>
                    <v-btn
                      icon
                      variant="text"
                      size="small"
                      :data-testid="`tpsv2-clone-btn-${v.id}`"
                      :aria-label="`ทำสำเนาเวอร์ชัน ${v.versionCode || v.id}`"
                      @click="openClone(v)"
                    >
                      <v-icon size="19">mdi-content-copy</v-icon>
                      <v-tooltip activator="parent" location="top">ทำสำเนา</v-tooltip>
                    </v-btn>
                    <v-btn
                      icon
                      variant="text"
                      size="small"
                      color="error"
                      :data-testid="`tpsv2-delete-btn-${v.id}`"
                      :aria-label="`ลบเวอร์ชัน ${v.versionCode || v.id}`"
                      @click="openDelete(v)"
                    >
                      <v-icon size="19">mdi-delete-outline</v-icon>
                      <v-tooltip activator="parent" location="top">ลบ</v-tooltip>
                    </v-btn>
                  </div>
                </td>
              </tr>
            </tbody>
          </v-table>
        </div>

        <v-card-actions class="tpsv2-foot">
          <v-spacer />
          <v-btn variant="outlined" data-testid="tpsv2-close-btn" @click="close">
            <v-icon start>mdi-close</v-icon>
            ปิด
          </v-btn>
        </v-card-actions>
      </template>

      <!-- ══════════════ LEVEL 2 — per-version editor ══════════════ -->
      <template v-else>
        <div class="tpsv2-toolbar">
          <v-text-field
            v-model="fieldQuery"
            class="tpsv2-search"
            data-testid="tpsv2-field-search"
            variant="outlined"
            density="compact"
            hide-details
            clearable
            prepend-inner-icon="mdi-magnify"
            placeholder="ค้นหาช่อง — ชื่อไทยหรือ key"
            aria-label="ค้นหาช่องตั้งค่า"
          />
          <!-- The search narrows, it never hides a problem: an invalid field is ALWAYS rendered
               (see fieldVisible) so the query can never hide the one thing blocking save. -->
          <v-btn
            v-if="filterActive"
            variant="text"
            size="small"
            class="tpsv2-clearfilter"
            data-testid="tpsv2-clear-filter"
            @click="clearFilters"
          >
            ล้างตัวกรอง
          </v-btn>
          <v-spacer />
          <v-btn
            variant="outlined"
            size="small"
            prepend-icon="mdi-history"
            :disabled="!editingId"
            data-testid="tpsv2-editor-history-btn"
            @click="openHistoryFromEditor"
          >
            ประวัติการบันทึก
          </v-btn>
        </div>

        <div class="tpsv2-split">
          <v-tabs
            v-model="tab"
            :direction="railDirection"
            :show-arrows="railDirection === 'horizontal' ? true : 'never'"
            color="primary"
            class="tpsv2-rail"
            data-testid="tpsv2-tabs"
          >
            <v-tab
              v-for="t in tabs"
              :key="t.value"
              :value="t.value"
              :data-testid="t.testid"
              class="tpsv2-railtab"
              :class="{ 'is-dimmed': fieldQuery && !matchCountOf(t) }"
            >
              <v-icon start size="18">{{ t.icon }}</v-icon>
              <span class="tpsv2-railtab-label">{{ t.label }}</span>
              <v-icon v-if="errorCountOf(t)" size="15" color="error" class="tpsv2-railtab-alert">
                mdi-alert-circle
              </v-icon>
            </v-tab>
          </v-tabs>

          <div class="tpsv2-scroll">
            <div v-if="loading" class="tpsv2-pane" data-testid="tpsv2-loading">
              <v-skeleton-loader
                type="heading, list-item-three-line, list-item-three-line, list-item-three-line"
              />
            </div>

            <div v-else class="tpsv2-pane">
              <v-alert
                v-if="loadError"
                type="error"
                variant="tonal"
                density="compact"
                class="tpsv2-alert mb-4"
                data-testid="tpsv2-load-error"
              >
                {{ loadError }}
                <template #append>
                  <v-btn size="small" variant="text" data-testid="tpsv2-reload-btn" @click="reloadEditor">
                    ลองใหม่
                  </v-btn>
                </template>
              </v-alert>

              <!-- No defaults = the controls cannot be prefilled AND `payloadValue` has nothing to
                   compare against, so an untouched field could be written back as an override.
                   Saving stays blocked until this clears. -->
              <v-alert
                v-if="defaultsError"
                type="error"
                variant="tonal"
                density="compact"
                class="tpsv2-alert mb-4"
                data-testid="tpsv2-defaults-error"
              >
                {{ defaultsError }}
                <template #append>
                  <v-btn size="small" variant="text" data-testid="tpsv2-defaults-retry-btn" @click="retryDefaults">
                    ลองใหม่
                  </v-btn>
                </template>
              </v-alert>

              <v-window v-model="tab" :disabled="Boolean(loadError)" class="tpsv2-window">
                <v-window-item
                  v-for="t in tabs"
                  :key="t.value"
                  :value="t.value"
                  transition="fade-transition"
                  reverse-transition="fade-transition"
                >
                  <!-- Tab blurbs run 2-3 lines; clamped to one by default so the fields stay above
                       the fold. Nothing is removed — the chevron expands it. -->
                  <div
                    class="tpsv2-note"
                    :class="`is-${t.noteType}`"
                    :data-testid="`tpsv2-note-${t.value}`"
                  >
                    <v-icon size="16" :color="t.noteType === 'warning' ? 'warning' : undefined">
                      {{ t.noteType === 'warning' ? 'mdi-alert-outline' : 'mdi-information-outline' }}
                    </v-icon>
                    <p class="tpsv2-note-text" :class="{ 'is-clamped': !noteExpanded[t.value] }">
                      {{ t.note }}
                    </p>
                    <v-btn
                      variant="text"
                      size="small"
                      density="comfortable"
                      class="tpsv2-note-toggle"
                      :icon="noteExpanded[t.value] ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                      :aria-label="noteExpanded[t.value] ? 'ย่อคำอธิบายแท็บ' : 'อ่านคำอธิบายแท็บทั้งหมด'"
                      :data-testid="`tpsv2-note-toggle-${t.value}`"
                      @click="toggleNote(t.value)"
                    />
                  </div>

                  <!-- Provider/Model come from the SAME catalog the Flow node inspector uses. If that
                       call fails the dialog still works (values are kept, lists are just empty) —
                       say so instead of leaving two mysteriously empty dropdowns. -->
                  <v-alert
                    v-if="t.value === 'llm' && catalogError"
                    type="warning"
                    variant="tonal"
                    density="compact"
                    class="tpsv2-alert mb-4"
                    data-testid="tpsv2-catalog-error"
                  >
                    {{ catalogError }}
                    <template #append>
                      <v-btn
                        size="small"
                        variant="text"
                        data-testid="tpsv2-catalog-reload-btn"
                        @click="loadModelCatalog"
                      >
                        ลองใหม่
                      </v-btn>
                    </template>
                  </v-alert>

                  <!-- version identity — NOT a config field: blank here means "คงเดิม", never
                       "กลับไปใช้ค่าเริ่มต้น", and it is never hidden by the filters. -->
                  <section v-if="t.value === 'general'" class="tpsv2-group">
                    <div class="tpsv2-group-head">
                      <h3 class="tpsv2-group-title">ข้อมูลเวอร์ชัน</h3>
                      <span class="tpsv2-group-rule" aria-hidden="true" />
                    </div>
                    <div class="tpsv2-fields tpsv2-fields--identity">
                      <div>
                        <div class="tpsv2-field">
                          <div class="tpsv2-field-head">
                            <span class="tpsv2-field-label">รหัสเวอร์ชัน <em>*</em></span>
                          </div>
                          <v-text-field
                            :model-value="identity.versionCode"
                            data-testid="tpsv2-field-versionCode"
                            aria-label="รหัสเวอร์ชัน"
                            variant="outlined"
                            density="compact"
                            hide-details="auto"
                            prepend-inner-icon="mdi-tag-outline"
                            :error-messages="versionCodeErrors"
                            @update:model-value="identity.versionCode = $event"
                          />
                          <p class="tpsv2-hint">ห้ามซ้ำกับเวอร์ชันอื่น</p>
                        </div>
                      </div>
                      <div>
                        <div class="tpsv2-field">
                          <div class="tpsv2-field-head">
                            <span class="tpsv2-field-label">คำอธิบาย</span>
                          </div>
                          <v-text-field
                            :model-value="identity.description"
                            data-testid="tpsv2-field-description"
                            aria-label="คำอธิบายเวอร์ชัน"
                            variant="outlined"
                            density="compact"
                            hide-details="auto"
                            prepend-inner-icon="mdi-text-box-outline"
                            @update:model-value="identity.description = $event"
                          />
                          <p class="tpsv2-hint">อธิบายว่าเวอร์ชันนี้ต่างจากเวอร์ชันอื่นอย่างไร</p>
                        </div>
                      </div>
                    </div>
                  </section>

                  <section
                    v-for="g in visibleGroupsOf(t)"
                    :key="g.name"
                    class="tpsv2-group"
                    :data-testid="`tpsv2-group-${t.value}-${g.index}`"
                  >
                    <div class="tpsv2-group-head">
                      <h3 class="tpsv2-group-title">{{ g.name }}</h3>
                      <span class="tpsv2-group-rule" aria-hidden="true" />
                    </div>

                    <div class="tpsv2-fields">
                      <div
                        v-for="f in g.visible"
                        :key="f.key"
                        :class="{ 'is-wide': (f.cols || 12) >= 12 }"
                      >
                        <div
                          class="tpsv2-field"
                          :class="{
                            'is-invalid': errorsByKey[f.key].length > 0,
                          }"
                        >
                          <div class="tpsv2-field-head">
                            <span class="tpsv2-field-label">{{ f.label }}</span>
                          </div>

                          <v-textarea
                            v-if="f.kind === 'textarea'"
                            :model-value="displayValue(f)"
                            :data-testid="`tpsv2-field-${f.key}`"
                            :aria-label="f.label"
                            :aria-describedby="`tpsv2-hint-${f.key}`"
                            :error-messages="errorsByKey[f.key]"
                            hide-details="auto"
                            variant="outlined"
                            density="compact"
                            rows="4"
                            auto-grow
                            @update:model-value="(v) => onEdit(f, v)"
                          />

                          <!-- Provider / Model name — a catalog picker, NEVER a free-text box.
                               The value stored is the catalog's own `modelName`, i.e. the full
                               "ชื่อ|id" key ("gpt-5.4-mini|gpt-5.4-mini"), exactly what the Flow node
                               inspector stores (Flow.vue:527 `item-value="modelName"`). Free typing is
                               what produced values the catalog lookup could not resolve at all — no
                               provider template, no api-version, and a 400 on every LLM call. -->
                          <v-autocomplete
                            v-else-if="f.kind === 'provider' || f.kind === 'model'"
                            :model-value="displayValue(f)"
                            :items="catalogItems(f)"
                            item-title="title"
                            item-value="value"
                            :item-props="catalogItemProps"
                            :data-testid="`tpsv2-field-${f.key}`"
                            :aria-label="f.label"
                            :aria-describedby="`tpsv2-hint-${f.key}`"
                            :error-messages="errorsByKey[f.key]"
                            :loading="catalogLoading"
                            :prepend-inner-icon="f.kind === 'provider' ? 'mdi-cloud-outline' : 'mdi-brain'"
                            hide-details="auto"
                            variant="outlined"
                            density="compact"
                            auto-select-first
                            @update:model-value="(v) => onEdit(f, v)"
                          />

                          <!-- บอทจาก Model Creator — ONE picker that writes BOTH `Model ID` and
                               `Model display ID` of its group (see onPickGptModel). The two number
                               boxes it replaces are still real saved columns, just not rendered:
                               typing them by hand is how a pair ended up pointing at two different
                               bots. `clearable` IS the revert gesture — it blanks both, and blank
                               goes back as `null`. -->
                          <v-autocomplete
                            v-else-if="f.kind === 'gptModelRef'"
                            :model-value="displayValue(f)"
                            :items="gptModelItemsWithCurrent(f)"
                            item-title="title"
                            item-value="value"
                            clearable
                            :data-testid="`tpsv2-field-${f.key}`"
                            :aria-label="f.label"
                            :aria-describedby="`tpsv2-hint-${f.key}`"
                            :error-messages="errorsByKey[f.key]"
                            :loading="catalogLoading"
                            prepend-inner-icon="mdi-robot"
                            hide-details="auto"
                            variant="outlined"
                            density="compact"
                            auto-select-first
                            @update:model-value="(v) => onPickGptModel(f, v)"
                          />

                          <v-select
                            v-else-if="f.kind === 'bool' || f.kind === 'select'"
                            :model-value="displayValue(f)"
                            :items="f.kind === 'bool' ? BOOL_ITEMS : f.options"
                            item-title="title"
                            item-value="value"
                            :data-testid="`tpsv2-field-${f.key}`"
                            :aria-label="f.label"
                            :aria-describedby="`tpsv2-hint-${f.key}`"
                            :error-messages="errorsByKey[f.key]"
                            hide-details="auto"
                            variant="outlined"
                            density="compact"
                            @update:model-value="(v) => onEdit(f, v)"
                          />

                          <!-- กติกาตรวจก่อนส่ง — ตารางที่ประกอบ JSON ให้เอง แทนการให้แอดมินพิมพ์ JSON ดิบ
                               (ผู้ใช้พิมพ์ regex ตรง ๆ ไม่ต้อง escape backslash คู่ · ลำดับแถว = ลำดับรัน)
                               🔴 มันยังคืนค่าเป็น "string ของ JSON" เหมือนเดิมทุกประการ — `values[f.key]`
                               จึงยังเป็น string ทั้งแผง และ displayValue / onEdit / isAtDefault /
                               payloadValue / fieldErrors / applyDto ไม่ต้องรู้จัก kind นี้เลย
                               🔴 `data-testid` ตกลงบน root ของคอมโพเนนต์ (single root) ซึ่งมี input อยู่ข้างใน
                               ตามที่ `jumpToField` ต้องการ (`querySelector('input, textarea')`)
                               🔴 ส่ง '' เมื่อค่ายังเท่าค่าเริ่มต้น — ตัวแก้นี้ถือ '' เป็น "ยังไม่ override" (โชว์ชุดในโค้ดแบบ
                               อ่านอย่างเดียว + ปุ่มแก้เป็นของตัวเอง) แต่ `applyDto` เติมค่าเริ่มต้นลงคอลัมน์ที่เป็น NULL
                               ไว้แล้ว ถ้าส่งตรง ๆ จะขึ้นว่า "กติกาของเวอร์ชันนี้ N ข้อ" ทั้งที่ DB ไม่ได้เก็บอะไร และแก้
                               นิดเดียวก็เซฟสำเนาชุดเริ่มต้นเป็น override (ข้อความ serialize ไม่เคยตรงกับของ C#) -->
                          <TaxOutputGuardRulesEditor
                            v-else-if="f.kind === 'guardRules'"
                            :model-value="isAtDefault(f.key) ? '' : displayValue(f)"
                            :default-json="defaultOf(f.key) || ''"
                            :error-messages="errorsByKey[f.key]"
                            :data-testid="`tpsv2-field-${f.key}`"
                            @update:model-value="(v) => onEdit(f, v)"
                          />

                          <!-- ความรู้ที่ใช้ (Knowledge) — เลือกเป็น "หมวดย่อยเอกสาร" (DocumentCategory → Title)
                               ชุดเดียวกับที่หน้า Tax Question V2 ใช้ แล้วประกอบ JSON array ให้เอง แทนการให้แอดมิน
                               พิมพ์ ["Tax:Personal Income Tax"] ด้วยมือ
                               🔴 `values[f.key]` ยังเป็น "string ของ JSON" เหมือนทุกช่องในแผงนี้ — displayValue
                                  parse ให้เป็น array ตอนแสดง / onEdit stringify กลับตอนแก้ (ต่างจาก kind
                                  'guardRules' ที่คอมโพเนนต์ลูกคืน string มาให้ตรง ๆ จึงไม่ต้องแตะ 6 ฟังก์ชันนั้น)
                               🔴 ไม่เลือกอะไรเลย = '' ⇒ `isAtDefault` ⇒ save ส่ง `null` ไม่ใช่ "[]"
                                  ("ว่าง = ไม่ใช้ knowledge" ตามกติกาเดียวกับทั้งแผง: null = ไม่มี override)
                               🔴 token ที่บันทึกไว้แต่ไม่อยู่ในแคตตาล็อก (เอกสารถูกลบ / สิทธิ์แอดมินคนละชุด)
                                  ถูก PREPEND เป็นตัวเลือกของตัวเอง ด้วยเหตุผลเดียวกับ gptModelItemsWithCurrent —
                                  ค่าที่หายจากจอคือค่าที่ save ครั้งถัดไปจะทับทิ้งเงียบ ๆ -->
                          <v-autocomplete
                            v-else-if="f.kind === 'knowledgeCategories'"
                            :model-value="displayValue(f)"
                            :items="knowledgeCategoryItems(f)"
                            item-title="title"
                            item-value="value"
                            multiple
                            chips
                            closable-chips
                            :data-testid="`tpsv2-field-${f.key}`"
                            :aria-label="f.label"
                            :aria-describedby="`tpsv2-hint-${f.key}`"
                            :error-messages="errorsByKey[f.key]"
                            :loading="knowledgeCatalogLoading"
                            prepend-inner-icon="mdi-bookshelf"
                            no-data-text="ไม่พบหมวดย่อยเอกสาร"
                            hide-details="auto"
                            variant="outlined"
                            density="compact"
                            @update:model-value="(v) => onEdit(f, v)"
                          />

                          <v-text-field
                            v-else
                            :model-value="displayValue(f)"
                            :type="f.kind === 'int' || f.kind === 'float' ? 'number' : 'text'"
                            :step="f.kind === 'float' ? 'any' : undefined"
                            :min="f.min"
                            :max="f.max"
                            :maxlength="f.maxlength"
                            :counter="f.maxlength"
                            :data-testid="`tpsv2-field-${f.key}`"
                            :aria-label="f.label"
                            :aria-describedby="`tpsv2-hint-${f.key}`"
                            :error-messages="errorsByKey[f.key]"
                            hide-details="auto"
                            variant="outlined"
                            density="compact"
                            @update:model-value="(v) => onEdit(f, v)"
                          />

                          <!-- The two numbers the picker just wrote, spelled out. They are what the
                               runtime and the token report actually read, so they stay visible even
                               though the boxes that used to hold them are gone. -->
                          <p
                            v-if="f.kind === 'gptModelRef'"
                            class="tpsv2-hint"
                            :data-testid="`tpsv2-modelref-ids-${f.key}`"
                          >
                            Model ID {{ values[f.idKey] || '—' }} ·
                            Model display ID {{ values[f.displayKey] || '—' }}
                          </p>

                          <!-- A value already in the DB may not be an exact catalog key (e.g. the
                               hand-typed "gpt-5.4-mini"). Never swallow it: it stays selected and
                               visible, and this banner reports how it really resolves — info when the
                               backend still matches it by the head before `|`, warning only when
                               nothing matches — plus the exact key it corresponds to. -->
                          <v-alert
                            v-if="catalogWarning(f)"
                            :type="catalogAlertType(f)"
                            variant="tonal"
                            density="compact"
                            class="tpsv2-alert mt-2"
                            :data-testid="`tpsv2-catalog-warning-${f.key}`"
                          >
                            <div class="d-flex align-center flex-wrap ga-2">
                              <span class="flex-grow-1">{{ catalogWarning(f) }}</span>
                              <v-btn
                                v-if="catalogSuggestion(f)"
                                size="small"
                                variant="tonal"
                                :color="catalogAlertType(f)"
                                :data-testid="`tpsv2-catalog-fix-${f.key}`"
                                @click="applyCatalogSuggestion(f)"
                              >
                                ใช้ค่า "{{ catalogSuggestion(f).value }}"
                              </v-btn>
                            </div>
                          </v-alert>

                          <p :id="`tpsv2-hint-${f.key}`" class="tpsv2-hint">{{ fieldHint(f) }}</p>
                        </div>
                      </div>
                    </div>
                  </section>

                  <!-- TIME CONTEXT — การ์ดสวิตช์ + dropdown ที่ยกทรงมาจาก node AI Model
                       (`views/Flow.vue:1308-1358`) เพื่อให้คนตั้งค่าเจอหน้าตาเดียวกันทั้งสองที่
                       · สวิตช์ไม่ใช่คอลัมน์ — มันคือ "มี `timeZone` ไหม" ในรูปสวิตช์ (ดู `onToggleTimeContext`)
                       · 🔴 section นี้เป็น hard-code เหมือน "ข้อมูลเวอร์ชัน" ด้านบน จึง **ไม่ถูกกรอง
                         ด้วยช่องค้นหา และไม่ถูกนับใน `matchCountOf`** — ยอมรับได้ (เป็นแบบเดียวกับที่นั่น)
                         แต่ error ของมันถูกนับใน `errorCountOf` เพื่อไม่ให้ปุ่มบันทึกโดนบล็อกแบบไร้เบาะแส -->
                  <section v-if="t.value === 'llm'" class="tpsv2-group">
                    <div class="tpsv2-group-head">
                      <h3 class="section-label">Time Context</h3>
                      <span class="tpsv2-group-rule" aria-hidden="true" />
                    </div>

                    <div class="switch-option-compact">
                      <div
                        class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                        :class="{ 'switch-active-bg': timeContextEnabled }"
                      >
                        <div class="d-flex align-center">
                          <v-avatar
                            size="28"
                            :color="timeContextEnabled ? 'success' : 'grey-lighten-1'"
                            class="me-3"
                          >
                            <v-icon
                              :color="timeContextEnabled ? 'white' : 'grey-darken-1'"
                              size="16"
                            >mdi-clock-outline</v-icon>
                          </v-avatar>
                          <div>
                            <div class="switch-title">Time Context</div>
                            <div class="switch-description">Enable time awareness for AI responses</div>
                          </div>
                        </div>
                        <v-switch
                          :model-value="timeContextEnabled"
                          data-testid="tpsv2-switch-timeContext"
                          aria-label="Time Context"
                          color="success"
                          hide-details
                          density="compact"
                          inset
                          @update:model-value="onToggleTimeContext"
                        />
                      </div>
                    </div>

                    <div v-if="timeContextEnabled" class="mt-3">
                      <v-select
                        :model-value="displayTimeZone"
                        :items="timeZoneItems"
                        item-title="name"
                        item-value="value"
                        label="Time Zone"
                        data-testid="tpsv2-field-timeZone"
                        aria-label="Time zone"
                        :error-messages="errorsByKey.timeZone"
                        variant="outlined"
                        density="comfortable"
                        hide-details="auto"
                        prepend-inner-icon="mdi-earth"
                        placeholder="Select your timezone"
                        @update:model-value="onPickTimeZone"
                      >
                        <template #selection="{ item }">
                          <div class="d-flex align-center">
                            <v-icon size="small" color="primary" class="me-2">mdi-map-marker</v-icon>
                            <span class="font-weight-medium">{{ item.raw.name }}</span>
                            <!-- ป้ายเมือง — ผ่าน `timeZoneRegion` เสมอ เพราะค่าเดิมอาจไม่มี `/` -->
                            <v-chip
                              v-if="timeZoneRegion(item.raw.value)"
                              size="x-small"
                              color="grey-darken-1"
                              text-color="white"
                              class="ml-2"
                            >
                              {{ timeZoneRegion(item.raw.value) }}
                            </v-chip>
                          </div>
                        </template>
                      </v-select>
                      <p class="tpsv2-hint">Select your timezone for time-aware responses</p>
                    </div>
                  </section>

                  <div v-if="!visibleGroupsOf(t).length" class="tpsv2-nomatch">
                    <v-icon size="22">mdi-filter-remove-outline</v-icon>
                    <p>ไม่มี<strong>ช่องตั้งค่า</strong>ในหมวดนี้ที่ตรงกับตัวกรอง</p>
                    <v-btn variant="text" size="small" color="primary" @click="clearFilters">
                      ล้างตัวกรอง
                    </v-btn>
                  </div>
                </v-window-item>
              </v-window>
            </div>
          </div>
        </div>

        <!-- Save feedback and the "what is blocking save" list live OUTSIDE the scroller, right
             above the button that caused them — pinned at the top of a 51-field form they would be
             off-screen exactly when they matter (the author is at the bottom, on บันทึก). -->
        <div
          v-if="!loading && invalidEntries.length"
          class="tpsv2-invalid"
          data-testid="tpsv2-invalid-summary"
        >
          <v-icon size="18" color="error">mdi-alert-circle-outline</v-icon>
          <div class="tpsv2-invalid-body">
            <div class="tpsv2-invalid-title">
              ยังบันทึกไม่ได้ — มี {{ invalidEntries.length }} ช่องที่ต้องแก้
            </div>
            <div class="tpsv2-invalid-chips">
              <button
                v-for="e in invalidEntries"
                :key="e.key"
                type="button"
                class="tpsv2-jump"
                :data-testid="`tpsv2-jump-${e.key}`"
                @click="jumpToField(e.key)"
              >
                {{ e.label }}
              </button>
            </div>
          </div>
        </div>

        <div v-if="saveError" class="tpsv2-status is-error" data-testid="tpsv2-save-error">
          <v-icon size="17">mdi-alert-circle-outline</v-icon>
          <span>{{ saveError }}</span>
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" size="small" aria-label="ปิดข้อความ" @click="saveError = ''" />
        </div>
        <div v-else-if="saveMessage" class="tpsv2-status is-ok" data-testid="tpsv2-save-message">
          <v-icon size="17">mdi-check-circle-outline</v-icon>
          <span>{{ saveMessage }}</span>
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" size="small" aria-label="ปิดข้อความ" @click="saveMessage = ''" />
        </div>

        <v-card-actions class="tpsv2-foot">
          <div class="tpsv2-audit" data-testid="tpsv2-audit">
            <v-icon size="15">mdi-history</v-icon>
            <span>{{ auditText }}</span>
          </div>
          <span v-if="isDirty" class="tpsv2-dirty" data-testid="tpsv2-dirty">ยังไม่ได้บันทึก</span>
          <v-spacer />
          <v-btn variant="text" data-testid="tpsv2-back-btn" @click="backToList">
            <v-icon start>mdi-arrow-left</v-icon>
            รายการเวอร์ชัน
          </v-btn>
          <v-btn variant="outlined" data-testid="tpsv2-close-btn" @click="close">
            <v-icon start>mdi-close</v-icon>
            ปิด
          </v-btn>
          <v-btn
            data-testid="tpsv2-save-btn"
            color="primary"
            variant="elevated"
            :loading="saving"
            :disabled="loading || Boolean(loadError) || Boolean(defaultsError)"
            @click="save"
          >
            <v-icon start>mdi-content-save-outline</v-icon>
            บันทึก
          </v-btn>
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>
  <!-- ── create version ─────────────────────────────────────────────────────── -->
  <v-dialog v-model="createDialog" max-width="560px" persistent data-testid="tpsv2-create-dialog">
    <v-card class="tpsv2-sub">
      <div class="tpsv2-head tpsv2-head--sub bg-primary text-white">
        <div class="tpsv2-head-mark"><v-icon size="19">mdi-plus</v-icon></div>
        <div class="tpsv2-head-text">
          <h2 class="tpsv2-head-title">เพิ่มเวอร์ชันการตั้งค่า</h2>
          <p class="tpsv2-head-sub">
            เวอร์ชันใหม่จะถูกสร้างแบบ “ไม่ได้ใช้งาน” เสมอ — ต้องกดตั้งเป็นเวอร์ชันที่ใช้งานเองภายหลัง
          </p>
        </div>
      </div>
      <div class="tpsv2-subbody">
        <v-alert
          v-if="createError"
          type="error"
          variant="tonal"
          density="compact"
          class="tpsv2-alert mb-4"
          data-testid="tpsv2-create-error"
        >
          {{ createError }}
        </v-alert>

        <div class="tpsv2-field">
          <div class="tpsv2-field-head"><span class="tpsv2-field-label">รหัสเวอร์ชัน</span></div>
          <v-text-field
            v-model="createForm.versionCode"
            data-testid="tpsv2-create-code"
            aria-label="รหัสเวอร์ชัน"
            variant="outlined"
            density="compact"
            hide-details="auto"
            prepend-inner-icon="mdi-tag-outline"
            placeholder="เว้นว่าง = ระบบตั้งให้ (V2, V3, ...)"
            persistent-placeholder
          />
        </div>

        <div class="tpsv2-field">
          <div class="tpsv2-field-head"><span class="tpsv2-field-label">คำอธิบาย</span></div>
          <v-text-field
            v-model="createForm.description"
            data-testid="tpsv2-create-description"
            aria-label="คำอธิบายเวอร์ชัน"
            variant="outlined"
            density="compact"
            hide-details="auto"
            prepend-inner-icon="mdi-text-box-outline"
          />
        </div>

        <div class="tpsv2-field">
          <div class="tpsv2-field-head"><span class="tpsv2-field-label">คัดลอกค่าจากเวอร์ชัน</span></div>
          <v-select
            v-model="createForm.cloneFromId"
            :items="cloneSourceItems"
            item-title="title"
            item-value="value"
            data-testid="tpsv2-create-clonefrom"
            aria-label="คัดลอกค่าจากเวอร์ชัน"
            variant="outlined"
            density="compact"
            hide-details="auto"
            prepend-inner-icon="mdi-content-duplicate"
            clearable
          />
          <p class="tpsv2-hint">ไม่เลือก = เริ่มจากค่าเริ่มต้นของระบบทุกช่อง</p>
        </div>
      </div>
      <v-divider />
      <v-card-actions class="tpsv2-foot">
        <v-spacer />
        <v-btn variant="outlined" data-testid="tpsv2-create-cancel" @click="createDialog = false">
          <v-icon start>mdi-close</v-icon>
          ยกเลิก
        </v-btn>
        <v-btn
          color="primary"
          variant="elevated"
          :loading="createSaving"
          data-testid="tpsv2-create-submit"
          @click="submitCreate"
        >
          <v-icon start>mdi-check</v-icon>
          สร้าง
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- ── clone version ──────────────────────────────────────────────────────── -->
  <v-dialog v-model="cloneDialog" max-width="560px" persistent data-testid="tpsv2-clone-dialog">
    <v-card class="tpsv2-sub">
      <div class="tpsv2-head tpsv2-head--sub bg-primary text-white">
        <div class="tpsv2-head-mark"><v-icon size="19">mdi-content-copy</v-icon></div>
        <div class="tpsv2-head-text">
          <h2 class="tpsv2-head-title">ทำสำเนาเวอร์ชัน</h2>
          <p class="tpsv2-head-sub">
            คัดลอกทุกค่าจาก {{ cloneForm.sourceCode }} — สำเนาเริ่มแบบ “ไม่ได้ใช้งาน” เสมอ
          </p>
        </div>
      </div>
      <div class="tpsv2-subbody">
        <v-alert
          v-if="cloneError"
          type="error"
          variant="tonal"
          density="compact"
          class="tpsv2-alert mb-4"
          data-testid="tpsv2-clone-error"
        >
          {{ cloneError }}
        </v-alert>

        <div class="tpsv2-confirm-target">
          <span class="tpsv2-muted">คัดลอกจากเวอร์ชัน</span>
          <span class="tpsv2-confirm-code">{{ cloneForm.sourceCode }}</span>
        </div>

        <div class="tpsv2-field">
          <div class="tpsv2-field-head"><span class="tpsv2-field-label">รหัสเวอร์ชันใหม่</span></div>
          <v-text-field
            v-model="cloneForm.versionCode"
            data-testid="tpsv2-clone-code"
            aria-label="รหัสเวอร์ชันใหม่"
            variant="outlined"
            density="compact"
            hide-details="auto"
            prepend-inner-icon="mdi-tag-plus-outline"
            placeholder="เว้นว่าง = ระบบตั้งให้ (V2, V3, ...)"
            persistent-placeholder
          />
        </div>

        <div class="tpsv2-field">
          <div class="tpsv2-field-head"><span class="tpsv2-field-label">คำอธิบาย</span></div>
          <v-text-field
            v-model="cloneForm.description"
            data-testid="tpsv2-clone-description"
            aria-label="คำอธิบายเวอร์ชัน"
            variant="outlined"
            density="compact"
            hide-details="auto"
            prepend-inner-icon="mdi-text-box-outline"
          />
        </div>
      </div>
      <v-divider />
      <v-card-actions class="tpsv2-foot">
        <v-spacer />
        <v-btn variant="outlined" data-testid="tpsv2-clone-cancel" @click="cloneDialog = false">
          <v-icon start>mdi-close</v-icon>
          ยกเลิก
        </v-btn>
        <v-btn
          color="primary"
          variant="elevated"
          :loading="cloneSaving"
          data-testid="tpsv2-clone-submit"
          @click="submitClone"
        >
          <v-icon start>mdi-check</v-icon>
          ทำสำเนา
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- ── delete confirm (Vuetify dialog — never window.confirm: it blocks the page) ── -->
  <v-dialog v-model="deleteDialog" max-width="520px" persistent data-testid="tpsv2-delete-dialog">
    <v-card class="tpsv2-sub">
      <div class="tpsv2-head tpsv2-head--sub bg-error text-white">
        <div class="tpsv2-head-mark"><v-icon size="19">mdi-alert-circle</v-icon></div>
        <div class="tpsv2-head-text">
          <h2 class="tpsv2-head-title">ยืนยันการลบเวอร์ชัน</h2>
          <p class="tpsv2-head-sub">ลบแล้วกู้คืนไม่ได้</p>
        </div>
      </div>
      <div class="tpsv2-subbody">
        <!-- The backend answers 400 + a Thai reason for both "ไม่พบ" and "ถูกปฏิเสธ";
             show that message verbatim instead of guessing from the HTTP status. -->
        <v-alert
          v-if="deleteError"
          type="error"
          variant="tonal"
          density="compact"
          class="tpsv2-alert mb-4"
          data-testid="tpsv2-delete-error"
        >
          {{ deleteError }}
        </v-alert>

        <p class="tpsv2-warnline">
          <v-icon size="16" color="error">mdi-alert-outline</v-icon>
          <span>ลบแล้ว<strong>กู้คืนไม่ได้</strong></span>
        </p>
        <p class="tpsv2-confirm-lead">ต้องการลบเวอร์ชันนี้ใช่หรือไม่?</p>
        <div class="tpsv2-confirm-target">
          <span class="tpsv2-confirm-code">{{ deleteTarget ? (deleteTarget.versionCode || '-') : '' }}</span>
          <span v-if="deleteTarget && deleteTarget.description" class="tpsv2-muted">
            {{ deleteTarget.description }}
          </span>
        </div>
        <p class="tpsv2-hint">
          เวอร์ชันที่กำลังใช้งานอยู่ และเวอร์ชันสุดท้ายที่เหลือ ลบไม่ได้ — ระบบจะแจ้งเหตุผลกลับมา
        </p>
      </div>
      <v-divider />
      <v-card-actions class="tpsv2-foot">
        <v-spacer />
        <v-btn variant="outlined" data-testid="tpsv2-delete-cancel" @click="deleteDialog = false">
          <v-icon start>mdi-close</v-icon>
          ยกเลิก
        </v-btn>
        <v-btn
          color="error"
          variant="elevated"
          :loading="deleting"
          data-testid="tpsv2-delete-confirm"
          @click="confirmDelete"
        >
          <v-icon start>mdi-delete</v-icon>
          ลบ
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- ── unsaved-changes gate in front of the editor's history button ────────── -->
  <v-dialog
    v-model="historyDirtyDialog"
    max-width="520px"
    persistent
    data-testid="tpsv2-history-dirty-dialog"
  >
    <v-card class="tpsv2-sub">
      <div class="tpsv2-head tpsv2-head--sub bg-warning text-white">
        <div class="tpsv2-head-mark"><v-icon size="19">mdi-alert-outline</v-icon></div>
        <div class="tpsv2-head-text">
          <h2 class="tpsv2-head-title">ยังไม่ได้บันทึก</h2>
          <p class="tpsv2-head-sub">สิ่งที่แก้ค้างไว้ยังไม่อยู่ในประวัติ</p>
        </div>
      </div>
      <div class="tpsv2-subbody">
        <p class="tpsv2-confirm-lead">
          หน้านี้มีการแก้ไขที่ยังไม่ได้บันทึก — ประวัติจะยังไม่มีรอบของการแก้ครั้งนี้
          และถ้ากดกู้คืนจากในหน้าต่างประวัติ ค่าที่ค้างอยู่บนจอจะถูกทับทิ้ง
        </p>
        <p class="tpsv2-hint">ปิดหน้าต่างนี้แล้วกดบันทึกก่อนได้ ถ้ายังไม่อยากทิ้งของที่แก้ไว้</p>
      </div>
      <v-divider />
      <v-card-actions class="tpsv2-foot">
        <v-spacer />
        <v-btn variant="outlined" data-testid="tpsv2-history-dirty-cancel" @click="historyDirtyDialog = false">
          <v-icon start>mdi-close</v-icon>
          ยกเลิก
        </v-btn>
        <v-btn
          color="primary"
          variant="elevated"
          data-testid="tpsv2-history-dirty-confirm"
          @click="confirmOpenHistoryDirty"
        >
          <v-icon start>mdi-history</v-icon>
          เปิดประวัติต่อ
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- ── save history / compare / import (own overlays — see the `--tps-*` note in <style>) ── -->
  <tax-settings-history-v2-dialog
    v-model="historyDialog"
    :settings-version-id="historyVersionId"
    :version-label="historyVersionLabel"
    :version-active="historyVersionActive"
    @restored="onHistoryRestored"
  />

  <tax-settings-import-v2-dialog
    v-model="importDialog"
    @imported="onImported"
  />
</template>

<script>
/**
 * Tax Planning Settings V2 — VERSIONED prompt + config editor (2-level dialog).
 *
 * Opened from /TaxConfigurationV2 (the SETTINGS button).
 *   • LEVEL 1 (`view === 'list'`)   — every version of `tax.TaxPlanningSettingsV2`:
 *     code / description / "ใช้งานอยู่" chip / last edit, plus แก้ไข · ตั้งเป็นเวอร์ชันที่ใช้งาน ·
 *     ทำสำเนา · ลบ, and เพิ่มเวอร์ชัน.
 *   • LEVEL 2 (`view === 'editor'`) — one version's 69 prompt+config columns, grouped into
 *     named blocks under six sections (the left rail), with a free-text search over the fields.
 *
 * The table is SEPARATE from V1's `tax.TaxPlanningSettings`, so nothing here changes V1.
 *
 * ── Override semantics (the whole point of this screen) ───────────────────────────────
 * The backend stores `null` to mean "no override — runtime uses the built-in default" (C#
 * `TaxPromptDefaultsV2` for prompt copy, `TaxPlanningConfigDefaultsV2` for LLM / node prompt /
 * form behaviour). `null` is therefore NOT "empty", and the two must never be confused. The
 * author is never asked to declare that intent — it is DERIVED from the value itself:
 *   • load   — every control is HYDRATED: a stored override is shown as-is, a `null` column is
 *              PREFILLED with the built-in default. The author never edits from a blank box.
 *   • edit   — every control is always editable. There is no toggle and nothing is disabled.
 *   • save   — a key whose current value still equals the built-in default (or is blank) is
 *              sent as `null` = "no override". Only a value that really differs is stored.
 *   • revert — clearing a box is legal, and IS the revert gesture: blank counts as "at default",
 *              goes back as `null`, and can never be written to the column as `''`.
 * So a round-trip with no edits saves exactly as it loaded, which is what keeps the Playground
 * able to tell `config:` from `nodeDefault` (it judges by "is the column empty?", not by value).
 *
 * `saveVersion` is DIRECT-ASSIGN for the whole set: any key missing from the payload is
 * received as `null` and CLEARS that override. The payload is therefore always built by
 * iterating `TAX_PLANNING_SETTINGS_V2_ALL_KEYS` — all 87 keys, every time.
 * `versionCode` / `description` are the exception: they are version IDENTITY, not config, so a
 * blank there means "คงเดิม" and is never read as "กลับไปใช้ค่าเริ่มต้น".
 *
 * 🔴 Key spelling: `modelID` / `modelDisplayID` / `yearID` / `topP` keep a trailing capital
 * (ASP.NET camel-cases only the leading run of capitals) — verified against
 * `Model/AppModel/TaxPlanningSettingsV2Models.cs`. A mis-spelled key is silently dropped on save.
 *
 * 🔴 Tab `label`s and field `label`s are MIRRORED SERVER-SIDE in
 * `BAL/BAL/AppBAL/LHB/TaxPlaygroundV2BAL.cs` → `SettingsColumnLabelThMap` ("แท็บ <tab> → <field
 * label>"), which `TaxPlaygroundPromptViewerV2.vue` renders to users as "ไปแก้ที่ไหน". Renaming
 * one here desyncs that caption with NO build error. `group` is presentation-only and safe to edit.
 * ⚰️ Two entries of that server map (`PhraseQuestionTaskTh` / `PhraseConfirmTaskTh`) no longer have a
 * field here at all — they are kept server-side to LABEL archived prompts, and their copy was rewritten
 * to stop naming a box that was removed. Nothing to keep in sync for those two.
 *
 * 🔴 Provider / Model name are CATALOG PICKERS, never free text. They read the very list the Flow
 * node inspector reads (`$call.GetAllDDModelManagement` → `data.provider` / `data.modelData`) and
 * store the catalog's own `modelName`, i.e. the full `"ชื่อ|id"` key (`"gpt-5.4-mini|gpt-5.4-mini"`),
 * so an author cannot invent a value the runtime has never heard of.
 * `TaxPlanningV2NodeProcessor.BuildGptModelFromConfigAsync` resolves that key in two steps —
 * exact (trimmed, case-insensitive) first, then by the head before `|` on BOTH sides, with the
 * provider as a tie-breaker between same-named entries, never as a filter. So a bare
 * `"gpt-5.4-mini"` (and the built-in default `"gpt-4"`) DOES resolve; only a value that matches
 * neither step leaves `JsonRequest` null + `APIVersion` on a stale fallback and risks a 400.
 * `catalogMatch()` reports those three states verbatim — a stored value is kept, labelled, and
 * offered a one-click upgrade to the full key; it is never silently dropped or rewritten.
 *
 * 🔴 Errors: every version-CRUD failure is HTTP 400 with a Thai `message` (the backend cannot
 * distinguish "ไม่พบ" from "ถูกปฏิเสธ"), so this component always renders `err.message` and never
 * derives copy from the status code. Confirmations use `v-dialog`, never `window.confirm`/`alert`.
 *
 * 🔴 A successful save does NOT close the dialog — it stays on the editor, shows the inline
 * status strip, and still emits `saved` for the host page's snackbar.
 */
import {
  taxPlanningSettingsV2Api,
  TAX_PLANNING_SETTINGS_V2_PROMPT_KEYS,
  TAX_PLANNING_SETTINGS_V2_ALL_KEYS,
} from '../services/taxPlanningSettingsV2Api';
import { metaDocApi } from '../services/metaDocApi';
import { parseUtcDate, fmtThaiDateTimeIso } from '../utils/thaiTime.js';
import TaxSettingsHistoryV2Dialog from './TaxSettingsHistoryV2Dialog.vue';
import TaxSettingsImportV2Dialog from './TaxSettingsImportV2Dialog.vue';
import TaxOutputGuardRulesEditor from './TaxOutputGuardRulesEditor.vue';

/** Marker V1 embeds in message text to carry chips. V2 sends quick replies as structured
 *  data instead, so an embedded marker would be shown to the user as raw text. */
const QUICK_REPLY_MARKER = '|quickReply|';
/** The state messages that go straight to the user (no LLM) — chips are separate data. */
const NO_MARKER_KEYS = [
  'startPromptTh', 'versionChoicePromptTh', 'resumePromptTh', 'historyResumePromptTh', 'yearQuestionPromptTh',
  'oldYearResumePromptTh', 'closedYearStartPromptTh', 'completedOldYearPromptTh',
  'unmatchedYearNoticeTh', 'yearChangeNoticeTh', 'yearSwitchPromptTh',
];

const PROMPT_KEY_SET = new Set(TAX_PLANNING_SETTINGS_V2_PROMPT_KEYS);

/** `{ [key]: value }` for all 87 editable keys — the shape of every per-key map below. */
function blankMap(value) {
  const map = {};
  TAX_PLANNING_SETTINGS_V2_ALL_KEYS.forEach((k) => { map[k] = value; });
  return map;
}

const MARKER_ERROR =
  'ห้ามใส่ marker |quickReply| — V2 ส่งปุ่มตอบเป็นข้อมูลแยก ไม่ฝังในข้อความ (ต่างจาก V1) ให้พิมพ์เฉพาะข้อความ';
/**
 * ช่องที่เก็บ "JSON array" ล้วน ไม่ใช่ prompt — ต่างจากทุกช่องข้อความอื่นตรงที่ค่าที่ผิดรูปไม่ได้ทำให้อ่านยาก
 * เฉย ๆ แต่ทำให้ backend ทิ้งกฎทั้งชุดแบบเงียบ ๆ (ตัวตรวจเป็น fail-soft: parse ไม่ผ่าน ⇒ ข้ามการตรวจทั้งก้อน
 * แล้วส่งข้อความเดิมออกไป) แอดมินจึงจะเห็นแค่ "กฎไม่ทำงาน" โดยไม่มีอะไรฟ้อง — ดักตั้งแต่ตอนบันทึกแทน
 */
const JSON_ARRAY_KEYS = ['outputGuardRulesJson'];
const JSON_ERROR = 'รูปแบบ JSON ไม่ถูกต้อง — ตรวจวงเล็บ [ ] { } เครื่องหมายคำพูด และคอมมาให้ครบ';
const JSON_ARRAY_ERROR = 'รูปแบบ JSON ไม่ถูกต้อง — ต้องเป็น array ของกฎ เช่น [{"id":"…","enabled":true,…}]';
const NUMBER_ERROR = 'ต้องเป็นตัวเลข';
const INT_ERROR = 'ต้องเป็นจำนวนเต็ม';
/**
 * ── kind 'knowledgeCategories' ─────────────────────────────────────────────────────────────────
 * ช่องเลือก "หมวดย่อยเอกสาร" ที่เก็บลงคอลัมน์ …CategoriesJson เป็น **string ของ JSON array**
 * (เช่น `["Tax:Personal Income Tax"]`) — token รูป `"DocumentCategory:Title"` ตัวเดียวกับที่หน้า
 * Tax Question V2 บันทึก และ backend resolve เป็นรายเอกสารสด ๆ ทุกเทิร์น (เอกสารที่เพิ่งอัปโหลดเข้า
 * หมวดย่อยนั้นจึงถูกใช้ทันทีโดยไม่ต้องกลับมาบันทึกซ้ำ)
 * 🔴 ว่าง = `null` ไม่ใช่ `"[]"` — ทั้งสองอย่างแปลว่า "ไม่ใช้ knowledge" เหมือนกัน แต่ null คือ
 *    "ไม่มี override" ตามกติกาเดียวกับทั้งแผง (ดู `payloadValue`)
 */
const KNOWLEDGE_ARRAY_ERROR =
  'รูปแบบไม่ถูกต้อง — ต้องเป็น array ของหมวดย่อยเอกสาร เช่น ["Tax:Personal Income Tax"]';
const KNOWLEDGE_TOKEN_ERROR =
  'รูปแบบไม่ถูกต้อง — ทุกรายการต้องเป็นข้อความ "หมวด:หมวดย่อย" เช่น "Tax:Personal Income Tax"';
/**
 * "string ของ JSON" → array ของ token · ทนทุกกรณี: ว่าง/null/parse ไม่ผ่าน/ไม่ใช่ array ⇒ `[]`
 * 🔴 คืน `[]` เฉย ๆ ไม่ทิ้งค่าเดิม — ค่าดิบยังอยู่ครบใน `values` และ `fieldErrors` เป็นคนขึ้นแดง
 *    (ซึ่งบล็อกปุ่มบันทึก) ค่าที่พังจากฐานข้อมูลจึงไม่มีทางถูกเขียนทับเงียบ ๆ ด้วยค่าว่าง
 */
function parseCategoryTokens(raw) {
  const text = raw === null || raw === undefined ? '' : String(raw).trim();
  if (!text) return [];
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed
    .map((t) => String(t === null || t === undefined ? '' : t).trim())
    .filter((t) => t.length > 0);
}
/** array (หรือค่าที่ parse มาแล้ว) → string ของ JSON ที่บันทึกจริง · ไม่มีตัวเลือกเลย ⇒ `''` */
function stringifyCategoryTokens(list) {
  const out = [];
  (Array.isArray(list) ? list : []).forEach((t) => {
    const token = String(t === null || t === undefined ? '' : t).trim();
    if (token && out.indexOf(token) < 0) out.push(token);
  });
  return out.length ? JSON.stringify(out) : '';
}
/** `maxlength` on the input stops typing, but a value loaded from an older row (or pasted before the
 *  cap existed) can still be over — catch it here so the author sees a Thai field-level message
 *  instead of the backend's 400. Caps MUST match the column widths in
 *  scripts/Tax_TaxPlanningSettingsV2.sql / the `[StringLength]`s on SaveTaxPlanningSettingsV2Request. */
const LENGTH_ERROR = (max) => `ยาวเกินกำหนด — ใส่ได้ไม่เกิน ${max} ตัวอักษร`;
/** ค่าตัวเลขนอกช่วงที่ descriptor ประกาศไว้ (`min`/`max`) — ประกาศข้างเดียวก็บอกได้ */
const RANGE_ERROR = (min, max) => {
  if (typeof min === 'number' && typeof max === 'number') return `ใส่ได้ระหว่าง ${min} ถึง ${max}`;
  if (typeof min === 'number') return `ต้องไม่น้อยกว่า ${min}`;
  return `ต้องไม่เกิน ${max}`;
};
/**
 * The part of a model key before `|` — the catalog stores `"ชื่อ|display"` and the backend falls
 * back to comparing THIS on both sides (case-insensitively) when the full keys differ, so
 * `"gpt-5.4-mini"` still resolves to `"gpt-5.4-mini|gpt-5.4-mini"`. Mirrors `ModelKeyHead` in
 * `TaxPlanningV2NodeProcessor.BuildGptModelFromConfigAsync`.
 */
function modelKeyHead(value) {
  const text = value === null || value === undefined ? '' : String(value);
  const cut = text.indexOf('|');
  return (cut < 0 ? text : text.slice(0, cut)).trim();
}

/** Labels appended to a value shown in the picker that is not an exact entry of the shown list. */
const ITEM_SUFFIX_UNKNOWN = '⚠️ ไม่พบในรายการ';
const ITEM_SUFFIX_SOFT = 'ชื่อไม่เต็มคีย์ (ระบบเทียบให้)';
const ITEM_SUFFIX_SOFT_PROVIDER = 'ต่างแค่ตัวพิมพ์ (ระบบเทียบให้)';
const ITEM_SUFFIX_OTHER_PROVIDER = 'อยู่ภายใต้ provider อื่น';
const CATALOG_ERROR =
  'โหลดรายการ Provider/Model ไม่สำเร็จ — ค่าที่ตั้งไว้ยังอยู่ครบและยังบันทึกได้ ' +
  'แต่ตอนนี้ยังไม่มีตัวเลือกให้เลือกใหม่';

const BOOL_ITEMS = [
  { title: 'เปิด (true)', value: true },
  { title: 'ปิด (false)', value: false },
];

/** เปิดสวิตช์ Time Context ทิ้งไว้โดยไม่เลือกโซนเวลา = ตั้งใจจะเปิดแต่ยังไม่ได้เปิดจริง (คอลัมน์จะเป็น null) */
const TIME_ZONE_REQUIRED_ERROR =
  'เปิด Time Context แล้วต้องเลือกโซนเวลา — ถ้ายังไม่ต้องการให้ AI รู้เวลา ให้ปิดสวิตช์แทน';

/**
 * คีย์ที่ยังถูกบันทึกอยู่แต่ "ไม่มี descriptor ใน `SETTINGS_TABS`" เพราะถูกเรนเดอร์ด้วย section แบบ
 * hard-code ในเทมเพลต — สรุปให้ตัวช่วยที่เดิมค้นหาบ้านของคีย์จาก `tabs` (สรุปช่องที่บันทึกไม่ได้,
 * ปุ่มกระโดดไปที่ช่อง, ป้ายนับ error ข้างแท็บ) ยังชี้ถูกแท็บและมีชื่อไทยให้แสดง
 */
const EXTRA_FIELD_HOMES = {
  timeZone: { label: 'Time zone', tab: 'llm' },
};

/**
 * โซนเวลาชุดเดียวกับ node AI Model (`views/Flow.vue:16970` · `views/ModelManagement.vue:2417`).
 * 🔴 เป็นสำเนาที่ 3 โดยตั้งใจ — โปรเจกต์นี้ยังไม่มี composable/constants กลางสำหรับลิสต์นี้
 *    และการรวมศูนย์อยู่นอกขอบเขตงานนี้
 */
const TIME_ZONE_ITEMS = [
  { value: 'Asia/Bangkok', name: 'Thailand' },
  { value: 'Asia/Hong_Kong', name: 'Hong Kong' },
  { value: 'Asia/Tokyo', name: 'Japan' },
  { value: 'America/New_York', name: 'USA' },
  { value: 'Europe/London', name: 'England' },
  { value: 'Australia/Sydney', name: 'Australia' },
];

/**
 * Tabs → fields. The union of all `key`s is 84 of the 87 direct-assign columns
 * (39 prompt copy + 45 config). `kind` drives both the control rendered and the type sent on
 * save — `int`/`float` are coerced to real JS numbers because System.Text.Json rejects a
 * numeric field sent as a string.
 *
 * ⚰️ It used to be all 51, and "field ⇔ column" was 1:1. Since 2026-08-03 it is a SUBSET:
 * `phraseQuestionTaskTh` / `phraseConfirmTaskTh` are stored, sent and preserved but have no
 * control, because the runtime stopped reading them (see the tombstone in the `ask` tab).
 * `voiceFillModelID` / `voiceFillModelDisplayID` are the other two: sent as `null` every save
 * because the Voice Fill tab deliberately offers no bot picker (see the note in that tab).
 * The save path is driven by `TAX_PLANNING_SETTINGS_V2_ALL_KEYS`, never by these fields, so a
 * column without a field is carried through untouched — do NOT "resync" the two lists.
 * `group` is PRESENTATION ONLY — it splits a long tab into titled blocks (and sets the order
 * fields are rendered in). It is not part of any contract; `label` is (see the 🔴 note above).
 * `hint` documents the `### Marker ###` a prompt is injected under (plus the tokens the backend
 * substitutes) or, for config, which node.Data key it replaced.
 * `maxlength` MIRRORS the nvarchar width of the column. On a free-text field it drives the input
 * cap + counter AND the length check in `fieldErrors`; on the two catalog pickers
 * (`provider` / `modelName`) a cap+counter would be meaningless on a `v-autocomplete`, so it is
 * NOT bound to the control there and only backs the `fieldErrors` check — which then acts purely
 * as a guard for an over-long legacy value loaded from the DB (the longest catalog key is ~44
 * chars, so a picked value can never trip it). Columns typed `nvarchar(max)` (every
 * prompt/textarea) intentionally carry none.
 */
const SETTINGS_TABS = [
  {
    value: 'general',
    label: 'ทั่วไป',
    testid: 'tpsv2-tab-general',
    icon: 'mdi-message-text-outline',
    noteType: 'info',
    note:
      'ชื่อเวอร์ชัน + ข้อความสถานะที่ส่งถึงผู้ใช้ตรง ๆ (ไม่ผ่าน LLM) — ปุ่มตอบด่วนระบบแนบให้เองเป็นข้อมูลแยก ' +
      'ทุกช่องใส่ค่าเริ่มต้นไว้ให้แล้ว แก้ทับได้ทันที · ลบข้อความให้ว่าง = กลับไปใช้ค่าเริ่มต้น',
    fields: [
      {
        key: 'startPromptTh',
        group: 'ข้อความสถานะถึงผู้ใช้',
        label: 'ข้อความทักทาย / ชวนเริ่มวางแผนภาษี (START_CONFIRM)',
        kind: 'textarea',
        hint:
          'ใช้ตอนผู้ใช้ยังไม่มีฟอร์มภาษี · token {ปี} = ปีภาษีที่จะวางแผน (ไม่รู้ปี ⇒ ตัดทิ้ง) · ' +
          '⚠️ ห้ามใส่ marker |quickReply| ในข้อความ — V2 ส่งปุ่มตอบเป็นข้อมูลแยก (ต่างจาก V1)',
      },
      {
        key: 'versionChoicePromptTh',
        group: 'ข้อความสถานะถึงผู้ใช้',
        label: 'ข้อความยืนยันเมื่อแผนเดิมอยู่บนเวอร์ชันเก่า',
        kind: 'textarea',
        hint:
          'ใช้ตอนลูกค้ากลับเข้าแผนภาษีที่ทำเสร็จแล้ว แต่แผนนั้นสร้างไว้กับเวอร์ชัน config/ชุดคำถามที่ไม่ได้ active แล้ว · ' +
          'ปุ่ม "สร้างแผนภาษีใหม่ / นำข้อมูลเดิมมาสร้างแผนภาษีใหม่" ระบบแนบให้เองเป็นข้อมูลแยก ไม่ต้องเขียนปุ่มในข้อความนี้ · ' +
          'token {ปี} = ปีภาษีของแผน (เช่น "ปี 2569") · ปล่อยเป็นค่าเริ่มต้น ⇒ ใช้ข้อความในระบบ · ' +
          '⚠️ ห้ามใส่ marker |quickReply| ในข้อความ',
      },
      {
        key: 'oldYearResumePromptTh',
        group: 'ข้อความสถานะถึงผู้ใช้',
        label: 'ข้อความเมื่อแผนที่ทำค้างอยู่ปีเก่า และเปิดปีใหม่แล้ว',
        kind: 'textarea',
        hint:
          'ใช้ตอนลูกค้ามีแผนที่ทำค้างอยู่ปีเก่า (เช่น 2569) ขณะที่เปิดวางแผนปีใหม่ (เช่น 2570) แล้ว · ' +
          'ปุ่ม "ทำต่อปี 2569 / นำข้อมูลเดิมไปทำปี 2570 / เริ่มใหม่ปี 2570" ระบบแนบให้เอง ไม่ต้องเขียนปุ่มในข้อความนี้ · ' +
          'token {ปี} = ปีของแผนเดิม · {ปีใหม่} = ปีใหม่ (เช่น "ปี 2570") · เว้นว่าง ⇒ ใช้ข้อความชวนทำต่อ (RESUME) ปกติ · ' +
          '⚠️ ห้ามใส่ marker |quickReply| ในข้อความ',
      },
      {
        key: 'closedYearStartPromptTh',
        group: 'ข้อความสถานะถึงผู้ใช้',
        label: 'ข้อความเมื่อปีภาษีของแผนเดิมถูกปิดแล้ว',
        kind: 'textarea',
        hint:
          'ใช้ตอนแผนของลูกค้า (ทำค้าง หรือทำเสร็จแล้วแล้วเปิดจากหน้าประวัติ) อยู่ในปีที่ปิด Active ไปแล้ว · ' +
          'ปุ่ม "นำข้อมูลเดิมไปทำปี 2570 / เริ่มใหม่ปี 2570" ระบบแนบให้เอง ไม่ต้องเขียนปุ่มในข้อความนี้ · ' +
          'token {ปี} = ปีของแผนเดิม · {ปีใหม่} = ปีใหม่ · เว้นว่าง ⇒ ใช้ข้อความทักทาย (START_CONFIRM) ปกติ · ' +
          '⚠️ ห้ามใส่ marker |quickReply| ในข้อความ',
      },
      {
        key: 'completedOldYearPromptTh',
        group: 'ข้อความสถานะถึงผู้ใช้',
        label: 'ข้อความเมื่อแผนที่ทำเสร็จแล้วอยู่ปีเก่า และเปิดปีใหม่แล้ว',
        kind: 'textarea',
        hint:
          'ใช้ตอนลูกค้ามีแผนที่ทำเสร็จแล้วในปีเก่า (เช่น 2569) ขณะที่เปิดวางแผนปีใหม่ (เช่น 2570) แล้ว · ' +
          'ปุ่ม "ดูแผนปี 2569 / นำข้อมูลเดิมไปวางแผนปี 2570 / วางแผนใหม่ปี 2570" ระบบแนบให้เอง ไม่ต้องเขียนปุ่มในข้อความนี้ · ' +
          'token {ปี} = ปีของแผนเดิม · {ปีใหม่} = ปีใหม่ · เว้นว่าง ⇒ ใช้ข้อความของฉากสรุป / จอทำเสร็จแล้วปกติ · ' +
          '⚠️ ห้ามใส่ marker |quickReply| ในข้อความ',
      },
      {
        key: 'unmatchedYearNoticeTh',
        group: 'ข้อความสถานะถึงผู้ใช้',
        label: 'ข้อความเมื่อลูกค้าพิมพ์ปีภาษีที่ไม่ได้เปิด',
        kind: 'textarea',
        hint:
          'ใช้ตอนเปิดหลายปี แล้วลูกค้าพิมพ์ปีที่ไม่ได้เปิดมาตอนเริ่มแผนใหม่ (เช่น «วางแผนภาษีปี 2568», «ปี 25700») · ' +
          'ระบบพาไปปีล่าสุดเสมอ (แม้เปิดโหมดถามปี) แล้วแสดงข้อความนี้ก่อนคำถามข้อแรก · ' +
          'token {ปีใหม่} = ปีที่พาไป · เว้นว่าง ⇒ ไม่แจ้ง (แต่ยังพาไปปีล่าสุด) · ⚠️ ห้ามใส่ marker |quickReply| ในข้อความ',
      },
      {
        key: 'yearChangeNoticeTh',
        group: 'ข้อความสถานะถึงผู้ใช้',
        label: 'ข้อความเตือนเมื่อแผนใหม่เปลี่ยนปีภาษี',
        kind: 'textarea',
        hint:
          'ใช้ตอนลูกค้าขอวางแผนใหม่ (เช่น พิมพ์ «วางแผนภาษีใหม่» ระหว่างตอบแผนปี 2569) แล้วแผนใหม่จะอยู่คนละปีกับแผนเดิม ' +
          '(หรือยังต้องเลือกปี) · แสดงบนจอเลือกปี (หรือก่อนคำถามข้อแรก ถ้าปิดโหมดถามปี) · ' +
          'token {ปี} = ปีของแผนเดิม · {ปีใหม่} = ปีของแผนใหม่ · เว้นว่าง ⇒ ไม่แจ้ง · ⚠️ ห้ามใส่ marker |quickReply| ในข้อความ',
      },
      {
        key: 'yearSwitchPromptTh',
        group: 'ข้อความสถานะถึงผู้ใช้',
        label: 'ข้อความเมื่อลูกค้าพิมพ์ขอเปลี่ยนไปปีภาษีใหม่กลางฟอร์ม',
        kind: 'textarea',
        hint:
          'ใช้ตอนเปิดหลายปี ลูกค้ากำลังกรอกแผนปีเก่า (เช่น 2569) แล้วพิมพ์ขอเปลี่ยนไปปีใหม่ (เช่น «ขอเปลี่ยนเป็นปี 2570») · ' +
          'ปุ่ม "นำข้อมูลเดิมไปทำปี 2570 / เริ่มใหม่ปี 2570" ระบบแนบให้เอง ไม่ต้องเขียนปุ่มในข้อความนี้ · ' +
          'ขอ "วางแผนภาษีใหม่" ไม่ใช้ข้อความนี้ (ไปทางข้อความเตือนเมื่อแผนใหม่เปลี่ยนปีภาษีด้านบน) · ' +
          'token {ปี} = ปีของแผนเดิม · {ปีใหม่} = ปีใหม่ · เว้นว่าง ⇒ ใช้ข้อความตั้งต้น · ⚠️ ห้ามใส่ marker |quickReply| ในข้อความ',
      },
      {
        key: 'resumePromptTh',
        group: 'ข้อความสถานะถึงผู้ใช้',
        label: 'ข้อความชวนทำต่อ (RESUME)',
        kind: 'textarea',
        hint:
          'ใช้ตอนมีฟอร์มค้างอยู่ · ⚠️ ห้ามใส่ marker |quickReply| — ปุ่ม "ทำต่อ / เริ่มใหม่" ระบบแนบให้เองเป็นข้อมูลแยก',
      },
      {
        key: 'historyResumePromptTh',
        group: 'ข้อความสถานะถึงผู้ใช้',
        label: 'ข้อความต้อนรับกลับจากหน้าประวัติ (HISTORY_RESUME)',
        kind: 'textarea',
        hint:
          'ใช้เฉพาะตอนแอปส่ง isResume=true (ลูกค้ากด Resume บนการ์ดใน Tax History) · ระบบส่งข้อความนี้ + ' +
          'คำถามถัดไปของฟอร์มให้ LLM เรียบเรียงเป็นข้อความเดียว (โมเดลจากแท็บ LLM) · LLM ล้มเหลว/ไม่มีคำถามค้าง ⇒ ' +
          'ส่งข้อความนี้ตรง ๆ พร้อมปุ่ม ทำต่อ · ⚠️ ห้ามใส่ marker |quickReply| — ปุ่มของคำถาม (answer options) ' +
          'ระบบแนบให้เองเป็นข้อมูลแยก',
      },
      {
        key: 'historyResumeComposePromptTh',
        group: 'ข้อความสถานะถึงผู้ใช้',
        label: 'คำสั่งเชื่อมข้อความต้อนรับกลับ + คำถามถัดไป (คำสั่งให้ AI ไม่ใช่ข้อความถึงลูกค้า)',
        kind: 'textarea',
        hint:
          'คู่กับช่องด้านบน (HISTORY_RESUME) — อันนั้นคือข้อความที่ลูกค้าเห็น อันนี้คือคำสั่งที่เอาข้อความนั้น ' +
          'มาเชื่อมกับคำถามถัดไปของฟอร์มให้เป็นข้อความเดียว · ' +
          'ถ้าแก้ข้อความต้อนรับหรือทำเวอร์ชันภาษาอื่น ต้องดูช่องนี้ด้วย ไม่งั้นคำสั่งยังพูดถึงข้อความเวอร์ชันเก่า · ' +
          '🔴 fail-soft: ไม่มีโมเดล / ไม่มีคำถามค้าง / LLM ล้มเหลว ⇒ ส่งข้อความต้อนรับตรง ๆ พร้อมปุ่ม ทำต่อ ' +
          'คำสั่งที่เขียนพลาดจึงเสียแค่ "การเชื่อม" ไม่เคยทำให้ข้อความหาย · ' +
          'ตอบภาษาอื่น: ระบบต่อท้ายบรรทัดระบุภาษาให้เองใต้ข้อความนี้ ไม่ต้องเขียนเอง',
      },
      {
        key: 'yearQuestionPromptTh',
        group: 'ข้อความสถานะถึงผู้ใช้',
        label: 'ข้อความถามปีภาษี (SELECT_YEAR)',
        kind: 'textarea',
        hint:
          'ใช้ตอนต้องให้ผู้ใช้เลือกปีภาษี (ใช้ทั้งใน Get Tax State และในโหนด Tax Planning V2 ให้ข้อความตรงกัน) · ' +
          '⚠️ ห้ามใส่ marker |quickReply| — รายการปีระบบแนบให้เองเป็นข้อมูลแยก',
      },
      {
        key: 'resumeGatePromptTh',
        group: 'ข้อความสถานะถึงผู้ใช้',
        label: 'prompt ตัดสิน "ทำต่อ / เริ่มใหม่" (คำสั่งให้ AI ไม่ใช่ข้อความถึงลูกค้า)',
        kind: 'textarea',
        hint:
          'คู่กับ "ข้อความชวนทำต่อ (RESUME)" ด้านบน — อันนั้นคือสิ่งที่ลูกค้าเห็น อันนี้คือคำสั่งที่ใช้ตีความคำตอบ · ' +
          'ถ้าแก้ข้อความ RESUME หรือทำเวอร์ชันภาษาอื่น ต้องแก้ช่องนี้ตามด้วย ไม่งั้นตัวตัดสินยังอ่านคำถามเวอร์ชันเก่า · ' +
          '🔴 ต้องมีคำว่า NEW และ CONTINUE อยู่ในข้อความ — ระบบจับคำตอบจาก 2 คำนี้ตรง ๆ ถ้าขาดข้อใดข้อหนึ่ง ' +
          'ระบบจะไม่ใช้ข้อความนี้และกลับไปใช้ prompt ตั้งต้นแทน (พร้อมโน้ตใน trace) · ' +
          '🔴 ตอบ NEW ผิด = ทิ้งคำตอบที่ลูกค้ากรอกมาแล้วทั้งฟอร์ม กติกา "ไม่แน่ใจให้ตอบ CONTINUE" ห้ามหาย',
      },
      {
        key: 'versionChoiceGatePromptTh',
        group: 'ข้อความสถานะถึงผู้ใช้',
        label: 'prompใt ตัดสิน "สร้างใหม่ / ช้ข้อมูลเดิม"',
        kind: 'textarea',
        hint:
          'คู่กับ "ข้อความยืนยันเมื่อแผนเดิมอยู่บนเวอร์ชันเก่า" ด้านบน — อันนั้นคือสิ่งที่ลูกค้าเห็น อันนี้คือคำสั่งให้ LLM ' +
          'อ่านคำตอบที่ลูกค้า "พิมพ์" ตอบจอนั้น (กดปุ่มระบบจับตรง ๆ ไม่ผ่านช่องนี้) · เขียนเป็นภาษาอังกฤษ · ' +
          '🔴 ต้องมีคำว่า NEW และ REUSE อยู่ในข้อความ — ระบบจับคำตอบจาก 2 คำนี้ตรง ๆ ถ้าขาดข้อใดข้อหนึ่ง ' +
          'ระบบจะไม่ใช้ข้อความนี้และกลับไปใช้ prompt ตั้งต้นแทน · ไม่แน่ใจ ⇒ ให้ตอบ UNCLEAR (ระบบถามจอยืนยันซ้ำ ไม่เดา)',
      },
      {
        key: 'yearSwitchGatePromptTh',
        group: 'ข้อความสถานะถึงผู้ใช้',
        label: 'prompt ตัดสิน "ขอเปลี่ยนปีภาษี" (คำสั่งให้ AI ไม่ใช่ข้อความถึงลูกค้า)',
        kind: 'textarea',
        hint:
          'คู่กับ "ข้อความเมื่อลูกค้าพิมพ์ขอเปลี่ยนไปปีภาษีใหม่กลางฟอร์ม" ด้านบน — ใช้อ่านประโยคที่ลูกค้าพิมพ์ระหว่างกรอกแผนปีเก่า ' +
          'แล้วมีเลขปีใหม่ที่เปิดอยู่ · SWITCH = ขอเปลี่ยนปี ⇒ ขึ้นจอ 2 ปุ่ม · NEW = ขอวางแผนใหม่ ⇒ ไปทางเดิม (B6) · ' +
          'STAY = อย่างอื่น ⇒ เดินฟอร์มตามปกติ · เขียนเป็นภาษาอังกฤษ · token {ปี} / {ปีใหม่} ระบบแทนให้ · ' +
          'ให้ตอบเป็น JSON {"intent":"SWITCH|NEW|STAY","targetYear":"2568","reason":"..."} — reason ขึ้นใน Tax Log ระบบตัดสินจาก intent ' +
          '· targetYear = ปีที่ลูกค้าขอ ระบบใช้เฉพาะเมื่อเลขนั้นอยู่ในข้อความลูกค้า และเช็คเองว่าปีเปิดอยู่ไหม (ไม่ใส่ก็ได้ ระบบใช้ปีที่อ่านจากข้อความแทน) ' +
          '(ตอบเป็นคำเดียวก็ยังอ่านได้) · ' +
          '🔴 ต้องมีคำว่า SWITCH, NEW และ STAY ครบ ถ้าขาดคำใด ระบบจะไม่ใช้ข้อความนี้และกลับไปใช้ prompt ตั้งต้นแทน · ' +
          'ไม่แน่ใจ ⇒ ให้ตอบ STAY (ไม่เดา SWITCH)',
      },
      {
        key: 'completionRestartAskTh',
        group: 'การบันทึกและข้อความปิดท้าย',
        label: 'ประโยคที่ทำให้ข้อความปิดท้ายกลายเป็นคำถาม (prompt เท่านั้น ลูกค้าไม่เห็น)',
        kind: 'textarea',
        hint:
          '🔴 ลูกค้าไม่เห็นข้อความนี้ — สิ่งที่ส่งถึงลูกค้าคือ "ข้อความตอนกรอกครบทุกข้อ" ด้านบน ' +
          'ประโยคนี้ถูกต่อท้ายเฉพาะตอนส่งให้ AI อ่าน · ' +
          'จำเป็นเพราะข้อความปิดท้ายเป็น "ประโยคบอกเล่า" (บันทึกครบถ้วนแล้ว) แต่ทั้งตัวสกัดคำตอบและเส้นทาง ' +
          '"ลูกค้าถามกลับ" ต่างคาดว่ามีคำถามค้างอยู่บนจอ · ไม่มีประโยคนี้ โมเดลไม่มีคำถามให้ย้อนกลับไป ' +
          'แล้วเอาคำถามของลูกค้าเองมาถามซ้ำ (ของจริง: “ขอกลับไปถามคำถามเดิมอีกครั้งนะคะ ทำไมค่าใช้จ่ายหักได้แค่ 100,000…”) · ' +
          'คู่กับ prompt ตัดสิน "เริ่มใหม่ไหม" ในแท็บ สกัดคำตอบ (Extract)',
      },
      {
        key: 'noConfigMessageTh',
        group: 'ข้อความตอนระบบไม่พร้อม',
        label: 'ข้อความตอนยังไม่มีชุดคำถามที่เปิดใช้งาน',
        kind: 'textarea',
        hint:
          'ขึ้นตอนที่ยังไม่มี question version ไหน active — ซึ่งคือช่วงที่กำลัง publish config พอดี · ' +
          'ใส่ข้อความของตัวเองได้ เช่น "กำลังปรับปรุงระบบ รบกวนลองใหม่ใน 10 นาทีค่ะ" · ' +
          '🔴 ส่งถึงลูกค้าตรง ๆ ไม่ผ่าน AI ⇒ "แทนที่ภาษาในการตอบ" ไม่มีผลกับช่องนี้ พิมพ์ภาษาไหนได้ภาษานั้น',
      },
      {
        key: 'unavailableMessageTh',
        group: 'ข้อความตอนระบบไม่พร้อม',
        label: 'ข้อความตอนระบบวางแผนภาษีขัดข้อง',
        kind: 'textarea',
        hint:
          'ขึ้นเมื่อโหนดเกิด error (รวมถึงสูตรคำนวณพัง) — เป็นสิ่งเดียวที่ลูกค้าเห็นตอนนั้น · ' +
          '🔴 ถ้าเปิด bypassOnError ที่โหนดใน Flow ระบบจะสะท้อนข้อความของลูกค้ากลับแทน ช่องนี้จะไม่ถูกใช้เลย · ' +
          'ส่งตรงถึงลูกค้าเหมือนช่องบน',
      },
      {
        key: 'enableLanguageReplace',
        group: 'พฤติกรรมการตอบกลับ',
        label: 'รองรับหลายภาษา (Language replacing)',
        kind: 'bool',
        cols: 6,
        hint:
          'เปิดแล้วบอทจะตอบเป็นภาษาเดียวกับที่ลูกค้าพิมพ์ (ไทย/อังกฤษ/จีน) โดยตรวจจากข้อความจริง · ' +
          'ปิด (ค่าเริ่มต้น) = ตอบไทยเสมอเหมือนเดิมทุกไบต์ · มีผลเฉพาะข้อความที่ LLM เรียบเรียง (คำถาม/สรุปยืนยัน) ' +
          'ข้อความระบบบางส่วน (เช่น ทักทาย, ข้อความปิดท้าย) ยังเป็นไทยเสมอ',
      },
    ],
  },
  {
    value: 'llm',
    label: 'LLM',
    testid: 'tpsv2-tab-llm',
    icon: 'mdi-robot-outline',
    noteType: 'warning',
    note:
      'ค่าที่เดิมอยู่บน node.Data ของโหนด Tax Planning V2 — ตอนนี้ย้ายมาที่นี่ทั้งหมด ' +
      'ช่องที่ไม่แก้จะใช้ค่าเดิมในโค้ดเป๊ะ ๆ (พฤติกรรมเท่าก่อนย้าย)',
    fields: [
      // ── โมเดลหลัก — ตัวที่สกัดคำตอบ (และปรุงคำถามด้วย ถ้าไม่ได้เปิดโมเดลตัวที่ 2) ──
      {
        key: 'provider',
        group: 'โมเดลหลัก (ใช้สกัดคำตอบ)',
        label: 'Provider',
        kind: 'provider',
        cols: 6,
        maxlength: 50,
        hint: 'เดิม node.Data["provider"] · เลือกจากรายการผู้ให้บริการจริง (แหล่งเดียวกับช่อง Provider ในโหนด)',
      },
      {
        key: 'modelName',
        group: 'โมเดลหลัก (ใช้สกัดคำตอบ)',
        label: 'Model name',
        kind: 'model',
        cols: 6,
        maxlength: 200,
        hint:
          'เดิม node.Data["model"] · เลือกจากรายการโมเดลจริง — ค่าที่บันทึกคือคีย์เต็ม "ชื่อ|id" ' +
          'เหมือนที่โหนดเก็บ (ชื่อล้วนระบบเทียบให้ด้วยชื่อหน้า "|" แต่คีย์เต็มชัดเจนกว่า)',
      },
      // 🔴 VIRTUAL field — NOT a column and deliberately absent from TAX_PLANNING_SETTINGS_V2_ALL_KEYS.
      //    It writes the two hidden `int` keys below (`idKey` / `displayKey`) in one gesture, so the
      //    pair can never be filled from two different bots.
      {
        key: 'gptModelRef',
        group: 'โมเดลหลัก (ใช้สกัดคำตอบ)',
        label: 'บอทจาก Model Creator',
        kind: 'gptModelRef',
        cols: 12,
        idKey: 'modelID',
        displayKey: 'modelDisplayID',
        hint:
          'เลือกบอทที่สร้างไว้ในหน้า Model Creator (เฉพาะที่ publish แล้ว) — ระบบจะเติม Model ID และ ' +
          'Model display ID ให้พร้อมกันจากบอทตัวเดียวกัน · กดกากบาทเพื่อล้างทั้งคู่ (กลับไปใช้ค่าเริ่มต้น)',
      },
      // 🔴 คงไว้ครบทั้ง 4 descriptor (kind 'int', label เดิม) เพราะ `payloadValue` coerce เป็นตัวเลข
      //    จาก `FIELD_BY_KEY[key].kind` — ลบทิ้งเมื่อไหร่ค่าจะถูกส่งเป็น string ไปยัง `int?` แล้วได้ 400
      //    และ label ยัง mirror อยู่ที่ TaxPlaygroundV2BAL.SettingsColumnLabelThMap
      //    `hidden` = ไม่ render (แต่ยังโผล่มาให้แก้ได้เสมอถ้ามี validation error — ดู `fieldVisible`)
      { key: 'modelID', group: 'โมเดลหลัก (ใช้สกัดคำตอบ)', label: 'Model ID', kind: 'int', cols: 6, hidden: true, hint: 'เดิม node.Data["modelId"] · คีย์ JSON คือ modelID (ตัว D ใหญ่) · ตั้งค่าผ่านช่อง "บอทจาก Model Creator"' },
      { key: 'modelDisplayID', group: 'โมเดลหลัก (ใช้สกัดคำตอบ)', label: 'Model display ID', kind: 'int', cols: 6, hidden: true, hint: 'เดิม node.Data["modelDisplayId"] · คีย์ JSON คือ modelDisplayID · ตั้งค่าผ่านช่อง "บอทจาก Model Creator"' },
      { key: 'apiVersion', group: 'โมเดลหลัก (ใช้สกัดคำตอบ)', label: 'API version', kind: 'text', cols: 6, maxlength: 50, hint: 'เดิม node.Data["apiVersion"] · ไม่กำหนด = ใช้ค่าจากรายการโมเดล' },
      { key: 'jsonRequest', group: 'โมเดลหลัก (ใช้สกัดคำตอบ)', label: 'JSON request (template)', kind: 'textarea', hint: 'เดิม node.Data["jsonRequest"] · ไม่กำหนด = ใช้ template จากรายการโมเดล' },

      // ── พารามิเตอร์การสร้างข้อความ ──
      { key: 'temperature', group: 'พารามิเตอร์การสร้างข้อความ', label: 'Temperature', kind: 'float', cols: 6, hint: 'เดิม node.Data["temperature"]' },
      { key: 'maxTokens', group: 'พารามิเตอร์การสร้างข้อความ', label: 'Max tokens', kind: 'int', cols: 6, hint: 'เดิม node.Data["maxTokens"] · ไม่กำหนด = ใช้ค่าจากรายการโมเดล' },
      { key: 'topP', group: 'พารามิเตอร์การสร้างข้อความ', label: 'Top P', kind: 'float', cols: 6, hint: 'เดิม node.Data["topP"]' },
      { key: 'frequencyPenalty', group: 'พารามิเตอร์การสร้างข้อความ', label: 'Frequency penalty', kind: 'float', cols: 6, hint: 'เดิม node.Data["frequencyPenalty"]' },
      { key: 'presencePenalty', group: 'พารามิเตอร์การสร้างข้อความ', label: 'Presence penalty', kind: 'float', cols: 6, hint: 'เดิม node.Data["presencePenalty"]' },
      { key: 'stopSequence', group: 'พารามิเตอร์การสร้างข้อความ', label: 'Stop sequence', kind: 'text', cols: 6, maxlength: 200, hint: 'เดิม node.Data["stopSequence"]' },
      { key: 'jsonResponse', group: 'พารามิเตอร์การสร้างข้อความ', label: 'บังคับตอบเป็น JSON', kind: 'bool', cols: 6, hint: 'เดิม node.Data["jsonResponse"]' },

      // ── บุคลิกและความจำ ──
      {
        key: 'gender',
        group: 'บุคลิกและความจำการสนทนา',
        label: 'เพศของผู้ช่วย (Gender)',
        kind: 'select',
        cols: 6,
        options: [
          { title: 'ชาย (m)', value: 'm' },
          { title: 'หญิง (f)', value: 'f' },
        ],
        hint: 'เดิม node.Data["gender"] · ระบบ normalize m/male → m ที่เหลือ → f',
      },
      // 🔴 โหนดภาษี V2 ไม่ใช้ "ความจำแชท" (replay ข้อความเก่าเป็น message list) อีกแล้ว — บทสนทนาย้อนหลังถูกเรนเดอร์
      //    เป็น section `### History ###` ใน system prompt ทั้งฝั่งสกัดคำตอบและฝั่งปรุงคำถาม (เห็นได้ใน Playground →
      //    แท็บ Prompt) และ 2 ค่าด้านล่างคือค่าที่คุมมัน — ไม่ใช่ค่าที่คุม memory ของ LLM แล้ว
      { key: 'memoryTurn', group: 'บุคลิกและความจำการสนทนา', label: 'Memory turn', kind: 'int', cols: 6, hint: 'จำนวนเทิร์นย้อนหลังที่ใส่ใน `### History ###` (สูงสุด 12) · เดิม node.Data["memoryTurn"]' },
      { key: 'memoryDuration', group: 'บุคลิกและความจำการสนทนา', label: 'Memory duration', kind: 'int', cols: 6, hint: '⚠ โหนดภาษี V2 ไม่ได้ใช้แล้ว (History ตัดตามจำนวนเทิร์น ไม่ใช่ตามเวลา) · เดิม node.Data["memoryDuration"]' },
      { key: 'shortTermMemory', group: 'บุคลิกและความจำการสนทนา', label: 'Short-term memory', kind: 'int', cols: 6, hint: 'ความยาวสูงสุดต่อ 1 ข้อความใน `### History ###` (ตัวอักษร, สูงสุด 1000) · เดิม node.Data["shortTermMemory"]' },
      { key: 'longTermMemory', group: 'บุคลิกและความจำการสนทนา', label: 'Long-term memory', kind: 'bool', cols: 6, hint: '⚠ โหนดภาษี V2 ไม่ได้ใช้แล้ว — ปิดตายทุกครั้งที่เรียก LLM · เดิม node.Data["longTermMemory"]' },
      // ⚰️ `timeZone` เคยเป็นช่องพิมพ์มือ (kind: 'text') ตรงนี้ — ย้ายไปเป็นการ์ด "TIME CONTEXT"
      //    (สวิตช์ + dropdown) ที่ท้ายแท็บนี้ ให้หน้าตาตรงกับ node AI Model
      //    🔴 คีย์ยังอยู่ครบใน `TAX_PLANNING_SETTINGS_V2_ALL_KEYS` เหมือนเดิม — ห้ามถอด: คีย์ที่หายไป
      //       จาก payload จะถูก backend อ่านเป็น `null` = ล้างค่าที่ admin ตั้งไว้
      //    ผลข้างเคียงที่ตั้งใจ: `FIELD_BY_KEY.timeZone === undefined` ⇒ `payloadValue` ตกลง default
      //       branch (coerce เป็น string, ถูกต้อง) และ `isAtDefault('timeZone')` คืน true เมื่อค่าว่าง
      //       (default ของคอลัมน์นี้เป็น null) ⇒ ปิดสวิตช์แล้วบันทึกจะส่ง `null` จริง
      //       ส่วน validation ที่หายไปพร้อม descriptor ถูกแทนด้วย `timeContextErrors` ใน `errorsByKey`

      // ── การเรียกใช้งานโหนด ──
      { key: 'nodeTimeout', group: 'การเรียกใช้งานโหนด', label: 'Node timeout (วินาที)', kind: 'int', cols: 6, hint: 'เดิม node.Data["nodeTimeout"] · 0 = ไม่กำหนด' },
      { key: 'nodeMaxRetry', group: 'การเรียกใช้งานโหนด', label: 'Node max retry', kind: 'int', cols: 6, hint: 'เดิม node.Data["nodeMaxRetry"]' },
      { key: 'enableTransactionId', group: 'การเรียกใช้งานโหนด', label: 'Enable transaction id', kind: 'bool', cols: 6, hint: 'เดิม node.Data["enableTransactionId"]' },

      // ── โมเดลตัวที่ 2 (ฝั่ง ASK) ────────────────────────────────────────────────
      // ช่องทั้งหมดข้างบนคือโมเดล "หลัก" ซึ่งเป็นตัวสกัดคำตอบ · 5 ช่องนี้ให้เลือกอีกตัวมาปรุงคำถามแทน
      {
        key: 'useQuestionModel',
        group: 'โมเดลแยกสำหรับปรุงคำถาม (ASK)',
        label: 'ใช้โมเดลแยกตอนปรุงคำถาม',
        kind: 'bool',
        cols: 6,
        hint:
          'เดิม node.Data["taxPlanningUseQuestionModel"] (V1) · ปิด = โมเดลตัวเดียวทำทั้งปรุงคำถามและสกัดคำตอบ · ' +
          'เลือก "เปิด" อย่างเดียวไม่พอ ต้องเลือก provider + model ด้านล่างด้วย ไม่งั้นระบบถอยไปใช้โมเดลหลัก',
      },
      {
        key: 'questionProvider',
        group: 'โมเดลแยกสำหรับปรุงคำถาม (ASK)',
        label: 'Provider (ตอนปรุงคำถาม)',
        kind: 'provider',
        cols: 6,
        maxlength: 50,
        hint: 'เดิม node.Data["taxPlanningQuestionProvider"] · ใช้กับขั้นปรุงคำถาม (phraseQuestion) และเลือกปุ่มตอบด่วนเท่านั้น',
      },
      {
        key: 'questionModelName',
        group: 'โมเดลแยกสำหรับปรุงคำถาม (ASK)',
        label: 'Model name (ตอนปรุงคำถาม)',
        kind: 'model',
        // 🔴 กรองรายการด้วย provider ของตัวเอง ไม่ใช่ provider ของโมเดลหลัก
        providerKey: 'questionProvider',
        cols: 6,
        maxlength: 200,
        hint:
          'เดิม node.Data["taxPlanningQuestionModel"] · ค่าที่บันทึกคือคีย์เต็ม "ชื่อ|id" เหมือนช่องโมเดลหลัก · ' +
          'ระบบ override เฉพาะตัวตนของโมเดล (+ APIVersion/JsonRequest ที่ดึงจาก catalog ให้) ส่วน temperature / tokens / memory ใช้ร่วมกับโมเดลหลัก',
      },
      // VIRTUAL — same contract as `gptModelRef` above, but for the ASK-side pair.
      {
        key: 'questionGptModelRef',
        group: 'โมเดลแยกสำหรับปรุงคำถาม (ASK)',
        label: 'บอทจาก Model Creator (ตอนปรุงคำถาม)',
        kind: 'gptModelRef',
        cols: 12,
        idKey: 'questionModelID',
        displayKey: 'questionModelDisplayID',
        hint:
          'เลือกบอทที่สร้างไว้ในหน้า Model Creator (เฉพาะที่ publish แล้ว) สำหรับขั้นปรุงคำถาม — ' +
          'ระบบจะเติม Model ID (ตอนปรุงคำถาม) และ Model display ID (ตอนปรุงคำถาม) ให้พร้อมกัน · กดกากบาทเพื่อล้างทั้งคู่',
      },
      { key: 'questionModelID', group: 'โมเดลแยกสำหรับปรุงคำถาม (ASK)', label: 'Model ID (ตอนปรุงคำถาม)', kind: 'int', cols: 6, hidden: true, hint: 'คีย์ JSON คือ questionModelID (ตัว D ใหญ่) · ตั้งค่าผ่านช่อง "บอทจาก Model Creator (ตอนปรุงคำถาม)"' },
      { key: 'questionModelDisplayID', group: 'โมเดลแยกสำหรับปรุงคำถาม (ASK)', label: 'Model display ID (ตอนปรุงคำถาม)', kind: 'int', cols: 6, hidden: true, hint: 'คีย์ JSON คือ questionModelDisplayID · ตั้งค่าผ่านช่อง "บอทจาก Model Creator (ตอนปรุงคำถาม)"' },
      // ── การค้นเอกสารความรู้ (### Knowledge ###) ──
      // เอกสารที่จะค้น "เลือกรายข้อ" อยู่ที่ Tax Config V2 → คำถาม (หมวดย่อยเอกสาร / รายเอกสาร ของแต่ละข้อ)
      // สองช่องนี้คือพารามิเตอร์ของการค้นเอง ซึ่งใช้ร่วมกันทุกข้อและทั้ง 2 จังหวะ (ตอนถาม + ตอนสกัดคำตอบ)
      {
        key: 'knowledgeTopN',
        group: 'การค้นเอกสารความรู้ (Knowledge)',
        label: 'จำนวนชิ้นเอกสารต่อการค้น 1 ครั้ง (Top-N)',
        kind: 'int',
        cols: 6,
        min: 1,
        max: 20,
        hint:
          'เดิม node.Data["taxPlanningKnowledgeTopN"] ซึ่งไม่มีหน้าจอไหนตั้งได้เลย · ค่าเริ่มต้น 3 · ' +
          'ทุกชิ้นถูกแนบเข้า prompt ทั้งตอนถามและตอนสกัดคำตอบ — เพิ่มเลขนี้ = จ่าย token เพิ่มทุกเทิร์น ไม่ใช่เฉพาะข้อที่ผูกเอกสารไว้ · ใส่ได้ 1–20',
      },
      {
        key: 'knowledgeCosineSimilarity',
        group: 'การค้นเอกสารความรู้ (Knowledge)',
        label: 'คะแนนความใกล้เคียงขั้นต่ำ (cosine similarity)',
        kind: 'float',
        cols: 6,
        min: 0.01,
        max: 1,
        hint:
          'ยิ่งมากยิ่งเข้ม (0–1) · เว้นว่าง = ไม่กรอง — เอาเท่าที่ Top-N ค้นได้เสมอ ซึ่งเป็นพฤติกรรมเดิมของ Tax V2 · ' +
          'ตั้งแล้วเอกสารที่คะแนนต่ำกว่าจะถูกตัดทิ้ง (ไม่มีเอกสารเข้าเกณฑ์ = ไม่ใส่หัวข้อ ### Knowledge ### เลย ไม่ใช่ error) · ' +
          'อ้างอิงจาก log จริง: คำถามที่ตรงเรื่องได้ราว 0.50–0.61 คำถามนอกเรื่องราว 0.11 — เริ่มที่ 0.4 ถ้าจะเปิดกรอง',
      },
    ],
  },
  {
    value: 'nodePrompt',
    label: 'Node Prompt',
    testid: 'tpsv2-tab-nodeprompt',
    icon: 'mdi-file-document-edit-outline',
    noteType: 'info',
    note:
      'prompt 3 ชั้นของโหนด (เดิมพิมพ์ไว้บน node inspector) — ชั้นฐานใช้ทุกจังหวะ ส่วนอีก 2 ชั้นถูกฉีดเป็น ' +
      '### Tax Node Prompt ### ตอนถามคำถาม / ตอนสกัดคำตอบ · ปล่อยว่าง = ไม่ใส่ชั้นนั้นเลย',
    fields: [
      {
        key: 'nodeSystemPromptTh',
        group: 'prompt 3 ชั้นของโหนด',
        label: 'System prompt ชั้นฐานของโหนด',
        kind: 'textarea',
        hint: 'เดิม node.Data["systemPrompt"] · ค่าเริ่มต้นคือว่าง (ไม่ใส่ชั้นนี้)',
      },
      {
        key: 'nodeQuestionSystemPromptTh',
        group: 'prompt 3 ชั้นของโหนด',
        label: 'Prompt ตอนเรียบเรียงคำถาม',
        kind: 'textarea',
        hint: 'เดิม node.Data["taxPlanningV2QuestionSystemPrompt"] · marker ### Tax Node Prompt ###',
      },
      {
        key: 'nodeAnswerSystemPromptTh',
        group: 'prompt 3 ชั้นของโหนด',
        label: 'Prompt ตอนสกัดคำตอบ',
        kind: 'textarea',
        hint: 'เดิม node.Data["taxPlanningV2AnswerSystemPrompt"] · marker ### Tax Node Prompt ###',
      },
      {
        key: 'agentFormRulesTh',
        group: 'บล็อกข้อมูลฟอร์มที่ส่งให้ Agent อื่นอ่าน (### Tax Planning Form (V2) ###)',
        label: 'กติกาปิดท้ายบล็อก (บรรทัดเดียวที่บล็อกนี้มีคำสั่งได้)',
        kind: 'textarea',
        hint:
          'โหนดภาษีมีบทบาทที่สองคือเป็น "ปลั๊กอินอ่านอย่างเดียว" ใต้ Agent ตัวอื่น — มันเรนเดอร์บล็อกสรุปฟอร์มของลูกค้า ' +
          '(ฟอร์ม/สถานะ/คำถามที่อยู่บนจอ/ปุ่ม/ข้อมูลที่กรอกแล้ว) ส่งให้ Agent นั้นอ่าน · ช่องนี้คือกติกาที่ต่อท้ายบล็อกนั้น ' +
          'และเป็น "คำสั่ง" อย่างเดียวที่บล็อกนี้มีได้ ที่เหลือเป็นข้อเท็จจริงล้วน · ' +
          '🔴 ไม่มีปุ่มปิด — เว้นว่าง = กลับไปใช้ข้อความ default ไม่ใช่เอากติกาออก เพราะ Agent ที่รับบล็อกนี้ไปมีหน้าที่ ' +
          '"ตอบคำถาม" อยู่แล้ว ถ้าไม่มีกติกา สิ่งที่มันจะทำโดยธรรมชาติคือตอบคำถามของแบบฟอร์มแทนลูกค้า หรืออาสาเก็บข้อมูลเอง — ' +
          'ซึ่งผลลัพธ์ไม่ผ่าน guardrail/walker/ตัวเขียนของโหนดภาษีเลย กรอกไปก็ไม่ถูกบันทึกที่ไหน แต่ลูกค้าจะเข้าใจว่าบันทึกแล้ว · ' +
          '🔴 แก้ถ้อยคำได้ แต่ 3 ข้อนี้ต้องรอด: (1) ห้ามตอบคำถามของแบบฟอร์มแทนลูกค้า (2) ห้ามเก็บข้อมูล/ชวนกรอกเอง ' +
          'ให้ส่งต่อไป Tax Planning Agent (3) ตัวเลขที่ยกมาพูดต้องตรงทุกหลัก · ' +
          'ค่า default เป็นภาษาอังกฤษเหมือน scaffolding อื่น ๆ ของ V2 (เดิมเป็นไทยและแก้ไม่ได้เลย)',
      },
    ],
  },
  {
    value: 'form',
    label: 'พฤติกรรมฟอร์ม',
    testid: 'tpsv2-tab-form',
    icon: 'mdi-form-select',
    noteType: 'info',
    note:
      'ตัวเลือกว่าโหนดจะเลือกปีภาษีอย่างไร บันทึกคำตอบหรือไม่ และจะพูดอะไรตอนกรอกครบ ' +
      '(เดิมอยู่บน panel Tax Settings ของ node inspector)',
    fields: [
      {
        key: 'yearMode',
        group: 'การเลือกปีภาษี',
        label: 'โหมดเลือกปีภาษี',
        kind: 'select',
        cols: 6,
        options: [
          { title: 'ใช้ปีที่เปิดใช้งานอยู่ (active)', value: 'active' },
          { title: 'ปักปีที่กำหนดเอง (pinned)', value: 'pinned' },
        ],
        hint: 'เดิม node.Data["taxPlanningV2YearMode"]',
      },
      {
        key: 'yearID',
        group: 'การเลือกปีภาษี',
        label: 'Year ID (ใช้เมื่อโหมด pinned)',
        kind: 'int',
        cols: 6,
        hint: 'เดิม node.Data["taxPlanningV2YearId"] · คีย์ JSON คือ yearID (ตัว D ใหญ่) · 0 = ไม่ปัก',
      },
      {
        key: 'askWhenMultipleActive',
        group: 'การเลือกปีภาษี',
        label: 'ถามผู้ใช้เมื่อมีปีภาษี active หลายปี',
        kind: 'bool',
        cols: 6,
        hint: 'เดิม node.Data["taxPlanningV2AskWhenMultipleActive"]',
      },
      {
        key: 'persist',
        group: 'การบันทึกและข้อความปิดท้าย',
        label: 'บันทึกคำตอบลงฐานข้อมูล',
        kind: 'bool',
        cols: 6,
        hint: 'เดิม node.Data["taxPlanningV2Persist"] · ปิด = ทดลองอย่างเดียว ไม่เขียนฟอร์มจริง',
      },
      {
        key: 'streamingEnabled',
        group: 'การบันทึกและข้อความปิดท้าย',
        label: 'ส่งคำตอบแบบ streaming',
        kind: 'bool',
        cols: 6,
        hint: 'เปิด (ค่าเริ่มต้น) = ให้ช่องทางตัดสินเอง · ปิด = ส่งคำตอบ tax ทั้งก้อนเป็น Streaming:false เสมอ แม้แอปจะขอ stream · เทิร์นที่คำตอบพกการ์ด (|taxcard|) เป็น Streaming:false เสมอไม่ว่าสวิตช์นี้จะเปิดหรือปิด',
      },
      {
        key: 'clarifyAttemptLimit',
        group: 'เมื่อคำตอบยังไม่ชัด (Clarify)',
        label: 'ถามย้ำได้กี่ครั้งต่อ 1 ประเด็น',
        kind: 'int',
        cols: 6,
        min: 0,
        hint:
          'ค่าเริ่มต้น 3 · นับเฉพาะครั้งที่ "ถามย้ำ" ประเด็นเดิมที่ตอบมาแล้วยังไม่ชัด ครบแล้วระบบปล่อยประเด็นนั้นไปโดยไม่วนถามอีก · ' +
          '⚠️ 0 = ไม่จำกัด (ถามย้ำได้เรื่อย ๆ) — บนคำถามจำแนกเงินได้ §40 ตัวนับนี้เป็นฟิวส์ตัวสุดท้าย ' +
          'ตั้ง 0 แล้วเจอคำตอบที่ลูกค้าให้ไม่ได้จริง ๆ บทสนทนาจะไม่มีทางออก',
      },
      {
        key: 'completionMessageTh',
        group: 'การบันทึกและข้อความปิดท้าย',
        label: 'ข้อความตอนกรอกครบทุกข้อ',
        kind: 'textarea',
        hint: 'เดิม node.Data["taxPlanningV2CompletionMessage"] · ค่าเริ่มต้นคือไม่กำหนด (ระบบใช้ข้อความของตัวเอง)',
      },
    ],
  },
  {
    value: 'ask',
    label: 'ใช้ถาม (Ask)',
    testid: 'tpsv2-tab-ask',
    icon: 'mdi-comment-question-outline',
    noteType: 'info',
    note:
      'prompt ที่ระบบใช้ตอนเรียบเรียงคำถาม (phraseQuestion) — ต่อท้ายชั้น node / section / question ของคำถามนั้น ๆ',
    fields: [
      {
        key: 'askGuardrailPromptTh',
        group: 'คำสั่งเรียบเรียงคำถาม',
        label: 'Guardrail ตอนถาม — "ถามเท่านั้น ห้ามตอบแทน"',
        kind: 'textarea',
        hint: 'marker ### Ask Guardrail ### · อยู่นอกทุกชั้น — ไม่มี prompt ระดับ node / section / question ใด override ได้',
      },
      {
        key: 'replyLanguagePromptTh',
        group: 'คำสั่งเรียบเรียงคำถาม',
        label: 'ภาษาที่ใช้ตอบลูกค้า',
        kind: 'textarea',
        hint:
          'marker ### Reply Language ### · เป็นชั้นสุดท้ายของ stack ตอนถาม (อ่านทีหลังสุด = มีน้ำหนักที่สุด) · ' +
          'ใช้ {{Language}} แทนชื่อภาษาที่ระบบตรวจได้ · ' +
          '🔴 ขึ้นเฉพาะตอนเปิด "แทนที่ภาษาในการตอบ" ในแท็บ LLM — เว้นว่างช่องนี้ = กลับไปใช้ค่า default ไม่ใช่ปิดหัวข้อ',
      },
      {
        key: 'askFormDataCaptionTh',
        group: 'ข้อมูลที่ลูกค้ากรอกไว้แล้ว (### Tax Planning Form Data ###)',
        label: 'คำอธิบายเหนือข้อมูลในฟอร์ม (ตอนถาม)',
        kind: 'textarea',
        hint:
          'marker ### Tax Planning Form Data ### · ข้อความนี้อยู่ "เหนือ" ข้อมูลที่ลูกค้ากรอกไว้แล้ว — เป็นกติกาว่าอ่านข้อมูลชุดนั้นไปทำอะไรได้บ้าง ' +
          '(ห้ามตอบแทน, ห้ามพูดตัวเลขที่ไม่มีที่มาในข้อมูล, ห้ามสรุปฟอร์มยกเว้นเทิร์น “ทำต่อ/เริ่มใหม่”) · ' +
          '🔴 ข้อ CONFIRM ใช้ข้อความนี้เหมือนกัน แล้วระบบต่อท้ายอีก 1 บรรทัดให้เอง — แก้บรรทัดนั้นได้ที่ช่องถัดไป ' +
          'และไม่ว่าจะแก้ช่องไหน บรรทัดนั้นก็ไม่หาย · เว้นว่าง = กลับไปใช้ค่า default ไม่ใช่ปิดหัวข้อ',
      },
      {
        key: 'confirmFormDataClauseTh',
        group: 'ข้อมูลที่ลูกค้ากรอกไว้แล้ว (### Tax Planning Form Data ###)',
        label: 'บรรทัดเพิ่มเฉพาะข้อ CONFIRM (ต่อท้ายคำอธิบายด้านบน)',
        kind: 'textarea',
        hint:
          'ใช้เฉพาะข้อที่เป็น CONFIRM (สรุปยืนยัน) · ระบบเอาข้อความในช่องด้านบน แล้วต่อท้ายด้วยข้อความนี้ 1 บรรทัด ' +
          'ข้ออื่นไม่เห็นข้อความนี้เลย · ' +
          '🔴 หน้าที่ของมันคือบอกว่า ### Confirm Context ### คือ “รายการเดียว” ที่อ่านกลับให้ลูกค้าฟัง ส่วนข้อมูลทั้งฟอร์มด้านล่างเป็นแค่พื้นหลัง ' +
          'ไม่มีบรรทัดนี้เมื่อไร ข้อ CONFIRM จะเห็น 2 รายการพร้อมกัน (รายการสรุปแคบ ๆ กับข้อมูลทั้งฟอร์มกว้าง ๆ) แล้วอ่านตัวเลขผิดข้าม ' +
          'รายการ — เคสจริงจาก POC คือ “35” ถูกอ่านเป็น “35,000” · ' +
          '🔴 ต่อท้ายเสมอ: เว้นว่าง = กลับไปใช้ข้อความ default ไม่ใช่เอาบรรทัดนี้ออก (ถ้าจะเปลี่ยนกติกา ต้องพิมพ์กติกาใหม่ลงไปเอง)',
      },
      {
        key: 'clarifyRewriteRulesTh',
        group: 'กติกาการถามย้ำ (Clarify)',
        label: 'กติกาการเรียบเรียงคำถามย้อนถาม (ทั้งก้อน)',
        kind: 'textarea',
        hint:
          'ใช้เฉพาะเทิร์นที่ระบบ "ถามย้ำ" — ลูกค้าตอบแล้วแต่ยังไม่พอที่จะไปข้อถัดไป ระบบจึงคาคำถามเดิมไว้แล้วถามเฉพาะส่วนที่ยังไม่ชัด · ' +
          'ช่องนี้คือ "ทั้งก้อน" ของ ### Question Context ### ในเทิร์นนั้น ไม่ใช่แค่ caption · ' +
          '🔴 ต้องมี {UNRESOLVED} เสมอ — ระบบเอาประโยคที่สรุปว่า "ยังไม่ชัดตรงไหน" (ชื่อรายการ + ตัวเลือกที่ค้างอยู่) ไปแทนที่ตรงนั้น ' +
          'ถ้าลบทิ้ง ระบบจะไม่ใช้ข้อความในช่องนี้เลย กลับไปใช้ค่า default แล้วเขียนเตือนไว้ใน turn log · ' +
          '{LANGUAGE} = ภาษาที่จะตอบ (ค่าเดียวกับหัวข้อ ### Reply Language ###) ใส่หรือไม่ใส่ก็ได้ · ' +
          '🔴 กติกาที่อยู่ในค่า default ทุกข้อมาจากปัญหาจริง: ห้ามอ่านตัวเลือกเชิงเทคนิค (ม.40(x)) ให้ลูกค้าฟัง — เคยได้คำถาม ' +
          '“เป็นเงินได้ ม.40(2) หรือ ม.40(3) หรือ ม.40(8) คะ” ซึ่งโยนงานจำแนกกลับให้ลูกค้า, ให้ยกตัวอย่างที่แยกตัวเลือกออกจากกันแทน ' +
          '(ใครจ่าย จ่ายค่าอะไร) จาก ### §40 Income Signals ###, ห้ามถามกว้างซ้ำแบบ “ช่วยเล่ารายละเอียดเพิ่มเติม” · ' +
          'เว้นว่าง = กลับไปใช้ค่า default (ข้อความเดิมทุกตัวอักษร) ไม่ใช่ปิดหัวข้อ · ' +
          'ส่วน "ถามย้ำได้กี่ครั้ง" เป็นคนละเรื่อง อยู่แท็บ "พฤติกรรมฟอร์ม" · ' +
          '⚠️ อ้างชื่อหัวข้อ ### … ### กลางประโยคได้ (ค่า default ก็ทำอยู่) แต่ห้ามพิมพ์ ### … ### ไว้ "ทั้งบรรทัด" ' +
          'ตัวแยกบล็อกใน Flow tab จะนับบรรทัดนั้นเป็นหัวข้อใหม่ แล้วเพี้ยนทั้งหน้า',
      },
      {
        key: 'clarifyDefaultPhraseTh',
        group: 'กติกาการถามย้ำ (Clarify)',
        label: 'ประโยคตั้งต้นตอนถามย้ำ (ใช้เมื่อ AI ไม่ได้ส่งประโยคมา)',
        kind: 'text',
        cols: 6,
        hint:
          '4 ช่องถัดจากนี้ประกอบกันเป็น "ประโยคดิบ" ของการถามย้ำ แล้วช่องกติกาด้านบนสั่งให้ AI เอาไปเรียบเรียงใหม่ · ' +
          'ปกติลูกค้าจึงไม่เห็นคำพวกนี้ตรง ๆ — ยกเว้นตอนเรียบเรียงล้มเหลว ระบบจะส่งประโยคดิบนี้แทน จึงยังต้องอ่านรู้เรื่อง · ' +
          'ค่าเริ่มต้นเป็นภาษาอังกฤษโดยตั้งใจ (เป็นโครงให้ AI ไม่ใช่คำพูดกับลูกค้า)',
      },
      {
        key: 'clarifyAboutLeadTh',
        group: 'กติกาการถามย้ำ (Clarify)',
        label: 'คำนำหน้าชื่อรายการที่กำลังถามย้ำ',
        kind: 'text',
        cols: 6,
        hint:
          'ระบบเขียนเป็น «<คำนี้> "ชื่อรายการ" — ประโยค» เครื่องหมายคำพูดกับขีดระบบใส่ให้เอง พิมพ์แค่คำเดียวพอ · ' +
          '🔴 การระบุชื่อรายการไม่ใช่ของประดับ: ถ้ามีค่าเช่าหลายรายการในข้อเดียว คำถาม "อันไหนคะ" ตอบไม่ได้เลย ' +
          'ถ้าไม่บอกว่ากำลังถามถึงรายการไหน',
      },
      {
        key: 'clarifyChooseLeadTh',
        group: 'กติกาการถามย้ำ (Clarify)',
        label: 'คำนำหน้าบรรทัดตัวเลือก',
        kind: 'text',
        cols: 6,
        hint:
          'บรรทัดตัวเลือกจะไม่ขึ้นเลยถ้าไม่มีตัวเลือกให้เลือก · ' +
          '🔴 อย่าเคาะเว้นวรรคท้าย — ทุกช่องถูกตัดช่องว่างหน้า-หลังตอนเซฟ ระบบเติมเว้นวรรคก่อนตัวเลือกให้เอง',
      },
      {
        key: 'clarifyChooseSeparatorTh',
        group: 'กติกาการถามย้ำ (Clarify)',
        label: 'ตัวคั่นระหว่างตัวเลือก',
        kind: 'text',
        cols: 6,
        hint:
          '🔴 อย่าเคาะเว้นวรรคหน้า-หลัง — ระบบเติมให้ทั้งสองข้าง (เหมือนช่องซ้าย) · ' +
          'ตัวคั่นที่ต้องการ "ไม่มีเว้นวรรค" เลย ทำจากหน้านี้ไม่ได้ ตั้งใจให้เป็นแบบนั้น เพราะบรรทัดนี้ถูกอ่านออกเสียง',
      },
      {
        key: 'confirmSkipOfferRulesTh',
        group: 'ชวนกลับไปกรอกข้อที่ข้ามไว้ (CONFIRM_SKIP)',
        label: 'กติกาการถามว่าจะกลับไปกรอกข้อที่ข้ามไว้ไหม (ทั้งก้อน)',
        kind: 'textarea',
        hint:
          'ใช้เฉพาะข้อที่เป็น CONFIRM_SKIP · ช่องนี้คือ "ทั้งก้อน" ของ ### Question Context ### ในเทิร์นนั้น · ' +
          '🔴 ต้องมี {SKIPPED} (รายชื่อข้อที่ข้ามไว้) และ {QUESTION} (ข้อความคำถามที่ตั้งไว้ในข้อนั้น) ครบทั้งคู่ ' +
          'ขาดตัวใดตัวหนึ่ง ระบบจะไม่ใช้ข้อความนี้เลย กลับไปใช้ค่า default แล้วเขียนเตือนไว้ใน turn log · ' +
          '🔴 ทำไมต้องมีรายชื่อ: ข้อความที่ตั้งไว้เป็นคำถามกลาง ๆ ("จะกลับไปกรอกข้อที่ข้ามไว้ไหมคะ") ถ้าไม่ส่งรายชื่อไปด้วย ' +
          'ลูกค้าถูกให้ตัดสินใจเรื่องคำถามที่ไม่มีใครบอกว่าคืออะไร แล้ว AI ก็แต่งขึ้นมาเอง · ' +
          '🔴 "ตอบได้แค่ กลับไปกรอก หรือ ข้ามต่อ" เป็นข้อเท็จจริงของระบบ ไม่ใช่สำนวน — ระบบเปิดข้อที่ข้ามกลับมา ' +
          '"ทั้งหมดพร้อมกัน" คำถามที่ชวนเลือกทีละข้อจึงสัญญาสิ่งที่ระบบทำไม่ได้ · ' +
          'เว้นว่าง = กลับไปใช้ค่า default (ข้อความเดิมทุกตัวอักษร)',
      },
      {
        key: 'askedBackWithKnowledgeRulesTh',
        group: 'เมื่อลูกค้าถามกลับแทนการตอบ',
        label: 'กติกาตอนมีข้อมูลอ้างอิง (### Knowledge ###)',
        kind: 'textarea',
        hint:
          'ใช้ตอนลูกค้า "ถามกลับ" แทนที่จะตอบคำถาม และคำถามข้อนั้นมีเอกสารความรู้ผูกไว้ + ค้นเจอจริง · ' +
          '🔴 ต้องมี {CUSTOMER_QUESTION} และ {QUESTION} ครบทั้งคู่ ขาดตัวใดตัวหนึ่ง = กลับไปใช้ค่า default · ' +
          '🔴 {CUSTOMER_QUESTION} ไม่ใช่ของประดับ: เทิร์นนี้ระบบ "ไม่" เอาข้อความลูกค้าไปใส่ใน ### History ### ' +
          '(จะกลายเป็นคำสั่ง 2 ชุดที่ขัดกันเรื่องประโยคเดียวกัน) token นี้จึงเป็นทางเดียวที่คำถามของลูกค้าไปถึง AI · ' +
          '🔴 "อ้างอิงเฉพาะข้อมูลใน ### Knowledge ### เท่านั้น" คือหัวใจ — ปล่อยให้ตอบนอกเอกสารเมื่อไร ' +
          'บอทจะตอบคำถามภาษีจากความรู้ตัวเอง ซึ่งเป็นสิ่งเดียวที่ ### Ask Guardrail ### ห้ามไว้',
      },
      {
        key: 'askedBackNoKnowledgeRulesTh',
        group: 'เมื่อลูกค้าถามกลับแทนการตอบ',
        label: 'กติกาตอนไม่มีข้อมูลอ้างอิง',
        kind: 'textarea',
        hint:
          'ใช้ตอนลูกค้าถามกลับ แต่คำถามข้อนั้นไม่มีเอกสารความรู้ผูกไว้ (หรือค้นไม่เจอ) — ให้บอกตรง ๆ ว่ายังตอบไม่ได้ ' +
          'แล้วถามคำถามเดิมซ้ำ · 🔴 token เหมือนช่องบน ({CUSTOMER_QUESTION} + {QUESTION}) บังคับทั้งคู่ · ' +
          '🔴 ทำไมต้องแยก 2 ช่อง: เมื่อก่อนมีสำนวนเดียวและมันอ้างถึง ### Knowledge ### ทั้งที่ตอนนั้นระบบไม่เคยส่ง ' +
          'หัวข้อนั้นเลย ⇒ กติกาข้อเดียวที่ทำได้จริงคือ "บอกว่ายังตอบไม่ได้" ลูกค้าที่ถามกลับจึง "ถูกปฏิเสธทุกคน" ' +
          'โดยโครงสร้าง · ห้ามเขียนช่องนี้ให้ชี้ไปที่ ### Knowledge ### เพราะจะสร้างทางตันเดิมขึ้นมาใหม่',
      },
      {
        key: 'calcResultLeadTh',
        group: 'บล็อกตัวเลขในข้อมูลฟอร์ม (### Tax Planning Form Data ###)',
        label: 'คำนำหน้าบล็อก "ผลคำนวณที่บันทึกแล้ว"',
        kind: 'textarea',
        hint:
          'ขึ้นเหนือตัวเลขที่ระบบคำนวณจากคำตอบในฟอร์มไปแล้ว · หน้าที่คือบอกที่มา เพื่อให้ AI ถือว่า ' +
          '"อ้างอิงเป็นตัวเลขจริงได้" — ซึ่งเป็นสิ่งที่ช่องถัดไปต้อง "ไม่" พูด',
      },
      {
        key: 'calcPreviewLeadTh',
        group: 'บล็อกตัวเลขในข้อมูลฟอร์ม (### Tax Planning Form Data ###)',
        label: 'คำนำหน้าบล็อก "ตัวเลขจำลอง"',
        kind: 'textarea',
        hint:
          'ขึ้นเหนือตัวเลขจำลองของคำถามที่กำลังถาม (เช่น เลือกหักเหมา vs ตามจริง จะได้เท่าไร) · ' +
          '🔴 ตัวเลขชุดนี้ "ยังไม่ได้บันทึก" และไม่ใช่ยอดของลูกค้า · สองช่องนี้คือสิ่งเดียวที่แยกว่าบล็อกไหนพูดได้ ' +
          'บล็อกไหนพูดไม่ได้ · เคสจริง: §40(5) ยอด 600,000 ค่าที่บันทึกไว้คือ 60,000 (อัตรา default 10% ' +
          'เพราะยังไม่ได้ตอบข้อนี้) ส่วนตัวเลขจำลองคือ 180,000 — บอทอ่านตัวที่ผิดออกไปว่า "หักค่าใช้จ่ายแบบเหมาได้รวม 60,000 บาท"',
      },
      {
        key: 'historyCaptionTh',
        group: 'ประวัติบทสนทนา (### History ###)',
        label: 'คำอธิบายเหนือประวัติบทสนทนา (ใช้ทั้งฝั่งถามและฝั่งสกัดคำตอบ)',
        kind: 'textarea',
        hint:
          'marker ### History ### · 🔴 ช่องนี้ช่องเดียวคุมทั้ง 2 ฝั่ง — ระบบส่งหัวข้อนี้ทั้งตอนถามและตอนสกัดคำตอบ ' +
          'บอกโมเดลว่าบทสนทนาเป็นบริบทเท่านั้น (USER: = ลูกค้าพูด, BOT: = บอทถาม) ห้ามตอบซ้ำ ห้ามดึงค่าจากในนี้มากรอกฟอร์ม · ' +
          '🔴 ประโยคท้ายสุดเป็น "กฎการอ่านคำตอบ" ไม่ใช่สำนวน: ถ้าลูกค้าพูดค่าเดิมซ้ำ ต้องนับเป็นคำตอบของข้อที่กำลังถาม ' +
          'เคยตัดออกแล้วตัวสกัดตอบ unclear วนไม่จบ · ' +
          '⚠️ ห้ามพิมพ์ ### … ### ลงในช่องนี้ ตัวแยกบล็อกใน Flow tab หา marker ด้วยตำแหน่ง จะเพี้ยนทั้งหน้า',
      },
      {
        key: 'askKnowledgeCaptionTh',
        group: 'ข้อมูลอ้างอิง (### Knowledge ###)',
        label: 'คำอธิบายเหนือข้อมูลอ้างอิง (ตอนถาม)',
        kind: 'textarea',
        hint:
          'marker ### Knowledge ### · เป็นกติกาการใช้เอกสารที่ค้นมาได้ — "ห้ามพูดเกินจากที่ให้มา ห้ามยกมาสอนกฎภาษีถ้าลูกค้าไม่ได้ถาม" · ' +
          'คู่กับ Guardrail ตอนถาม: อันนั้นห้าม "ตอบแทนลูกค้า" อันนี้ห้าม "แต่งเสริมจากที่ค้นมา" · ' +
          'ตัวเลือกว่าจะค้นอะไรมาอยู่ที่ knowledge ของหมวด/ข้อ + top-K และคะแนนขั้นต่ำในแท็บ LLM',
      },
      // ⚰️ TOMBSTONE (2026-08-03) — two textareas used to sit here:
      //     { key: 'phraseQuestionTaskTh', label: 'คำสั่งเรียบเรียงคำถาม (คำถามทั่วไป)',    kind: 'textarea' }
      //     { key: 'phraseConfirmTaskTh',  label: 'คำสั่งเรียบเรียงข้อความสรุปยืนยัน',       kind: 'textarea' }
      //   They fed `### Instruction ###`, which the backend no longer emits at all
      //   (BAL/BAL/FlowEngine/Nodes/TaxPromptStackV2.cs → AppendAskSections). A box whose text reaches nothing is
      //   worse than a missing box: the author types, saves, sees no change, and blames the model. So the CONTROLS
      //   are gone. Steering a question now belongs on the question itself — Tax Config V2 → คำถาม → prompt ของข้อนั้น.
      //
      // 🔴 The two KEYS deliberately stay in TAX_PLANNING_SETTINGS_V2_PROMPT_KEYS (services/taxPlanningSettingsV2Api.js).
      //   `saveVersion` is DIRECT-ASSIGN — a key missing from the payload is received as null and CLEARS the column.
      //   `save()` iterates TAX_PLANNING_SETTINGS_V2_ALL_KEYS (not the tab fields) and `applyDto` hydrates
      //   `values[key]` for that same list, so a key with no field descriptor still round-trips its stored value
      //   untouched: `isAtDefault`/`fieldErrors`/`payloadValue` all handle `FIELD_BY_KEY[key] === undefined`.
      //   Drop the keys from that list and the next save silently deletes whatever the admin had typed.
      {
        key: 'quickReplyMode',
        group: 'ปุ่มตอบด่วน (Quick reply)',
        label: 'ปุ่มตอบด่วน (quick reply)',
        kind: 'select',
        cols: 6,
        options: [
          { title: 'ตัวเลือกที่ตั้งไว้ ถ้าไม่มีค่อยให้ AI คิด (configThenLlm)', value: 'configThenLlm' },
          { title: 'ใช้เฉพาะตัวเลือกที่ตั้งไว้ ไม่เรียก AI (config)', value: 'config' },
          { title: 'ปิด ไม่แสดงปุ่มเลย (off)', value: 'off' },
        ],
        hint: 'ตัวเลือกคำตอบที่ตั้งไว้ในคำถาม (AnswerOptions / หมวดเงินได้ / CONFIRM) ชนะเสมอ — AI ถูกเรียกเฉพาะข้อที่ไม่มีตัวเลือกและไม่ใช่คำถามจำนวนเงิน/วันที่',
      },
      {
        key: 'quickReplyMaxBubbles',
        group: 'ปุ่มตอบด่วน (Quick reply)',
        label: 'จำนวนปุ่มสูงสุด (เฉพาะปุ่มที่ AI สร้าง)',
        kind: 'int',
        cols: 6,
        hint:
          'ตรงกับ quick_reply_max_bubbles ของ POC · ' +
          'ไม่ตัดตัวเลือกที่ตั้งไว้ในคำถาม — รายการที่เขียนไว้คือชุดคำตอบครบชุด ตัดทิ้งแล้วลูกค้าจะกดข้อนั้นไม่ได้ ' +
          '(จำแนกเงินได้ §40 มี 8 หมวด)',
      },
      {
        key: 'quickReplySystemPromptTh',
        group: 'ปุ่มตอบด่วน (Quick reply)',
        label: 'prompt สร้างปุ่มตอบด่วน (ใช้เฉพาะโหมด configThenLlm)',
        kind: 'textarea',
        hint: 'ใช้ต่อเมื่อคำถามนั้นไม่มีตัวเลือกที่ตั้งไว้เลย · ระบบประกอบ marker |quickReply| ให้เองจากรายการที่อ่านได้ ไม่เชื่อรูปแบบที่โมเดลพิมพ์',
      },
    ],
  },
  {
    value: 'extract',
    label: 'ใช้สกัดคำตอบ (Extract)',
    testid: 'tpsv2-tab-extract',
    icon: 'mdi-text-search',
    noteType: 'warning',
    note:
      'prompt ที่ระบบใช้ตอนสกัด/จำแนกคำตอบ (extractFields) · ตัวที่เป็น JSON contract (Rules) ' +
      'แก้ด้วยความระวัง — โครงสร้างพัง = การสกัดคำตอบเพี้ยนทั้งเส้น',
    fields: [
      {
        key: 'completionRestartPromptTh',
        group: 'คำถามปิดท้าย (จบฟอร์มแล้ว)',
        label: 'prompt ตัดสินว่าลูกค้าขอ "เริ่มวางแผนภาษีใหม่" หรือไม่',
        kind: 'textarea',
        hint:
          'ใช้กับคำถามปิดท้ายที่ระบบสร้างเอง (ฟอร์มตอบครบและปิดไปแล้ว) — คำถามข้อนี้ไม่มีแถวใน Tax Question V2 ' +
          'จึงไม่มี "prompt ต่อข้อ" ให้แขวน ต้องตั้งจากที่นี่ · คู่กับช่อง "ประโยคที่ทำให้ข้อความปิดท้ายกลายเป็นคำถาม" ' +
          'ในแท็บ พฤติกรรมฟอร์ม · ' +
          '🔴 ต้องมีคำว่า RESTART อยู่ในข้อความ — ระบบเทียบคำตอบกับคำนี้แบบตรงตัว ถ้าขาด ลูกค้าจะตอบ "ใช่" ไม่ได้เลย ' +
          'และจะติดอยู่กับฟอร์มที่จบแล้วโดยไม่มีทางขอเริ่มใหม่ · ระบบจะไม่ใช้ข้อความที่ขาดคำนี้ กลับไปใช้ค่า default แทน · ' +
          '🔴 กติกา "ไม่แน่ใจให้ตั้ง unclear" ห้ามหาย: ตอบผิดทาง = ทิ้งแผนที่เพิ่งทำเสร็จแล้วเริ่มถามใหม่ตั้งแต่ข้อแรก ' +
          'ส่วนตอบพลาดอีกทางเสียแค่ 1 เทิร์น (ข้อเสนอยังอยู่บนจอ) · ' +
          '🔴 ต้องสั่งเรื่อง focusIntent แยกจาก value ด้วย — ร่างแรกพูดถึงแต่ value พอบอกให้ "ตอบ UNCLEAR" ' +
          'โมเดลก็ตั้ง focusIntent = unclear ให้ด้วย ลูกค้าที่ถามว่าทำไมหักค่าใช้จ่ายได้แค่นั้นเลยโดนอ่านข้อความปิดท้ายกลับใส่',
      },
      {
        key: 'extractContractPromptTh',
        group: 'โครงสร้างผลลัพธ์ (JSON contract)',
        label: 'Turn contract — กติกาผลลัพธ์ JSON',
        kind: 'textarea',
        hint: 'marker ### Rules ### · อยู่ท้ายสุดของ prompt เสมอ · เป็น JSON contract — แก้ด้วยความระวัง',
      },
      {
        key: 'extractFocusIntentPromptTh',
        group: 'โครงสร้างผลลัพธ์ (JSON contract)',
        label: 'Focus intent — ตีความเจตนาต่อคำถามที่กำลังถาม',
        kind: 'textarea',
        hint: 'marker ### Focus Intent ### · แยก section ของตัวเอง วางไว้ก่อน ### Rules ### เสมอ',
      },
      {
        key: 'extractConfirmPromptTh',
        group: 'โครงสร้างผลลัพธ์ (JSON contract)',
        label: 'Confirm — ตีความ ยืนยัน / ขอแก้',
        kind: 'textarea',
        hint: 'marker ### Confirm ### · ใส่เฉพาะตอนที่ข้อนั้นเป็นจุดยืนยัน (CONFIRM)',
      },
      {
        key: 'extractFormDataCaptionTh',
        group: 'ข้อมูลที่ลูกค้ากรอกไว้แล้ว (### Tax Planning Form Data ###)',
        label: 'คำอธิบายเหนือข้อมูลในฟอร์ม (ตอนสกัดคำตอบ)',
        kind: 'textarea',
        hint:
          'marker ### Tax Planning Form Data ### · หัวข้อเดียวกับฝั่งถาม แต่คนละหน้าที่ — ฝั่งนี้บอกให้ใช้ข้อมูลเดิมตัดสินว่าจะ set / edit / del ' +
          'และรายการที่มีอยู่แล้วต้องอ้างของเดิม ห้ามสร้างใหม่ซ้ำ · แยกช่องกับฝั่งถามโดยตั้งใจ: เอาคำสั่งนี้ไปให้ฝั่งถามอ่าน = ชวนให้มันไปแก้ฟอร์มแทนที่จะถาม',
      },
      {
        key: 'extractKnowledgeCaptionTh',
        group: 'ข้อมูลอ้างอิง (### Knowledge ###)',
        label: 'คำอธิบายเหนือข้อมูลอ้างอิง (ตอนสกัดคำตอบ)',
        kind: 'textarea',
        hint:
          'marker ### Knowledge ### · เอกสารชุดเดียวกับฝั่งถาม แต่คนละหน้าที่ — ฝั่งนี้ใช้แปลงสิ่งที่ลูกค้าพิมพ์ให้เป็นค่าที่เก็บได้ ' +
          'ไม่ได้ใช้เรียบเรียงคำถาม · แยกช่องกับฝั่งถามด้วยเหตุผลเดียวกับคำอธิบายข้อมูลในฟอร์ม',
      },
      {
        key: 'section40SignalsPromptTh',
        group: 'การจำแนกประเภทเงินได้',
        label: 'สัญญาณจำแนกเงินได้ §40',
        kind: 'textarea',
        hint: 'marker ### §40 Income Signals ### · ใส่ทุกครั้งในฝั่งสกัดคำตอบ',
      },
      {
        key: 'incomeClassifyRulesTh',
        group: 'การจำแนกประเภทเงินได้',
        label: 'กติกาจำแนกประเภทเงินได้ (Income Classification)',
        kind: 'textarea',
        hint: 'marker ### Income Classification ### · token {REF_LIST} = รายการ ref ที่ให้เลือก (ระบบแทนค่าให้)',
      },
      {
        key: 'subTypeMappingRulesTh',
        group: 'การจำแนกประเภทเงินได้',
        label: 'กติกา map ประเภทย่อยของเงินได้ (Sub-Types)',
        kind: 'textarea',
        hint:
          'marker ### Income Sub-Types ### · tokens {CATALOG} = แคตตาล็อกประเภทย่อย, ' +
          '{SECTION_REF} = ref ของมาตราที่กำลังโฟกัส (ระบบแทนค่าให้)',
      },
    ],
  },
  // 🔴 "เทิร์นพิเศษ" ไม่ใช่คำถามในฟลว์คำถาม: เปิดเมื่อลูกค้าขอแก้ข้อมูลแต่ยังไม่ได้บอกว่าจะแก้อะไร
  //    (ตอบ "ไม่ใช่ / ขอแก้" บนด่านยืนยัน · ด่านยืนยันที่ไม่มีรายการให้ทวน · ขอแก้กลางคันนอกด่านยืนยัน)
  //    เดิมเส้นทางนี้ยืม prompt ของ "ข้อ CONFIRM" มาปรุงข้อความ ผลคือลูกค้าเห็นคำถามยืนยันเดิมซ้ำพร้อมปุ่ม
  //    ใช่/ไม่ใช่ แทนที่จะถูกถามว่าจะแก้รายการไหน — แท็บนี้จึงให้เทิร์นนั้นมี prompt / โมเดล / ความรู้ของตัวเอง
  //    แยกขาดจากแท็บ ใช้ถาม (Ask) และ ใช้สกัดคำตอบ (Extract)
  {
    value: 'editRequest',
    label: 'ขอแก้ (Edit Request)',
    testid: 'tpsv2-tab-editrequest',
    icon: 'mdi-pencil-box-outline',
    noteType: 'info',
    note:
      'เทิร์นพิเศษตอนลูกค้าขอแก้ข้อมูล — ใช้เมื่อลูกค้าบอกว่า "ขอแก้ / ไม่ใช่ / ข้อมูลคลาดเคลื่อน" แต่ยังไม่ได้บอกว่า ' +
      'จะแก้รายการไหน ระบบจะปรุงคำถาม "จะแก้รายการไหน" ด้วย prompt ในแท็บนี้ (พร้อมปุ่มรายชื่อรายการที่แก้ได้) ' +
      'แล้วอ่านคำตอบเทิร์นถัดไปด้วย prompt อีกตัวในแท็บเดียวกัน · จับไม่ได้ครบจำนวนรอบที่ตั้งไว้ = กลับไปด่านยืนยันเดิม ' +
      'โดยไม่แตะคำตอบที่บันทึกไว้ · ทุกช่องเว้นว่าง = ใช้ค่าเริ่มต้นในโค้ด (ปิดสวิตช์บนสุด = ไม่เปิดเทิร์นนี้เลย ' +
      'กลับไปพฤติกรรมเดิม)',
    fields: [
      // ── การเปิดใช้ ──
      {
        key: 'editRequestEnabled',
        group: 'การเปิดใช้',
        label: 'เปิดเทิร์นขอแก้',
        kind: 'bool',
        cols: 6,
        hint:
          'เปิด (ค่าเริ่มต้น) = ลูกค้าที่ขอแก้แบบไม่ระบุข้อจะถูกถามกลับว่าจะแก้รายการไหน · ' +
          'ปิด = กลับไปพฤติกรรมเดิม (ถามข้อยืนยันเดิมซ้ำ) กติกาและ prompt ที่ตั้งไว้ยังอยู่ครบ',
      },
      {
        key: 'editRequestAttemptLimit',
        group: 'การเปิดใช้',
        label: 'จำนวนรอบที่ถามซ้ำได้',
        kind: 'int',
        cols: 6,
        hint:
          'ถามว่า "จะแก้รายการไหน" ได้กี่รอบก่อนยอมแพ้แล้วกลับไปด่านยืนยันเดิม (ค่าเริ่มต้น 2) · ' +
          'เว้นว่าง = ใช้ค่าเริ่มต้น · ครบรอบแล้วระบบไม่ลบและไม่ปิดข้อไหนทั้งสิ้น แค่พาลูกค้ากลับไปที่คำถามยืนยัน',
      },
      // ── คำสั่ง (prompt) ──
      {
        key: 'editRequestAskPromptTh',
        group: 'คำสั่ง (prompt)',
        label: 'คำสั่งปรุงคำถาม “จะแก้รายการไหน”',
        kind: 'textarea',
        hint:
          'ใช้ตอนระบบถามกลับว่าจะแก้อะไร · token {CANDIDATES} = รายชื่อรายการที่แก้ได้ (ชุดเดียวกับปุ่มที่แนบไปและ ' +
          'ชุดเดียวกับที่ตัวจับคำตอบยอมรับ), {LANGUAGE} = ภาษาที่ต้องตอบ, {ATTEMPT} = รอบที่ถาม (1, 2, …) · ' +
          'เว้นว่าง = ใช้ค่าเริ่มต้นในโค้ด · 🔴 ห้ามสั่งให้แต่งรายการนอก {CANDIDATES} — ลูกค้าจะเลือกสิ่งที่ระบบจับกลับไม่ได้',
      },
      {
        key: 'editRequestExtractPromptTh',
        group: 'คำสั่ง (prompt)',
        label: 'คำสั่งอ่านคำตอบว่าจะแก้รายการไหน (JSON contract)',
        kind: 'textarea',
        hint:
          'ใช้ตอนอ่านคำตอบของเทิร์นถัดไป · token {CANDIDATE_REFS} = ref (FieldKey) ที่ตอบกลับได้, ' +
          '{CANDIDATES} = ชื่อรายการที่ลูกค้าเห็น · เว้นว่าง = ใช้ค่าเริ่มต้นในโค้ด · ' +
          '🔴 เป็น JSON contract — แก้ด้วยความระวัง โครงสร้างพัง = อ่านคำตอบไม่ได้ทุกครั้งแล้ววนถามจนครบรอบ',
      },
      // ── โมเดล ──
      // 🔴 ไม่เปิดสวิตช์ = ยืมโมเดลฝั่ง ASK (แท็บ LLM) มาใช้ทั้งดุ้น — กติกาเดียวกับ useQuestionModel
      {
        key: 'editRequestUseOwnModel',
        group: 'โมเดล',
        label: 'ใช้โมเดลแยกสำหรับเทิร์นขอแก้',
        kind: 'bool',
        cols: 6,
        hint:
          'ปิด (ค่าเริ่มต้น) = ใช้โมเดลตอนปรุงคำถาม (ASK) เดิม · เปิดอย่างเดียวไม่พอ ต้องเลือก provider + model ' +
          'ด้านล่างด้วย ไม่งั้นระบบถอยกลับไปใช้โมเดล ASK เหมือนเดิม',
      },
      {
        key: 'editRequestProvider',
        group: 'โมเดล',
        label: 'Provider (เทิร์นขอแก้)',
        kind: 'provider',
        cols: 6,
        maxlength: 50,
        hint: 'เว้นว่าง = ใช้ provider ของโมเดล ASK · งานนี้เป็นการถามสั้น ๆ + อ่าน JSON สั้น ๆ เลือกโมเดลที่เร็ว/ถูกกว่าได้',
      },
      {
        key: 'editRequestModelName',
        group: 'โมเดล',
        label: 'Model name (เทิร์นขอแก้)',
        kind: 'model',
        // 🔴 กรองรายการด้วย provider ของตัวเอง เหมือนคู่ ASK / Voice Fill — ไม่ใช่ provider ของโมเดลหลัก
        providerKey: 'editRequestProvider',
        cols: 6,
        maxlength: 200,
        hint:
          'ค่าที่บันทึกคือคีย์เต็ม "ชื่อ|id" เหมือนช่องโมเดลหลัก · ต้องเลือกคู่กับ Provider ด้านซ้าย ' +
          'ไม่งั้นระบบถอยไปใช้โมเดล ASK',
      },
      // VIRTUAL — สัญญาเดียวกับ `gptModelRef` ของโมเดลหลัก/ฝั่ง ASK แต่เป็นคู่ของเทิร์นขอแก้
      {
        key: 'editRequestGptModelRef',
        group: 'โมเดล',
        label: 'บอทจาก Model Creator (เทิร์นขอแก้)',
        kind: 'gptModelRef',
        cols: 12,
        idKey: 'editRequestModelID',
        displayKey: 'editRequestModelDisplayID',
        hint:
          'เลือกบอทที่สร้างไว้ในหน้า Model Creator (เฉพาะที่ publish แล้ว) สำหรับเทิร์นขอแก้ — ระบบจะเติม ' +
          'Model ID และ Model display ID ให้พร้อมกันจากบอทตัวเดียวกัน · กดกากบาทเพื่อล้างทั้งคู่ (กลับไปใช้ค่าเริ่มต้น)',
      },
      // 🔴 คงไว้ทั้งคู่ (kind 'int') เพราะ `payloadValue` coerce เป็นตัวเลขจาก `FIELD_BY_KEY[key].kind` —
      //    ลบทิ้งเมื่อไหร่ค่าจะถูกส่งเป็น string ไปยัง `int?` แล้วได้ 400 · `hidden` = ไม่ render แต่ยังโผล่มา
      //    ให้แก้ได้เสมอถ้ามี validation error (ดู `fieldVisible`)
      { key: 'editRequestModelID', group: 'โมเดล', label: 'Model ID (เทิร์นขอแก้)', kind: 'int', cols: 6, hidden: true, hint: 'คีย์ JSON คือ editRequestModelID (ตัว D ใหญ่) · ตั้งค่าผ่านช่อง "บอทจาก Model Creator (เทิร์นขอแก้)"' },
      { key: 'editRequestModelDisplayID', group: 'โมเดล', label: 'Model display ID (เทิร์นขอแก้)', kind: 'int', cols: 6, hidden: true, hint: 'คีย์ JSON คือ editRequestModelDisplayID · ตั้งค่าผ่านช่อง "บอทจาก Model Creator (เทิร์นขอแก้)"' },
      // ── ความรู้ที่ใช้ ──
      // 🔴 แยก 2 ชุดโดยตั้งใจ (เหมือนที่หน้า Tax Question V2 แยก question/answer): ชุดปรุงคำถามควรเป็นเอกสาร
      //    ที่ช่วยเรียกชื่อรายการให้ลูกค้าเข้าใจ ส่วนชุดอ่านคำตอบควรเป็นเอกสารที่ช่วยแปลงสิ่งที่ลูกค้าพิมพ์ให้เป็น ref
      {
        key: 'editRequestAskKnowledgeCategoriesJson',
        group: 'ความรู้ที่ใช้',
        label: 'หมวดย่อยเอกสาร — ตอนปรุงคำถาม',
        kind: 'knowledgeCategories',
        hint:
          'เอกสารที่แนบเข้า prompt ตอนถามว่าจะแก้รายการไหน · เว้นว่าง = ไม่ใช้ knowledge ในขั้นนี้ (ไม่ยิงค้นเลย) · ' +
          'เลือกเป็น "หมวดย่อย" ระบบจะไล่หาเอกสารในหมวดนั้นสด ๆ ทุกเทิร์น — เอกสารที่เพิ่งอัปโหลดเข้าไปถูกใช้ทันที · ' +
          'จำนวนชิ้นต่อการค้นและคะแนนขั้นต่ำใช้ค่ากลางในแท็บ LLM',
      },
      {
        key: 'editRequestExtractKnowledgeCategoriesJson',
        group: 'ความรู้ที่ใช้',
        label: 'หมวดย่อยเอกสาร — ตอนอ่านคำตอบ',
        kind: 'knowledgeCategories',
        hint:
          'เอกสารที่แนบเข้า prompt ตอนอ่านว่าลูกค้าจะแก้รายการไหน · เว้นว่าง = ไม่ใช้ knowledge ในขั้นนี้ · ' +
          'แยกจากชุดด้านบนโดยตั้งใจ — คนละหน้าที่กัน (ชุดบนช่วยเรียบเรียงคำถาม ชุดนี้ช่วยแปลงคำพูดเป็นรายการ)',
      },
    ],
  },
  // 🔴 คนละเส้นทางกับ 7 แท็บข้างบน: แท็บอื่นตั้งค่าให้ "โหนด Tax Planning V2" ส่วนแท็บนี้ตั้งค่าให้
  //    endpoint POST /api/v1/LHB/VoiceFill ที่มือถือเรียกตอนลูกค้ากดพูดใส่การ์ด SHORT_FORM —
  //    ไม่มี marker ไม่เข้า prompt stack และ VoiceFillBAL เองไม่รู้จักภาษี (controller เป็นคนอ่าน
  //    คอลัมน์พวกนี้ส่งให้) ที่มันมาอยู่ในตารางนี้เพราะเป็นที่เดียวที่มี versioning + ตัวแก้ของแอดมิน
  //    + การ pin เวอร์ชันจาก playground อยู่แล้ว
  {
    value: 'voiceFill',
    label: 'กรอกด้วยเสียง (Voice Fill)',
    testid: 'tpsv2-tab-voicefill',
    icon: 'mdi-microphone-outline',
    noteType: 'info',
    note:
      'ใช้ตอนลูกค้าพูดใส่การ์ดฟอร์มสั้น (SHORT_FORM) แล้วระบบเติมช่องให้ — คนละตัวกับ prompt ของโหนดสนทนา ' +
      'ปล่อยว่างทุกช่อง = ใช้ prompt เริ่มต้นในโค้ดและยืมโมเดลของโหนด Tax Planning V2 มาใช้ · ' +
      'คำตอบของ API มี action: input = พูดแล้วลงช่องได้จริง · none = พูดนอกเรื่องไม่มีช่องไหนรับค่า · ' +
      'submit/cancel = ลูกค้าสั่งยืนยันหรือยกเลิกการ์ดด้วยเสียง ' +
      '— ตัว API ยังไม่เขียนอะไรลงฟอร์มเหมือนเดิม แอปเป็นคนกดปุ่มให้',
    fields: [
      {
        key: 'voiceFillPromptTh',
        group: 'Prompt',
        label: 'System prompt ของการกรอกด้วยเสียง',
        kind: 'textarea',
        hint:
          'ระบบต่อท้ายให้เอง 2 ก้อน: รายการช่องบนจอ (แถวละช่อง i|l|t|v) + สัญญารูปคำตอบ ' +
          '{"v":{"<i ของช่อง>":"<ค่า>"},"a":"none|submit|cancel"} — ' +
          '(prompt สั่งได้แค่ 3 คำนี้ ส่วนค่า input โค้ดเติมเองเมื่อมีช่องรับค่าจริง) ' +
          'ก้อนหลังอยู่ในโค้ด (VoiceFillBAL.OutputContract) แก้จากหน้านี้ไม่ได้ และไม่ต้องเขียนซ้ำในช่องนี้ · ' +
          'ปล่อยว่าง = ใช้ prompt เริ่มต้นในโค้ด (VoiceFillPromptDefaults) · ' +
          '⚠️ กติกาที่ห้ามหาย: ใส่เฉพาะช่องที่ลูกค้าพูดถึง, ห้ามคำนวณ/เดา และห้ามสร้าง id ใหม่ — ' +
          'ช่องที่ไม่ได้พูดถึงต้องคงค่าเดิมเป๊ะ',
      },
      // ── โมเดลแยกของ Voice Fill (ไม่เลือก = ใช้โมเดลหลักของโหนด) ──
      {
        key: 'voiceFillProvider',
        group: 'โมเดลแยกสำหรับกรอกด้วยเสียง',
        label: 'Provider (Voice Fill)',
        kind: 'provider',
        cols: 6,
        maxlength: 50,
        hint:
          'ไม่เลือก = ใช้ provider/โมเดลของโหนด Tax Planning V2 · งานนี้เป็นการสกัดค่าสั้น ๆ ' +
          'เลือกโมเดลที่เร็ว/ถูกกว่าได้โดยไม่กระทบบทสนทนา',
      },
      {
        key: 'voiceFillModelName',
        group: 'โมเดลแยกสำหรับกรอกด้วยเสียง',
        label: 'Model name (Voice Fill)',
        kind: 'model',
        // 🔴 กรองรายการด้วย provider ของตัวเอง เหมือนคู่ ASK — ไม่ใช่ provider ของโมเดลหลัก
        providerKey: 'voiceFillProvider',
        cols: 6,
        maxlength: 200,
        hint:
          'ค่าที่บันทึกคือคีย์เต็ม "ชื่อ|id" เหมือนช่องโมเดลหลัก · ต้องเลือกคู่กับ Provider ด้านซ้าย ' +
          'ไม่งั้นระบบถอยไปใช้โมเดลของโหนด',
      },
      // 🔴 ไม่มีช่อง "บอทจาก Model Creator" ที่นี่ โดยตั้งใจ (ต่างจากโมเดลหลักและโมเดลฝั่ง ASK)
      //    งานนี้คือสกัดค่าไม่กี่ช่องด้วย system prompt ของตัวเองแล้วจบ ไม่ใช้บุคลิก/ความรู้/เครื่องมือของบอท —
      //    เลือกบอทมาใช้จึงไม่ได้อะไรเพิ่มนอกจากช่องทางให้ config หลุดกัน
      //    คู่คอลัมน์ voiceFillModelID / voiceFillModelDisplayID ยังอยู่ในตารางและยังถูกส่งไปกับ payload
      //    ทุกครั้งด้วยค่าที่อ่านมา (carry-through เหมือน phraseQuestionTaskTh) — บันทึกจากหน้านี้จึงไม่ทำให้
      //    ค่าที่เคยตั้งผ่าน API หาย และก็ไม่มีทางตั้งใหม่จากหน้านี้เช่นกัน
      //    ⚠️ ห้ามเติมช่องกลับมาโดยไม่ทวนกับเจ้าของฟีเจอร์ก่อน — VoiceFillBAL หาโมเดลจาก provider + ชื่อโมเดล
      //    เท่านั้น (BuildModelAsync → FindCatalogModel) สองคอลัมน์นั้นไม่ได้ใช้ค้นหาอะไรเลย
      {
        key: 'voiceFillTimeoutMs',
        group: 'การเรียกใช้งาน',
        label: 'Timeout (มิลลิวินาที)',
        kind: 'int',
        cols: 6,
        hint:
          'เพดานเวลาของการเรียก LLM หนึ่งครั้ง (ค่าเริ่มต้น 8000) · หมดเวลา = API ตอบ 500 แล้วแอปจะเงียบ ' +
          'ให้ลูกค้าพิมพ์เองแทน · ⚠️ เป็นเพดานแบบนุ่ม: ตัวเรียกไม่มี CancellationToken งานที่ค้างยังวิ่งต่อและยังเสีย token',
      },
    ],
  },
  // 🔴 แท็บสุดท้ายโดยตั้งใจ: มันคือชั้นสุดท้ายของข้อความจริง ๆ — ทุกแท็บข้างบนกำหนดว่าโหนดจะ "พูดอะไร"
  //    ส่วนแท็บนี้กำหนดว่าข้อความที่พูดแล้วจะถูก "สะกดคำ" อย่างไรก่อนออกจากระบบ (ไม่มี marker ไม่เข้า prompt stack
  //    ไม่ถึง LLM เลย) · ชื่อแท็บนี้ถูกอ้างอิงแบบตัวอักษรใน TaxPlaygroundV2BAL.SettingsColumnLabelThMap
  //    ("แท็บ ตรวจก่อนส่ง (Guard) → …") เปลี่ยนชื่อที่นี่แล้วคำอธิบาย "ไปแก้ที่ไหน" ในหน้า playground จะชี้ผิด
  //    โดยไม่มี build error
  {
    value: 'guard',
    label: 'ตรวจก่อนส่ง (Guard)',
    testid: 'tpsv2-tab-guard',
    icon: 'mdi-shield-check-outline',
    noteType: 'warning',
    note:
      'ชั้นสุดท้ายก่อนข้อความถึงลูกค้า — ระบบไล่กฎในแท็บนี้ทับข้อความที่โมเดลตอบ "ทุกใบ" (รวมข้อความตอนระบบขัดข้อง ' +
      'และข้อความที่ทดสอบจาก Tax Playground) แล้วค่อยส่งออกและบันทึกลงประวัติแชท ⇒ สิ่งที่ลูกค้าเห็นกับสิ่งที่อยู่ในล็อก ' +
      'เป็นข้อความเดียวกันเสมอ · มีชั้นนี้เพราะ <brk/> (ตัวคั่นฟองแชทของแอปมือถือ) พิมพ์ผิดนิดเดียว เช่น "< brk/>" ' +
      'ที่มีช่องว่าง แอปก็แยกฟองไม่ออกแล้วลูกค้าเห็น token ดิบ — พรอมต์สั่งห้ามได้แต่ไม่เคยเหลือศูนย์ · ' +
      '🔴 กฎที่เขียนผิดกระทบข้อความขาออกทุกข้อความ ไม่ใช่เฉพาะเคสที่ตั้งใจแก้ — แก้แล้วดูผลจริงที่ขั้นตอน GUARD ' +
      'ในแท็บ Flow ของ Tax Playground ก่อนเปิดใช้จริง',
    fields: [
      {
        key: 'enableOutputGuard',
        group: 'ตัวตรวจข้อความก่อนส่ง',
        label: 'เปิดตัวตรวจข้อความก่อนส่ง',
        kind: 'bool',
        cols: 6,
        hint:
          'เปิด (ค่าเริ่มต้น) = เดินกฎด้านล่างทุกครั้งก่อนส่ง · ปิด = ส่งข้อความที่โมเดลตอบออกไปดิบ ๆ ทุกไบต์ ' +
          'โดยไม่ต้องไปแก้กติกาที่ตั้งไว้ — เป็นสวิตช์ปิดด่วนตอนสงสัยว่ากฎทำข้อความเสีย กติกาที่พิมพ์ไว้ยังอยู่ครบ · ' +
          '⚠️ ปิดแล้ว token ที่โมเดลพิมพ์เพี้ยน (เช่น "< brk/>") จะกลับไปโผล่ในฟองแชทของลูกค้าเหมือนเดิม',
      },
      {
        key: 'outputGuardRulesJson',
        group: 'ตัวตรวจข้อความก่อนส่ง',
        // 🔴 ห้ามเปลี่ยนป้ายนี้ (และชื่อแท็บด้านบน) โดยไม่แก้ TaxPlaygroundV2BAL.SettingsColumnLabelThMap
        //    ด้วย — หน้า Playground อ้างสองสตริงนี้แบบตัวอักษรเพื่อบอกว่า "ค่านี้ไปแก้ที่ไหน"
        label: 'กติกาตรวจข้อความก่อนส่ง (JSON)',
        // ตารางแทน textarea: ช่อง "ค้นหา" เก็บ regex ดิบ (ไม่ต้องพิมพ์ backslash คู่) และลำดับแถวคือลำดับรัน
        // — คอมโพเนนต์ยังคืนค่าเป็น string ของ JSON เหมือน kind 'textarea' ทุกประการ
        kind: 'guardRules',
        hint:
          'ปล่อยว่าง = ใช้ชุดกฎเริ่มต้นในโค้ด · ใส่ [] (ลบกฎออกจนหมด) = ปิดทุกกฎ แต่ตัวตรวจยังเดินอยู่ ' +
          '(ทับชุดเริ่มต้นทิ้งด้วย ต่างจากสวิตช์ด้านบน) · ลำดับบนลงล่าง = ลำดับรัน กฎถัดไปทำงานบนผลของกฎก่อนหน้า · ' +
          '🔴 ตัวตรวจแตะเฉพาะ "เนื้อความ" — ทุกอย่างตั้งแต่ |taxcard| / |quickReply| ไปจนจบข้อความ ' +
          'ไม่ถูกแตะแม้แต่ไบต์เดียว และถ้ากฎทำให้ข้อความว่างหรือบานผิดปกติ ระบบจะคืนข้อความเดิมทั้งดุ้น',
      },
    ],
  },
];

/** key → field descriptor, so save/validation can find a field's `kind` without scanning tabs. */
const FIELD_BY_KEY = {};
SETTINGS_TABS.forEach((t) => t.fields.forEach((f) => { FIELD_BY_KEY[f.key] = f; }));

/**
 * provider field → the model field it owns. Derived from the registry (`providerKey`) rather than
 * hard-coded, so adding a third provider/model pair needs nothing here.
 * 🔴 The pairs are INDEPENDENT: changing the ASK-side provider must never blank the main model, and
 * vice versa — that was the whole failure mode of the single hard-coded `modelName` sync.
 */
const MODEL_KEY_BY_PROVIDER_KEY = {};
Object.keys(FIELD_BY_KEY).forEach((k) => {
  const f = FIELD_BY_KEY[k];
  if (f.kind === 'model') MODEL_KEY_BY_PROVIDER_KEY[f.providerKey || 'provider'] = k;
});

export default {
  name: 'TaxPlanningSettingsV2Dialog',
  components: { TaxSettingsHistoryV2Dialog, TaxSettingsImportV2Dialog, TaxOutputGuardRulesEditor },
  props: {
    modelValue: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'saved', 'error'],
  data() {
    return {
      BOOL_ITEMS,
      DDTimeZone: TIME_ZONE_ITEMS,
      tabs: SETTINGS_TABS,
      tab: 'general',
      /** `{ [tab.value]: boolean }` — the tab's explainer starts clamped to one line. Every key is
       *  seeded here (not added on demand) so the flag stays plain-object reactive. */
      noteExpanded: SETTINGS_TABS.reduce((acc, t) => { acc[t.value] = false; return acc; }, {}),

      // ── level 1 ──
      view: 'list',
      versions: [],
      listLoading: false,
      listError: '',
      listMessage: '',
      /** succeeded-but-read-this lines (import / restore) — the WARNING channel, never success */
      listWarnings: [],
      listWarningTitle: '',
      busyId: null,
      /** row whose export download is in flight — kept apart from `busyId` so the activate
       *  buttons are not disabled by a read-only download */
      exportingId: null,

      // ── level 2 ──
      editingId: null,
      editingCode: '',
      editingIsActive: false,
      loading: false,
      saving: false,
      loadError: '',
      /** set when `loadDefaults` fails — BLOCKS save (see `loadDefaults` for why) */
      defaultsError: '',
      saveError: '',
      saveMessage: '',
      /** version identity — blank means "คงเดิม", NOT "กลับไปใช้ค่าเริ่มต้น" (these are not config) */
      identity: { versionCode: '', description: '' },
      /** the value shown in each control — hydrated from the DTO, or from the built-in default
       *  when the column is `null`. Equal-to-default (or blank) is sent back as `null` on save. */
      values: blankMap(null),
      /** built-in default per key (from `prompt-defaults` + `config-defaults`) */
      defaults: {},
      audit: { updatedBy: '', updatedDate: '' },
      /** `snapshot` at the moment the editor was (re)loaded — drives the "ยังไม่ได้บันทึก" flag.
       *  VIEW-ONLY: it never gates the save button, so it cannot trap an author. */
      baseline: '',

      // ── editor view filters (presentation only — never affects validation or the payload) ──
      fieldQuery: '',

      // ── การ์ด Time Context (แท็บ LLM) ──────────────────────────────────────────
      /**
       * สถานะสวิตช์ — **UI state ล้วน ๆ (derived)** ไม่มีคอลัมน์ `EnableTimeContext` ในฐานข้อมูล
       * และห้ามใส่คีย์นี้ลง `values` / payload เด็ดขาด (เหมือน node AI Model ที่ derive จาก
       * `item.timeZone ? true : false` — ModelManagement.vue:3197)
       * · โหลด: ตั้งค่าใน `applyDto` จาก `values.timeZone`
       * · ปิด: ล้าง `values.timeZone` ⇒ `isAtDefault` ⇒ save ส่ง `null`
       */
      timeContextEnabled: false,
      /** โซนเวลาก่อนหน้าที่ถูกล้างตอนปิดสวิตช์ — ใช้คืนค่าให้ถ้าผู้ใช้เผลอปิดแล้วเปิดกลับทันที */
      timeContextLastZone: '',

      // ── provider / model catalog (same source as the Flow node inspector) ──
      /** `data.provider` — `[{ id, providerName }]` */
      providerCatalog: [],
      /** `data.modelData` — `[{ modelName: "ชื่อ|id", modelDisplay, provider, ... }]` */
      modelCatalog: [],
      /**
       * `data.modelPublih` (fallback `data.allModel`) — the BOTS registry from Model Creator,
       * `[{ id, modelDisplayID, name, typeName, isPublish }]`. A DIFFERENT registry from
       * `modelCatalog` above (that one is the LLM list, `ModelData`): `id` here is `GPTModel.ModelID`
       * and `modelDisplayID` is `GPTModel.ModelDisplay` — exactly the pair `modelID`/`modelDisplayID`
       * store. An empty list here must NEVER raise `catalogError` (that banner is about Provider/Model).
       */
      gptModelCatalog: [],
      catalogLoading: false,
      catalogError: '',
      /**
       * หมวดย่อยเอกสาร (MetaDoc) สำหรับช่อง kind 'knowledgeCategories' — `{ value, title }` โดย
       * `value` คือ token `"DocumentCategory:Title"` ที่ backend resolve เป็นรายเอกสารตอนรัน
       * 🔴 คนละแหล่งกับ `modelCatalog`/`gptModelCatalog` (นั่นมาจาก `$call.GetAllDDModelManagement`)
       *    และ FAIL-SOFT แบบไม่มี banner โดยตั้งใจ: โหลดไม่ได้ = ไม่มีตัวเลือกใหม่ให้เลือก แต่ค่าที่บันทึกไว้
       *    ยังโชว์ครบ (ดู `knowledgeCategoryItems`) และยังบันทึกได้ — ไม่ควรไปย้อมทั้งแท็บด้วยคำเตือน
       */
      knowledgeCategoryCatalog: [],
      knowledgeCatalogLoading: false,
      /** โหลดไปแล้วอย่างน้อย 1 ครั้งในรอบเปิดนี้ — กันการยิงซ้ำทุกครั้งที่กด "แก้ไข" บนระบบที่ไม่มีเอกสารเลย */
      knowledgeCatalogTried: false,

      // ── sub-dialogs ──
      createDialog: false,
      createSaving: false,
      createError: '',
      createForm: { versionCode: '', description: '', cloneFromId: null },
      cloneDialog: false,
      cloneSaving: false,
      cloneError: '',
      cloneForm: { sourceId: null, sourceCode: '', versionCode: '', description: '' },
      deleteDialog: false,
      deleting: false,
      deleteError: '',
      deleteTarget: null,

      // ── save history / export / import ──
      historyDialog: false,
      /** the version the history dialog is looking at — may or may not be the one in the editor */
      historyVersionId: null,
      historyVersionLabel: '',
      historyVersionActive: false,
      /** the editor's history button is gated behind this when there are unsaved edits */
      historyDirtyDialog: false,
      importDialog: false,
    };
  },
  computed: {
    headerTitle() {
      if (this.view === 'editor') return `เวอร์ชัน ${this.editingCode || '-'}`;
      return 'Tax Planning Settings V2';
    },
    headerSubtitle() {
      if (this.view === 'editor') {
        return 'ทุกช่องใส่ค่าเริ่มต้นไว้ให้แล้ว — แก้เฉพาะช่องที่ต้องการ · ลบข้อความให้ว่าง = กลับไปใช้ค่าเริ่มต้น';
      }
      return 'ข้อความ prompt และ config กลางของ Tax Planning V2 — จัดการเป็นเวอร์ชัน (ใช้งานได้ทีละ 1 เวอร์ชัน)';
    },
    /** The one version the runtime is really using — surfaced in the list toolbar. */
    activeVersion() {
      return this.versions.find((v) => v.isActive) || null;
    },
    /** Vertical rail on desktop, the classic tab strip when there is no room for it. */
    railDirection() {
      const display = this.$vuetify && this.$vuetify.display;
      return display && display.mdAndUp === false ? 'horizontal' : 'vertical';
    },
    cloneSourceItems() {
      return this.versions.map((v) => ({
        title: `${v.versionCode || '-'}${v.isActive ? ' (ใช้งานอยู่)' : ''}`,
        value: v.id,
      }));
    },
    versionCodeErrors() {
      const code = String(this.identity.versionCode || '').trim();
      return code ? [] : ['ห้ามเว้นว่าง — รหัสเวอร์ชันเป็นตัวระบุที่ต้องไม่ซ้ำ'];
    },
    /** Per-key validation over all 87 columns. Keys with no rendered control (see the ⚰️ tombstone in the
     *  `ask` tab) resolve to `FIELD_BY_KEY[key] === undefined`; `fieldErrors` returns `[]` for that, which
     *  is right — an author cannot make a value invalid through a box that no longer exists. */
    errorsByKey() {
      const out = {};
      TAX_PLANNING_SETTINGS_V2_ALL_KEYS.forEach((key) => {
        out[key] = this.fieldErrors(FIELD_BY_KEY[key]);
      });
      // 🔴 VIRTUAL fields (`gptModelRef`) are rendered but are NOT columns, so the loop above never
      // reaches them. The template reads `errorsByKey[f.key].length` UNGUARDED — leaving them
      // `undefined` throws while rendering the LLM tab. They have nothing of their own to validate:
      // the real errors belong to the two `int` keys they write.
      SETTINGS_TABS.forEach((t) => t.fields.forEach((f) => {
        if (!out[f.key]) out[f.key] = [];
      }));
      // 🔴 `timeZone` ไม่มี descriptor แล้ว (การ์ด TIME CONTEXT เป็น section แบบ hard-code) จึงไม่มีใคร
      // ตรวจให้ — ยัด error ของมันเองตรงนี้ เพื่อให้ `invalidKeys` / `invalidEntries` / การบล็อกปุ่ม
      // บันทึกใน `save()` ใช้กลไกเดิมได้ทั้งชุด โดยไม่ต้องเพิ่มเงื่อนไขใหม่ที่ปุ่ม
      out.timeZone = this.timeContextErrors;
      return out;
    },
    /**
     * เปิดสวิตช์แล้วยังไม่เลือกโซนเวลา = บันทึกไม่ผ่าน — ถ้าปล่อยผ่าน คอลัมน์จะถูกบันทึกเป็น `null`
     * (ค่าว่าง = at default) แล้วสวิตช์จะเด้งกลับไปปิดตอนเปิด dialog ครั้งถัดไป ดูเหมือนของหาย
     */
    timeContextErrors() {
      if (!this.timeContextEnabled) return [];
      const raw = this.values.timeZone;
      const text = raw === null || raw === undefined ? '' : String(raw).trim();
      return text ? [] : [TIME_ZONE_REQUIRED_ERROR];
    },
    /**
     * ตัวเลือกของ dropdown โซนเวลา — ค่าที่บันทึกไว้แล้วแต่ไม่อยู่ในลิสต์ 6 ตัว จะถูก **prepend**
     * เป็นตัวเลือกของตัวเอง (precedent: `catalogItems`) · ช่องนี้เคยเป็น free-text ค่าเดิมของ admin
     * จึงเป็นอะไรก็ได้ ถ้าไม่ prepend ช่องจะโชว์ว่างแล้ว save ครั้งถัดไปจะล้างค่าเขาทิ้งเงียบ ๆ
     */
    timeZoneItems() {
      const rows = this.DDTimeZone;
      const raw = this.values.timeZone;
      const current = raw === null || raw === undefined ? '' : String(raw).trim();
      if (!current || rows.some((r) => r.value === current)) return rows;
      return [{ value: current, name: `${current} — ${ITEM_SUFFIX_UNKNOWN}` }].concat(rows);
    },
    /** ค่าที่ผูกกับ dropdown — `values` เก็บเป็น string เสมอ ('' = ยังไม่ได้เลือก) */
    displayTimeZone() {
      const raw = this.values.timeZone;
      return raw === null || raw === undefined ? '' : String(raw);
    },
    /**
     * The bots offered by the "บอทจาก Model Creator" picker.
     *   • only `typeName === 'Bot'` (a GuardRail is not a chat model),
     *   • a row with no `modelDisplayID` is unusable here (the pair would be half-empty) → skipped,
     *   • deduped by `modelDisplayID`, KEEPING THE HIGHEST `id` — one display id spans every version
     *     of a bot and the newest is the one in force, mirroring `ReportBAL.cs:1290-1293`.
     * `value` is a STRING: it is bound to a `v-autocomplete` against `values[displayKey]` (also a
     * string), and Vuetify compares by identity — `'128' !== 128` would show an empty box.
     */
    gptModelItems() {
      const order = [];
      const byDisplay = {};
      (this.gptModelCatalog || []).forEach((row) => {
        if (!row) return;
        if (String(row.typeName || '').trim() !== 'Bot') return;
        const raw = row.modelDisplayID;
        if (raw === null || raw === undefined || raw === '') return;
        const key = String(raw);
        const prev = byDisplay[key];
        if (!prev) {
          order.push(key);
          byDisplay[key] = row;
          return;
        }
        if ((Number(row.id) || 0) > (Number(prev.id) || 0)) byDisplay[key] = row;
      });
      return order.map((key) => {
        const row = byDisplay[key];
        const name = String(row.name || '').trim();
        return {
          // Model Creator calls `modelDisplay` "Model ID" (GPTModelManagement.vue:157) — that is the
          // number an author recognises, so it leads the label.
          title: name ? `${key}: ${name}` : key,
          value: key,
          modelId: row.id,
        };
      });
    },
    invalidKeys() {
      return TAX_PLANNING_SETTINGS_V2_ALL_KEYS.filter((k) => this.errorsByKey[k].length > 0);
    },
    /** Everything blocking save, as `{ key, label, tab }` — rendered as jump-to chips. */
    invalidEntries() {
      const out = this.versionCodeErrors.length
        ? [{ key: 'versionCode', label: 'รหัสเวอร์ชัน', tab: 'general' }]
        : [];
      this.invalidKeys.forEach((k) => {
        const owner = this.tabs.find((t) => t.fields.some((f) => f.key === k));
        // คีย์ที่เรนเดอร์ด้วย section แบบ hard-code ไม่มีบ้านใน `tabs` — ไม่งั้นชิปจะพาไปแท็บ general
        const extra = EXTRA_FIELD_HOMES[k];
        out.push({
          key: k,
          label: (FIELD_BY_KEY[k] && FIELD_BY_KEY[k].label) || (extra && extra.label) || k,
          tab: owner ? owner.value : (extra ? extra.tab : 'general'),
        });
      });
      return out;
    },
    filterActive() {
      return Boolean(String(this.fieldQuery || '').trim());
    },
    /** Everything an author can change, serialized — compared against `baseline`. */
    snapshot() {
      return JSON.stringify({ i: this.identity, v: this.values });
    },
    isDirty() {
      return Boolean(this.editingId) && Boolean(this.baseline) && this.snapshot !== this.baseline;
    },
    /**
     * The provider the RUNTIME will actually use. The control is hydrated with the built-in
     * default when the column is `null`, so the shown value IS the effective one. Used to filter
     * the model list, so the models offered match the provider that will really be in force.
     */
    effectiveProvider() {
      const raw = this.values.provider;
      return raw === null || raw === undefined ? '' : String(raw).trim();
    },
    /**
     * Providers, from `data.provider` (Flow.vue:17899 `listDDProvider`, rendered there with
     * `item-title/-value="providerName"` — Flow.vue:515-522). Nothing is hard-coded: if that list
     * is missing but models did load, the provider names are derived from the model rows.
     */
    providerItems() {
      const names = [];
      const push = (name) => {
        const n = name === null || name === undefined ? '' : String(name).trim();
        if (n && names.indexOf(n) < 0) names.push(n);
      };
      (this.providerCatalog || []).forEach((p) => push(p && (p.providerName || p.name)));
      (this.modelCatalog || []).forEach((m) => push(m && (m.provider || m.providerName)));
      return names.map((n) => ({ title: n, value: n }));
    },
    /**
     * Models for the effective provider — same filter as Flow.vue:20738-20744
     * (`listDDModel.filter(e => e.provider == value)`), with V1's `providerName` fallback
     * (TaxConfiguration.vue:1951-1953).
     * 🔴 `value` is the catalog's `modelName`, i.e. the full "ชื่อ|id" key — that is what the node
     * writes to `node.Data["model"]` and what the backend catalog lookup matches on.
     * DELIBERATE DIFFERENCE from the node: with no provider resolved we list the WHOLE catalog
     * instead of disabling the field, so an author can never be trapped with an un-fillable
     * required box (blank = validation error = cannot save).
     */
    modelItems() {
      return this.mapModelRows(this.effectiveProvider);
    },
    /**
     * The WHOLE model catalog, unfiltered — what "does this value resolve?" must be judged
     * against. The backend resolver uses the provider only as a TIE-BREAKER between same-named
     * entries, never as a filter, so a key that exists under another provider still resolves and
     * must not be flagged as missing. Only the dropdown list itself is provider-filtered
     * (`modelItems`), to mirror the node inspector.
     */
    allModelItems() {
      return this.mapModelRows('');
    },
    auditText() {
      const by = this.audit.updatedBy;
      const at = this.formatDate(this.audit.updatedDate);
      if (!by && !at) return 'ยังไม่เคยแก้ไข — ทุกช่องยังเป็นค่าเริ่มต้นของระบบ';
      return `แก้ไขล่าสุด ${at || '-'}${by ? ' โดย ' + by : ''}`;
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.openList();
    },
  },
  methods: {
    toggleNote(value) {
      this.noteExpanded[value] = !this.noteExpanded[value];
    },
    /**
     * 🔴 เวลาไทย — `updatedDate` มาจาก backend เป็น UTC ที่ไม่มี `Z` ต่อท้าย ⇒ getters ท้องถิ่นชุดเดิม
     * โชว์ช้าไป 7 ชม. (ดู `utils/thaiTime.js`) · รูปแบบ `YYYY-MM-DD HH:mm` และ fallback ที่หั่นสตริงดิบ
     * เมื่อ parse ไม่ผ่าน ยังเหมือนเดิมทุกประการ
     */
    formatDate(value) {
      if (!value) return '';
      const d = parseUtcDate(value);
      if (!d) return String(value).slice(0, 19).replace('T', ' ');
      return fmtThaiDateTimeIso(d);
    },

    // ══════════════ editor view: grouping, filtering, navigation ══════════════

    /** Free-text match over the label, the JSON key and the hint (all three are searchable). */
    fieldMatches(f) {
      const q = String(this.fieldQuery || '').trim().toLowerCase();
      if (!q) return true;
      return (
        String(f.label || '').toLowerCase().indexOf(q) >= 0 ||
        String(f.key || '').toLowerCase().indexOf(q) >= 0 ||
        String(f.hint || '').toLowerCase().indexOf(q) >= 0
      );
    },
    /**
     * 🔴 A field with a validation error is ALWAYS rendered, whatever the filters say — otherwise
     * a filter could hide the very box that is blocking save and the button would look broken.
     */
    fieldVisible(f) {
      if (this.errorsByKey[f.key] && this.errorsByKey[f.key].length) return true;
      // `hidden` = the key is still saved (and still coerced by `payloadValue`), it just has a better
      // control elsewhere — the four Model ID boxes are written by the "บอทจาก Model Creator" picker.
      // The error check ABOVE still wins, so a legacy row with a broken number is never un-fixable.
      if (f.hidden) return false;
      return this.fieldMatches(f);
    },
    /** Tab → its `group` blocks, in field order, each carrying its own visible-field list. */
    groupsOf(t) {
      const out = [];
      const byName = {};
      t.fields.forEach((f) => {
        const name = f.group || t.label;
        let g = byName[name];
        if (!g) {
          g = byName[name] = { name, index: out.length, fields: [], visible: [] };
          out.push(g);
        }
        g.fields.push(f);
        if (this.fieldVisible(f)) g.visible.push(f);
      });
      return out;
    },
    visibleGroupsOf(t) {
      return this.groupsOf(t).filter((g) => g.visible.length > 0);
    },
    matchCountOf(t) {
      // นับตามสิ่งที่ "จะถูก render จริง" (fieldVisible) ไม่ใช่แค่ที่แมตช์ข้อความ —
      // field ที่ hidden: true จะไม่ขึ้นในหมวดนี้ ป้ายหรี่แท็บจึงต้องไม่นับมัน
      // (ไม่มีคำค้น → fieldMatches คืน true หมด พฤติกรรมเดิมไม่เปลี่ยน)
      return t.fields.filter((f) => this.fieldVisible(f)).length;
    },
    errorCountOf(t) {
      const own = t.fields.filter((f) => this.errorsByKey[f.key] && this.errorsByKey[f.key].length).length;
      // section แบบ hard-code (การ์ด TIME CONTEXT) ไม่มี descriptor ให้วน — บวกเอง ไม่งั้นป้ายแดง
      // ข้างแท็บจะไม่ขึ้นทั้งที่ปุ่มบันทึกโดนบล็อกอยู่
      const extra = Object.keys(EXTRA_FIELD_HOMES).filter(
        (k) => EXTRA_FIELD_HOMES[k].tab === t.value &&
          this.errorsByKey[k] && this.errorsByKey[k].length
      ).length;
      return own + extra;
    },
    clearFilters() {
      this.fieldQuery = '';
    },
    /**
     * Jump to one field from the "ยังบันทึกไม่ได้" summary: switch to its tab, drop any filter that
     * could be hiding it, then scroll it into view and focus it once the tab has rendered.
     */
    jumpToField(key) {
      const entry = this.invalidEntries.find((e) => e.key === key);
      if (entry) this.tab = entry.tab;
      this.clearFilters();
      this.$nextTick(() => {
        // The dialog is teleported to <body>, so query the document rather than this.$el
        // (which is a fragment anchor here — the component has four root dialogs).
        const host = document.querySelector(`[data-testid="tpsv2-field-${key}"]`);
        if (!host) return;
        host.scrollIntoView({ block: 'center', behavior: 'smooth' });
        const input = host.querySelector('input, textarea');
        if (input) input.focus({ preventScroll: true });
      });
    },

    // ══════════════ level 1 ══════════════

    async openList() {
      this.view = 'list';
      this.listMessage = '';
      this.clearListWarnings();
      await Promise.all([
        this.loadDefaults(),
        this.loadVersions(),
        this.loadModelCatalog(),
        this.loadKnowledgeCategoryCatalog(),
      ]);
    },
    backToList() {
      this.view = 'list';
      this.saveError = '';
      this.saveMessage = '';
      this.listMessage = '';
      this.clearListWarnings();
      this.clearFilters();
      this.loadVersions();
    },
    /**
     * Both defaults maps, merged into one `key → default` lookup. Fetched once per open;
     * they are pure code constants server-side (no DB, version-independent).
     *
     * FATAL for saving: the defaults are both what the controls are prefilled with AND the
     * yardstick `payloadValue` uses to decide `null` vs. a real override. Without them the author
     * would face a blank form and type over it — every touched key would be written to the DB as
     * an override it never was. So a failure raises `defaultsError`, which blocks the save button.
     */
    async loadDefaults() {
      try {
        const [promptDefaults, configDefaults] = await Promise.all([
          taxPlanningSettingsV2Api.getPromptDefaults(),
          taxPlanningSettingsV2Api.getConfigDefaults(),
        ]);
        this.defaults = Object.assign({}, promptDefaults || {}, configDefaults || {});
        this.defaultsError = '';
      } catch (e) {
        this.defaults = {};
        this.defaultsError = 'โหลดค่าเริ่มต้นของระบบไม่สำเร็จ — ยังบันทึกไม่ได้ ลองใหม่อีกครั้ง';
      }
    },
    /**
     * "ลองใหม่" on the defaults banner. Refetching the defaults alone would clear the banner but
     * leave every control blank (they were hydrated while `defaults` was empty), so a successful
     * retry re-runs the editor load to prefill them.
     */
    async retryDefaults() {
      await this.loadDefaults();
      if (!this.defaultsError) await this.reloadEditor();
    },
    /**
     * Provider + model catalog — the SAME source the Flow canvas node inspector reads
     * (Flow.vue:17877-17919: `$call.GetAllDDModelManagement(0, …)` → `data.provider` /
     * `data.modelData`), so this dialog can only offer values a node could also offer.
     * `$call` is a global plugin (`main.js:31 app.use(APICall)`), so it is available here;
     * no second axios client is introduced.
     *
     * FAIL-SOFT — this promise NEVER rejects and never clears `values`: on any failure the two
     * lists stay empty, `catalogError` explains it, and every stored value is still rendered
     * (see `catalogItems`) and still saveable.
     */
    loadModelCatalog() {
      return new Promise((resolve) => {
        const call = this.$call;
        if (!call || typeof call.GetAllDDModelManagement !== 'function') {
          this.catalogError = CATALOG_ERROR;
          resolve();
          return;
        }
        this.catalogLoading = true;
        this.catalogError = '';
        const fail = () => {
          this.providerCatalog = [];
          this.modelCatalog = [];
          this.gptModelCatalog = [];
          this.catalogError = CATALOG_ERROR;
          this.catalogLoading = false;
          resolve();
        };
        try {
          // Keep the method on its owner (`$call` methods call `this.apiGetAuth`) — never destructure.
          call.GetAllDDModelManagement(
            0,
            (res) => {
              if (res && res.status === 0 && res.data) {
                this.providerCatalog = Array.isArray(res.data.provider) ? res.data.provider : [];
                this.modelCatalog = Array.isArray(res.data.modelData) ? res.data.modelData : [];
                // Bots come from the SAME response — `modelPublih` is `allModel.Where(IsPublish)`
                // (ManageGPTModelBAL.cs:1456); fall back to the unfiltered list only when the
                // published one is empty, so an install that never flips `IsPublish` still gets a list.
                const published = Array.isArray(res.data.modelPublih) ? res.data.modelPublih : [];
                const everything = Array.isArray(res.data.allModel) ? res.data.allModel : [];
                this.gptModelCatalog = published.length ? published : everything;
                // 🔴 Judged on `modelData` ONLY. `catalogError` is the Provider/Model banner and
                // gates nothing else; letting an empty bot list raise it would paint the whole LLM
                // tab with a warning about lists that actually loaded fine.
                this.catalogError = this.modelCatalog.length ? '' : CATALOG_ERROR;
                this.catalogLoading = false;
                resolve();
                return;
              }
              fail();
            },
            fail
          );
        } catch (e) {
          fail();
        }
      });
    },
    /**
     * แคตตาล็อกหมวดย่อยเอกสาร (Knowledge) สำหรับช่อง kind 'knowledgeCategories'.
     * 🔴 derive แบบเดียวกับ `views/TaxQuestionV2Edit.vue` → `loadKnowledgeCatalog()` เป๊ะ ๆ
     *    (`DocumentCategory` + `Title` ของ metadoc ที่มีอยู่จริง → token `"Category:Title"`) เพื่อให้
     *    token ที่บันทึกจากสองหน้านี้เป็นชุดเดียวกัน — ตัว resolver ฝั่ง backend มีตัวเดียว
     *    ถ้าหน้านี้ derive คนละแบบ ค่าที่เลือกจะ resolve เป็นศูนย์เอกสารโดยไม่มีอะไรฟ้อง
     * FAIL-SOFT: ล้มเหลว = ลิสต์ว่าง ไม่มี error banner ไม่บล็อกอะไร และไม่แตะ `values` เลย
     */
    async loadKnowledgeCategoryCatalog() {
      this.knowledgeCatalogLoading = true;
      this.knowledgeCatalogTried = true;
      try {
        const docs = await metaDocApi.getAvailableDocuments();
        const subcats = new Map();
        (docs || []).forEach((d) => {
          const cat = String((d && d.documentCategory) || '').trim();
          const sub = String((d && d.title) || '').trim();
          if (!cat || !sub) return;
          const value = `${cat}:${sub}`;
          if (!subcats.has(value)) subcats.set(value, { value, title: `${cat} — ${sub}` });
        });
        this.knowledgeCategoryCatalog = [...subcats.values()].sort((a, b) => a.title.localeCompare(b.title));
      } catch (e) {
        this.knowledgeCategoryCatalog = [];
      } finally {
        this.knowledgeCatalogLoading = false;
      }
    },
    /**
     * ตัวเลือกของช่อง Knowledge — แคตตาล็อก บวก token ที่บันทึกไว้แล้วแต่ไม่อยู่ในแคตตาล็อก ซึ่งถูก
     * PREPEND เป็นตัวเลือกของตัวเอง ด้วยเหตุผลเดียวกับ `gptModelItemsWithCurrent`: เอกสารถูกลบหรือ
     * แอดมินคนละคนเห็นเอกสารคนละชุด — ปล่อยให้หายจากจอ = save ครั้งถัดไปทับค่าเดิมทิ้งเงียบ ๆ
     */
    knowledgeCategoryItems(f) {
      const rows = this.knowledgeCategoryCatalog || [];
      const current = parseCategoryTokens(this.values[f.key]);
      const missing = current
        .filter((t) => !rows.some((r) => r.value === t))
        .map((t) => ({ value: t, title: `${t} — ${ITEM_SUFFIX_UNKNOWN}` }));
      return missing.length ? missing.concat(rows) : rows;
    },
    /** Model catalog rows → picker items. `provider` blank = no filtering. */
    mapModelRows(provider) {
      const seen = {};
      const out = [];
      (this.modelCatalog || []).forEach((m) => {
        if (!m || !m.modelName) return;
        const rowProvider = String(m.provider || m.providerName || '').trim();
        if (provider && rowProvider !== provider) return;
        const value = String(m.modelName);
        if (seen[value]) return;
        seen[value] = true;
        out.push({
          title: m.modelDisplay ? String(m.modelDisplay) : modelKeyHead(value),
          subtitle: value,
          value,
        });
      });
      return out;
    },
    /**
     * Items for a Provider/Model picker. A value that is not in the (provider-filtered) list is
     * PREPENDED as its own option so it can never vanish from the screen just because the list
     * disagrees with it — labelled by how well it actually resolves (`catalogMatch`).
     */
    catalogItems(f) {
      const rows = f.kind === 'provider' ? this.providerItems : this.modelItemsFor(f);
      const current = this.displayValue(f);
      if (!current || rows.some((i) => i.value === current)) return rows;
      const suffix = {
        exact: ITEM_SUFFIX_OTHER_PROVIDER,
        soft: f.kind === 'provider' ? ITEM_SUFFIX_SOFT_PROVIDER : ITEM_SUFFIX_SOFT,
        none: ITEM_SUFFIX_UNKNOWN,
      }[this.catalogMatch(f)];
      return [{ title: suffix ? `${current} — ${suffix}` : current, value: current }].concat(rows);
    },
    /**
     * Items for one "บอทจาก Model Creator" picker. Same rule as `catalogItems`: a stored value that
     * is not in the list is PREPENDED as its own option.
     * 🔴 Not cosmetic — `DDGPTModel_ByUser` (ManageGPTModelBAL.cs:1267-1292) returns a DIFFERENT set
     * per role, so a bot another admin picked may simply not be in this user's list. Without the
     * prepend the control would show blank, and the next Save would silently wipe both columns.
     */
    gptModelItemsWithCurrent(f) {
      const rows = this.gptModelItems;
      const raw = this.values[f.displayKey];
      const current = String(raw === null || raw === undefined ? '' : raw).trim();
      if (!current || rows.some((i) => i.value === current)) return rows;
      const idRaw = this.values[f.idKey];
      return [{
        title: `${current} — (ไม่พบในรายการ)`,
        value: current,
        modelId: idRaw === null || idRaw === undefined || idRaw === '' ? null : idRaw,
      }].concat(rows);
    },
    /**
     * The whole point of the picker: `modelID` and `modelDisplayID` are written TOGETHER, from one
     * catalog row, so they can never describe two different bots.
     * Clearing (the ✕) blanks BOTH — a blank box is "at default" and goes back as `null`, which is
     * how the columns return to NULL.
     * Both are stored as STRINGS, like every other value in `values` (`payloadValue` coerces the
     * `int` kind back to a real number on save). `modelId` is left alone when the picked value is
     * only the prepended legacy option: there is no catalog row to read a fresh id from, and
     * dropping it would be a silent data loss.
     */
    onPickGptModel(f, v) {
      const picked = v === null || v === undefined ? '' : String(v).trim();
      if (!picked) {
        this.values[f.idKey] = '';
        this.values[f.displayKey] = '';
        return;
      }
      const row = this.gptModelItems.find((i) => i.value === picked);
      this.values[f.displayKey] = picked;
      if (row && row.modelId !== null && row.modelId !== undefined) {
        this.values[f.idKey] = String(row.modelId);
      }
    },
    /**
     * How the value in force resolves against the live catalog — THREE states, mirroring the
     * backend's two-step lookup in `TaxPlanningV2NodeProcessor.BuildGptModelFromConfigAsync`:
     *   'exact' — same key (trimmed). Step 1 of the backend lookup. Nothing to say.
     *   'soft'  — not the same key, but the backend's step 2 still finds it: for a model, the
     *             head before `|` matches on both sides case-insensitively (`"gpt-5.4-mini"` →
     *             `"gpt-5.4-mini|gpt-5.4-mini"`, and the built-in default `"gpt-4"` →
     *             `"gpt-4|gpt-4"`); for a provider, it differs only by case/whitespace.
     *             This RESOLVES — it is not an error and must never be reported as a 400.
     *   'none'  — neither step finds anything: no provider template is applied, 400 is a real risk.
     * '' = nothing to judge (not a catalog field / still loading / empty catalog / empty value).
     */
    catalogMatch(f) {
      if (!f || (f.kind !== 'provider' && f.kind !== 'model')) return '';
      if (this.catalogLoading) return '';
      const rows = f.kind === 'provider' ? this.providerItems : this.allModelItems;
      if (!rows.length) return '';
      const current = String(this.displayValue(f) || '').trim();
      if (!current) return '';
      if (rows.some((i) => String(i.value).trim() === current)) return 'exact';
      const head = modelKeyHead(current).toLowerCase();
      const softHit = rows.some((i) => {
        const value = String(i.value);
        return f.kind === 'provider'
          ? value.trim().toLowerCase() === current.toLowerCase()
          : modelKeyHead(value).toLowerCase() === head;
      });
      return softHit ? 'soft' : 'none';
    },
    /** Show the full "ชื่อ|id" key under the display name, so the stored string is never a mystery. */
    catalogItemProps(item) {
      return item && item.subtitle ? { subtitle: item.subtitle } : {};
    },
    /**
     * What to say about the value in force — '' when it is an exact catalog key (or there is
     * nothing to judge). Reported for the value ACTUALLY in force, so a box still sitting on the
     * prefilled built-in default is judged too, not only a value the author picked.
     *
     * 🔴 The 'soft' state is NOT an error. The backend resolves a bare name by matching the head
     * before `|`, so `"gpt-5.4-mini"` and the built-in default `"gpt-4"` both work — claiming a
     * 400 there would be false. Only 'none' earns the hard warning.
     * Silent while the catalog is unavailable — an empty list is no evidence of a bad value.
     */
    catalogWarning(f) {
      const match = this.catalogMatch(f);
      if (!match || match === 'exact') return '';
      const current = String(this.displayValue(f) || '').trim();
      const isDefault = this.isAtDefault(f.key);
      // ── resolves, just not written as the full catalog key ──
      if (match === 'soft') {
        if (f.kind === 'provider') {
          return isDefault
            ? `ยังไม่ได้เลือกเอง — ระบบใช้ค่าในโค้ด "${current}" ซึ่งต่างจากชื่อในรายการแค่ตัวพิมพ์/ช่องว่าง · ` +
              'ระบบเทียบให้เอง ใช้งานได้ปกติ'
            : `ค่าที่บันทึกไว้ "${current}" ต่างจากชื่อในรายการแค่ตัวพิมพ์/ช่องว่าง — ระบบเทียบให้เอง ใช้งานได้ปกติ · ` +
              'กดปุ่มด้านขวาเพื่อใช้ชื่อตามรายการจะชัดเจนกว่า';
        }
        return isDefault
          ? `ยังไม่ได้เลือกเอง — ระบบใช้ค่าในโค้ด "${current}" ซึ่งเป็นชื่อโมเดลแบบไม่เต็มคีย์ · ` +
            'ระบบเทียบให้เองด้วยชื่อหน้า "|" ใช้งานได้ปกติ · ถ้าอยากระบุรุ่นให้ชัดเจน ให้เลือกจากรายการ'
          : `ค่าที่บันทึกไว้ "${current}" เป็นชื่อโมเดลแบบไม่เต็มคีย์ — ระบบเทียบให้เองด้วยชื่อหน้า "|" ` +
            'ใช้งานได้ปกติ · กดปุ่มด้านขวาเพื่อเก็บเป็นคีย์เต็ม "ชื่อ|id" จะชัดเจนกว่า';
      }
      // ── resolves to nothing at all ──
      if (f.kind === 'provider') {
        return isDefault
          ? `ยังไม่ได้เลือกเอง — ระบบใช้ค่าในโค้ด "${current}" ซึ่งไม่ตรงกับผู้ให้บริการในรายการเลย · ` +
            'ถ้าเรียก LLM ไม่สำเร็จ ให้เลือกจากรายการ'
          : `ค่าที่บันทึกไว้ "${current}" ไม่ตรงกับผู้ให้บริการในรายการเลย — เลือกใหม่จากรายการ`;
      }
      return isDefault
        ? `ยังไม่ได้เลือกเอง — ระบบใช้ค่าในโค้ด "${current}" ซึ่งไม่ตรงกับรายการโมเดลเลย (ทั้งคีย์เต็มและชื่อหน้า "|") · ` +
          'ระบบจะหา template ของ provider ไม่เจอ และเรียก LLM ไม่สำเร็จ (400) · เลือกจากรายการ'
        : `ค่าที่บันทึกไว้ "${current}" ไม่ตรงกับรายการโมเดลเลย (ทั้งคีย์เต็มและชื่อหน้า "|") — ` +
          'ระบบจะหา template ของ provider ไม่เจอ และเรียก LLM ไม่สำเร็จ (400) · เลือกใหม่จากรายการ';
    },
    /** 'none' = hard warning; 'soft' = it works, we are only asking for a clearer value. */
    catalogAlertType(f) {
      return this.catalogMatch(f) === 'none' ? 'warning' : 'info';
    },
    /**
     * The catalog entry a non-exact value most likely MEANT — a bare name matched against the
     * catalog key's two halves ("gpt-5.4-mini" → "gpt-5.4-mini|gpt-5.4-mini"), or a provider typed
     * in the wrong case. Offered for BOTH non-exact states (a 'soft' value works, but storing the
     * full key is still clearer), and only for a value the author really set: a box still sitting
     * on the built-in default has no override to upgrade, and accepting one there would turn a
     * `null` column into a stored value nobody asked for.
     * The provider-filtered list is preferred; the full catalog is the fallback so a model that
     * lives under another provider can still be offered.
     */
    catalogSuggestion(f) {
      const match = this.catalogMatch(f);
      if (!match || match === 'exact') return null;
      if (this.isAtDefault(f.key)) return null;
      const raw = this.values[f.key];
      const current = String(raw === null || raw === undefined ? '' : raw).trim().toLowerCase();
      if (!current) return null;
      const matches = (i) => {
        const value = String(i.value);
        if (f.kind === 'provider') return value.trim().toLowerCase() === current;
        const parts = value.split('|');
        return String(parts[0]).trim().toLowerCase() === current ||
          String(parts[1] || '').trim().toLowerCase() === current;
      };
      const rows = f.kind === 'provider' ? this.providerItems : this.modelItemsFor(f);
      const hit = rows.find(matches) ||
        (f.kind === 'model' ? this.allModelItems.find(matches) : null);
      return hit || null;
    },
    applyCatalogSuggestion(f) {
      const hit = this.catalogSuggestion(f);
      if (!hit) return;
      this.values[f.key] = hit.value;
      if (f.kind === 'provider') this.syncModelToProvider(MODEL_KEY_BY_PROVIDER_KEY[f.key]);
    },
    /**
     * Provider changed BY THE AUTHOR → drop a model that does not belong to the new provider.
     * Flow.vue clears the model on EVERY `changeProvider()` and therefore needs the
     * `loadingModelConfig` guard (Flow.vue:20748) — it also calls `changeProvider()` while
     * restoring a saved node, and without the guard that wipes the value it just loaded.
     * Nothing to guard here: a loaded value is assigned straight into `values` by `applyDto` and
     * never travels through the control, and the item list is a computed (no `DDModelShow` to
     * refresh). We also clear only when the model really is foreign to the new provider
     * (TaxConfiguration.vue:1954-1960). Blanking is safe: a blank box means "กลับไปใช้ค่าเริ่มต้น"
     * and is sent as `null`, so it can never write an empty override into the column.
     */
    syncModelToProvider(modelKey) {
      if (!modelKey) return;                 // a provider field with no model partner — nothing to sync
      if (!this.modelCatalog.length) return; // catalog unavailable → never discard the author's value
      const current = String(this.values[modelKey] || '').trim();
      if (!current) return;
      // Head-aware, like the backend: a bare name that still resolves under the NEW provider is
      // not "foreign" and must not be thrown away.
      const head = modelKeyHead(current).toLowerCase();
      const belongs = this.modelItemsFor(FIELD_BY_KEY[modelKey]).some(
        (i) => String(i.value).trim() === current || modelKeyHead(i.value).toLowerCase() === head
      );
      if (belongs) return;
      // Blank, not silently re-picked: the author chooses a model that the new provider serves.
      // Left blank, the key is saved as `null` = กลับไปใช้ค่าเริ่มต้น (never as an empty override).
      this.values[modelKey] = '';
    },
    /**
     * The model list a given model picker should show — filtered by the provider field THAT picker
     * belongs to (`f.providerKey`, defaulting to the main `provider`). Without this the ASK-side model
     * picker would list the main provider's models, which is a list its own provider cannot serve.
     */
    modelItemsFor(f) {
      const providerKey = (f && f.providerKey) || 'provider';
      if (providerKey === 'provider') return this.modelItems;   // the memoised computed, unchanged
      const raw = this.values[providerKey];
      return this.mapModelRows(raw === null || raw === undefined ? '' : String(raw).trim());
    },
    async loadVersions() {
      this.listLoading = true;
      this.listError = '';
      try {
        const rows = await taxPlanningSettingsV2Api.listVersions();
        this.versions = Array.isArray(rows) ? rows : [];
      } catch (e) {
        this.listError = (e && e.message) || 'โหลดรายการเวอร์ชันไม่สำเร็จ';
      } finally {
        this.listLoading = false;
      }
    },
    async activate(item) {
      if (!item || item.isActive || this.busyId !== null) return;
      this.busyId = item.id;
      this.listError = '';
      this.listMessage = '';
      try {
        await taxPlanningSettingsV2Api.activateVersion(item.id); // no body — the id IS the request
        this.listMessage = `ตั้งเวอร์ชัน ${item.versionCode || item.id} เป็นเวอร์ชันที่ใช้งานแล้ว`;
        this.$emit('saved', this.listMessage);
        await this.loadVersions();
      } catch (e) {
        // 400 + Thai message (never 404) — show it verbatim.
        this.listError = (e && e.message) || 'ตั้งเวอร์ชันที่ใช้งานไม่สำเร็จ';
        this.$emit('error', this.listError);
      } finally {
        this.busyId = null;
      }
    },

    // ── create ──
    openCreate() {
      const active = this.versions.find((v) => v.isActive);
      this.createForm = {
        versionCode: '',
        description: '',
        cloneFromId: active ? active.id : null,
      };
      this.createError = '';
      this.createDialog = true;
    },
    async submitCreate() {
      this.createSaving = true;
      this.createError = '';
      try {
        const dto = await taxPlanningSettingsV2Api.createVersion({
          versionCode: String(this.createForm.versionCode || '').trim() || null,
          description: String(this.createForm.description || '').trim() || null,
          cloneFromId: this.createForm.cloneFromId || null,
        });
        this.createDialog = false;
        this.listMessage = `สร้างเวอร์ชัน ${dto && dto.versionCode ? dto.versionCode : ''} แล้ว (ยังไม่ได้ใช้งาน)`;
        this.$emit('saved', this.listMessage);
        await this.loadVersions();
      } catch (e) {
        this.createError = (e && e.message) || 'สร้างเวอร์ชันไม่สำเร็จ';
      } finally {
        this.createSaving = false;
      }
    },

    // ── clone ──
    openClone(item) {
      this.cloneForm = {
        sourceId: item.id,
        sourceCode: item.versionCode || String(item.id),
        versionCode: '',
        description: item.description ? `สำเนาจาก ${item.versionCode || item.id}` : '',
      };
      this.cloneError = '';
      this.cloneDialog = true;
    },
    async submitClone() {
      this.cloneSaving = true;
      this.cloneError = '';
      try {
        const dto = await taxPlanningSettingsV2Api.cloneVersion(this.cloneForm.sourceId, {
          versionCode: String(this.cloneForm.versionCode || '').trim() || null,
          description: String(this.cloneForm.description || '').trim() || null,
        });
        this.cloneDialog = false;
        this.listMessage = `ทำสำเนาเป็นเวอร์ชัน ${dto && dto.versionCode ? dto.versionCode : ''} แล้ว (ยังไม่ได้ใช้งาน)`;
        this.$emit('saved', this.listMessage);
        await this.loadVersions();
      } catch (e) {
        this.cloneError = (e && e.message) || 'ทำสำเนาเวอร์ชันไม่สำเร็จ';
      } finally {
        this.cloneSaving = false;
      }
    },

    // ── delete ──
    openDelete(item) {
      this.deleteTarget = item;
      this.deleteError = '';
      this.deleteDialog = true;
    },
    async confirmDelete() {
      if (!this.deleteTarget) return;
      this.deleting = true;
      this.deleteError = '';
      try {
        await taxPlanningSettingsV2Api.deleteVersion(this.deleteTarget.id);
        this.deleteDialog = false;
        this.listMessage = `ลบเวอร์ชัน ${this.deleteTarget.versionCode || this.deleteTarget.id} แล้ว`;
        this.$emit('saved', this.listMessage);
        this.deleteTarget = null;
        await this.loadVersions();
      } catch (e) {
        // The active version and the last remaining one are rejected with 400 + a Thai reason —
        // keep the confirm open and show that reason instead of a generic "ไม่พบข้อมูล".
        this.deleteError = (e && e.message) || 'ลบเวอร์ชันไม่สำเร็จ';
      } finally {
        this.deleting = false;
      }
    },

    // ══════════════ save history · export · import ══════════════

    clearListWarnings() {
      this.listWarnings = [];
      this.listWarningTitle = '';
    },
    /**
     * Show something that SUCCEEDED but carries lines the admin must read. The version list has a
     * warning band of its own; the editor has only the error strip, so there the lines are folded
     * into one sentence. Either way the host page's snackbar is fed through `error`, never `saved`.
     */
    reportWarnings(title, lines) {
      const list = (Array.isArray(lines) ? lines : []).filter((w) => String(w || '').trim());
      if (!list.length) return false;
      if (this.view === 'editor') {
        this.saveMessage = '';
        this.saveError = `${title} — ${list.join(' · ')}`;
      } else {
        this.listMessage = '';
        this.listWarningTitle = title;
        this.listWarnings = list;
      }
      this.$emit('error', `${title} (${list.length} รายการ)`);
      return true;
    },
    /** Open the history of a row in the version list. */
    openHistory(item) {
      if (!item || item.id == null) return;
      this.historyVersionId = item.id;
      this.historyVersionLabel = item.versionCode || String(item.id);
      this.historyVersionActive = Boolean(item.isActive);
      this.historyDialog = true;
    },
    /**
     * Same dialog from inside the editor. Unsaved edits are NOT in the history yet and a restore
     * would overwrite them, so that case asks first instead of opening straight away.
     */
    openHistoryFromEditor() {
      if (!this.editingId) return;
      if (this.isDirty) {
        this.historyDirtyDialog = true;
        return;
      }
      this.openHistoryForEditingVersion();
    },
    confirmOpenHistoryDirty() {
      this.historyDirtyDialog = false;
      this.openHistoryForEditingVersion();
    },
    openHistoryForEditingVersion() {
      this.historyVersionId = this.editingId;
      this.historyVersionLabel = this.editingCode || String(this.editingId);
      this.historyVersionActive = Boolean(this.editingIsActive);
      this.historyDialog = true;
    },
    /**
     * A restore rewrote the config columns of `historyVersionId` in place (the id, the code and
     * the active flag are untouched). If that version is the one open in the editor, the form on
     * screen is now describing a row that no longer exists — reload it rather than leave a screen
     * that lies about the database.
     */
    async onHistoryRestored(result) {
      const fromNo = result && result.restoredFromNo;
      const label = this.historyVersionLabel || '';
      const editingThisOne = this.view === 'editor'
        && this.editingId != null
        && Number(this.historyVersionId) === Number(this.editingId);
      if (editingThisOne) await this.reloadEditor();
      else await this.loadVersions();

      const title = `กู้คืนเวอร์ชัน ${label} จากรอบ v${fromNo == null ? '?' : fromNo} แล้ว`;
      const warnings = (result && Array.isArray(result.warnings)) ? result.warnings : [];
      if (this.reportWarnings(title, warnings)) return;
      // One snackbar, one inline line — never both channels for the same event.
      if (editingThisOne) this.saveMessage = title;
      else this.listMessage = title;
      this.$emit('saved', title);
    },
    /**
     * Download ONE settings version as the portable `.json` bundle.
     *
     * The endpoint answers with a normal `ResponseModel`, not a file, so the download is made here
     * from a Blob (same as `views/TaxConfigurationV2.vue` → `onExportQuestionVersion`).
     * 🔴 The object URL is revoked on the NEXT TASK, not immediately: Safari cancels an in-flight
     * download when its blob URL disappears in the same task.
     */
    async onExportVersion(item) {
      if (!item || item.id == null || this.exportingId !== null) return;
      this.exportingId = item.id;
      this.listError = '';
      this.listMessage = '';
      this.clearListWarnings();
      try {
        const bundle = await taxPlanningSettingsV2Api.exportVersion(item.id);
        const code = (bundle && bundle.source && bundle.source.versionCode)
          || item.versionCode
          || `v${item.id}`;
        const fileName = `taxsettings-${this.safeFilePart(code)}.json`;
        const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 0);
        this.listMessage = `ส่งออกเวอร์ชัน ${code} แล้ว — ${fileName}`;
      } catch (e) {
        this.listError = (e && e.message) || 'ส่งออกการตั้งค่าไม่สำเร็จ';
        this.$emit('error', this.listError);
      } finally {
        this.exportingId = null;
      }
    },
    /** Keep a code that came off the wire from turning into a path or an unsaveable name. */
    safeFilePart(value) {
      const cleaned = String(value ?? '').trim().replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '');
      return cleaned || 'export';
    },
    openImport() {
      this.listError = '';
      this.listMessage = '';
      this.clearListWarnings();
      this.importDialog = true;
    },
    /**
     * The import dialog never closes itself — closing, reloading the list and surfacing the
     * warnings all belong here.
     *
     * 🔴 `unresolvedModels` is not decoration: a slot that matched nothing on this machine leaves
     * that column NULL, which means "use the built-in default in the code" — a real behaviour
     * change someone has to go and fix. It is only spelled out here when the backend did not
     * already say it in `warnings`, so the same fact is never listed twice.
     */
    async onImported(result) {
      this.importDialog = false;
      await this.loadVersions();
      const code = (result && result.versionCode) || '';
      const title = `นำเข้าเป็นเวอร์ชัน ${code} แล้ว (ยังไม่ได้ใช้งาน)`;
      const warnings = (result && Array.isArray(result.warnings)) ? result.warnings.slice() : [];
      const unresolved = (result && Array.isArray(result.unresolvedModels)) ? result.unresolvedModels : [];
      if (unresolved.length && !warnings.length) {
        unresolved.forEach((m) => {
          const ref = [m && m.botName, m && m.provider, m && m.modelName]
            .filter((x) => String(x || '').trim())
            .join(' · ');
          warnings.push(
            `หาโมเดลของช่อง ${m && m.slot ? m.slot : '(ไม่ทราบ)'} ไม่เจอบนเครื่องนี้`
            + `${ref ? ` (${ref})` : ''} — ช่องนั้นถูกเว้นว่าง = ใช้ค่าเริ่มต้นในโค้ด`
          );
        });
      }
      if (this.reportWarnings(title, warnings)) return;
      this.listMessage = title;
      this.$emit('saved', title);
    },

    // ══════════════ level 2 ══════════════

    async openEditor(item) {
      this.view = 'editor';
      this.tab = 'general';
      this.clearFilters();
      // Blank the form BEFORE the fetch: otherwise the override counters and the rail badges keep
      // reporting the version the author just left, over this version's skeleton.
      this.values = blankMap(null);
      this.identity = { versionCode: item.versionCode || '', description: item.description || '' };
      this.audit = { updatedBy: '', updatedDate: '' };
      this.editingId = item.id;
      this.editingCode = item.versionCode || '';
      this.editingIsActive = Boolean(item.isActive);
      await this.reloadEditor();
    },
    async reloadEditor() {
      if (!this.editingId) return;
      this.loading = true;
      this.loadError = '';
      this.saveError = '';
      this.saveMessage = '';
      // Drop the old baseline first: if the fetch fails, `isDirty` must not report the PREVIOUS
      // version's edits against a form that never loaded.
      this.baseline = '';
      try {
        if (!Object.keys(this.defaults).length) await this.loadDefaults();
        // Safety net for an editor reached without a fresh `openList()` — never re-fetches on
        // every reload, and a previous failure is left to the banner's "ลองใหม่" button.
        // ทั้งสองลิสต์มาจาก response เดียวกัน — `modelCatalog` ที่มีของแปลว่า `gptModelCatalog`
        // โหลดสำเร็จแล้ว (ว่าง = ไม่มีบอทจริง) จึงไม่ต้องยิงซ้ำ ไม่งั้นระบบที่ยังไม่มีบอทเลย
        // จะ refetch ทั้งใบทุกครั้งที่กด "แก้ไข" เวอร์ชัน โดยเปล่าประโยชน์.
        if (!this.modelCatalog.length && !this.catalogError) {
          await this.loadModelCatalog();
        }
        // เหตุผลเดียวกัน แต่ต้องมีธงของตัวเอง: ลิสต์ว่างเป็นสถานะที่ถูกต้องได้ (ระบบที่ยังไม่มีเอกสารเลย)
        // ถ้าเช็คด้วย `.length` อย่างเดียว ระบบแบบนั้นจะยิง MetaDoc ใหม่ทุกครั้งที่กด "แก้ไข" โดยเปล่าประโยชน์
        if (!this.knowledgeCatalogTried) await this.loadKnowledgeCategoryCatalog();
        const dto = await taxPlanningSettingsV2Api.getVersion(this.editingId);
        this.applyDto(dto);
      } catch (e) {
        this.loadError = (e && e.message) || 'โหลดการตั้งค่าไม่สำเร็จ';
      } finally {
        this.loading = false;
      }
    },
    /**
     * Re-derive the whole editor from a settings DTO. A `null` column carries no override, so the
     * control is HYDRATED with the built-in default instead of being left blank — the author sees
     * (and edits on top of) the value the runtime really uses. `payloadValue` sends it back as
     * `null` for as long as it is left untouched, so hydrating never writes a default into the DB.
     */
    applyDto(dto) {
      const values = blankMap(null);
      TAX_PLANNING_SETTINGS_V2_ALL_KEYS.forEach((k) => {
        const raw = dto ? dto[k] : null;
        const src = raw === null || raw === undefined ? this.defaultOf(k) : raw;
        const f = FIELD_BY_KEY[k];
        // Knowledge: คอลัมน์เป็น nvarchar(max) จึงมาเป็น "string ของ JSON" ตามปกติ — แต่ถ้า DTO
        // (หรือค่า default ในโค้ด) ส่ง array มาตรง ๆ ต้องแปลงเป็น string ก่อน เพราะทั้งแผงถือ string
        // อย่างเดียว (`snapshot`/`isDirty` เทียบด้วย JSON.stringify ของ `values`)
        // 🔴 string ที่ parse ไม่ผ่านถูกเก็บดิบไว้ ไม่แปลงและไม่ทิ้ง — `fieldErrors` เป็นคนขึ้นแดง
        if (f && f.kind === 'knowledgeCategories') {
          values[k] = Array.isArray(src)
            ? stringifyCategoryTokens(src)
            : (src === null || src === undefined ? '' : String(src));
          return;
        }
        values[k] = f && f.kind === 'bool'
          ? src === true
          : (src === null || src === undefined ? '' : String(src));
      });
      this.values = values;
      // สวิตช์ Time Context เป็น derived state ล้วน ๆ — มีโซนเวลา = เปิด (เหมือน node AI Model:
      // ModelManagement.vue:3197 `enableTime = item.timeZone ? true : false`) ต้องตั้งหลัง `this.values`
      // และก่อน `this.baseline = this.snapshot` ด้านล่าง (สวิตช์ไม่ได้อยู่ใน snapshot — ไม่มีคอลัมน์ของตัวเอง)
      this.timeContextEnabled = Boolean(String(values.timeZone || '').trim());
      this.timeContextLastZone = '';
      this.identity = {
        versionCode: (dto && dto.versionCode) || '',
        description: (dto && dto.description) || '',
      };
      this.editingCode = this.identity.versionCode;
      this.editingIsActive = Boolean(dto && dto.isActive);
      // Keys mirror TaxPlanningSettingsV2Dto's …V2-family audit spelling (updatedBy/updatedDate,
      // createdBy/createdDate) — camelCased by the API serializer. Renaming these silently blanks the line.
      this.audit = {
        updatedBy: (dto && (dto.updatedBy || dto.createdBy)) || '',
        updatedDate: (dto && (dto.updatedDate || dto.createdDate)) || '',
      };
      // Freeze the "as loaded" state AFTER every assignment above, so `isDirty` starts false.
      this.baseline = this.snapshot;
    },

    /** The built-in default for a key (may legitimately be `null` = "ไม่กำหนด"). */
    defaultOf(key) {
      const v = this.defaults[key];
      return v === undefined ? null : v;
    },
    /**
     * The hint under a control, purely explanatory. Every control is prefilled with the value
     * actually in force, so spelling the default out here as well would only repeat what the box
     * already shows — a blank box says "ค่าเริ่มต้นคือว่าง" by itself.
     */
    fieldHint(f) {
      return f.hint;
    },
    /** Value bound to the control — `values` is already hydrated (default or stored override). */
    displayValue(f) {
      // A `gptModelRef` has no column of its own: what it shows is the display id it manages.
      if (f.kind === 'gptModelRef') {
        const ref = this.values[f.displayKey];
        return ref === null || ref === undefined ? '' : String(ref);
      }
      // Knowledge: คอลัมน์เก็บเป็น string ของ JSON แต่ v-autocomplete multiple ต้องการ array
      if (f.kind === 'knowledgeCategories') return parseCategoryTokens(this.values[f.key]);
      const raw = this.values[f.key];
      if (f.kind === 'bool') return raw === true;
      if (raw === null || raw === undefined) return '';
      return String(raw);
    },
    /**
     * สวิตช์ Time Context — เขียนลงคอลัมน์ `timeZone` เท่านั้น ไม่มี state ของตัวเองไปถึง backend
     * · ปิด ⇒ ล้างค่าเป็น '' (ค่าว่าง = at default ⇒ `payloadValue` ส่ง `null`) และ **นับเป็นการแก้ไข**
     *   เพราะ `values` เปลี่ยน ⇒ `snapshot` ต่างจาก `baseline` ⇒ ธง "ยังไม่ได้บันทึก" ขึ้นตามกลไกเดิม
     * · เปิด ⇒ คงค่าที่มีอยู่ ถ้าไม่มีก็คืนค่าที่เพิ่งล้างไป (ถ้ามี) ไม่งั้นปล่อยว่างรอผู้ใช้เลือก
     *   (ว่างค้างไว้ = `timeContextErrors` ขึ้น error และบล็อกปุ่มบันทึก)
     */
    onToggleTimeContext(on) {
      this.timeContextEnabled = on === true;
      const raw = this.values.timeZone;
      const current = raw === null || raw === undefined ? '' : String(raw).trim();
      if (!this.timeContextEnabled) {
        // จำไว้ก่อนล้าง — สลับสวิตช์ปิดแล้วเปิดกลับต้องไม่ทำให้ค่าที่ admin เลือกไว้หายไปเฉย ๆ
        if (current) this.timeContextLastZone = current;
        this.values.timeZone = '';
        return;
      }
      if (!current && this.timeContextLastZone) this.values.timeZone = this.timeContextLastZone;
    },
    /** เลือกโซนเวลาจาก dropdown — เก็บเป็น string เสมอ เหมือนทุกค่าใน `values` */
    onPickTimeZone(value) {
      this.values.timeZone = value === null || value === undefined ? '' : String(value);
    },
    /**
     * ป้ายเมืองที่โชว์ต่อท้ายชื่อประเทศในช่องที่เลือกแล้ว — คืน '' เมื่ออ่านไม่ได้
     * 🔴 ต้นแบบใน Flow.vue เขียน `item.raw.value.split('/')[1].replace(...)` ตรง ๆ ซึ่ง **throw**
     *    ถ้าค่าไม่มี `/` — ค่าเดิมจากยุคช่อง free-text เป็นอะไรก็ได้ จึงต้องเช็คก่อนเสมอ
     */
    timeZoneRegion(value) {
      const raw = value === null || value === undefined ? '' : String(value);
      const cut = raw.indexOf('/');
      if (cut < 0) return '';
      return raw.slice(cut + 1).trim().replace(/_/g, ' ');
    },
    onEdit(f, value) {
      if (f.kind === 'bool') {
        this.values[f.key] = value === true;
        return;
      }
      // Knowledge: array จาก picker → string ของ JSON · ไม่เลือกอะไรเลย = '' (ไม่ใช่ "[]")
      // ว่าง = "at default" ⇒ `payloadValue` ส่ง `null` ⇒ คอลัมน์กลับไปเป็น NULL = "ไม่ใช้ knowledge"
      if (f.kind === 'knowledgeCategories') {
        this.values[f.key] = stringifyCategoryTokens(value);
        return;
      }
      this.values[f.key] = value === null || value === undefined ? '' : String(value);
      // Author-driven provider change only (loading never routes through the control) —
      // see syncModelToProvider for why no `loadingModelConfig`-style guard is needed.
      // Each provider field owns exactly one model field: the main pair and the ASK-side pair are
      // independent, so changing one must never blank the other's model.
      if (f.kind === 'provider') this.syncModelToProvider(MODEL_KEY_BY_PROVIDER_KEY[f.key]);
    },
    /**
     * Is this key's current value still the built-in default? THE decider for what save sends:
     * `true` ⇒ the column stays `null` (no override), so a hydrated-but-untouched form saves
     * exactly as it loaded and the Playground can still tell `config:` from `nodeDefault`.
     * A blank box counts as "at default" — clearing a field IS how the author reverts it.
     * Numbers compare numerically ("0.0" is not an override of 0) and strings compare trimmed,
     * matching the backend's `Normalize()` / `Effective()`.
     */
    isAtDefault(key) {
      const f = FIELD_BY_KEY[key];
      const v = this.values[key];
      const d = this.defaultOf(key);
      if (f && f.kind === 'bool') return (v === true) === (d === true);
      // Knowledge: เทียบ "ชุด token" ไม่ใช่ตัวอักษร — `["A","B"]` กับ `[ "A", "B" ]` คือค่าเดียวกัน
      // และลิสต์ว่าง (`''` หรือ `"[]"`) = at default ⇒ ส่ง `null` เสมอ ตามกติกา "ว่าง = ไม่ใช้ knowledge"
      if (f && f.kind === 'knowledgeCategories') {
        return stringifyCategoryTokens(parseCategoryTokens(v)) ===
          stringifyCategoryTokens(parseCategoryTokens(d));
      }
      const text = v === null || v === undefined ? '' : String(v).trim();
      const dText = d === null || d === undefined ? '' : String(d).trim();
      if (!text) return true;                       // ว่าง = กลับไปใช้ค่าเริ่มต้น
      if (f && (f.kind === 'int' || f.kind === 'float')) {
        return dText !== '' && Number(text) === Number(dText);
      }
      return text === dText;
    },
    fieldErrors(f) {
      if (!f) return [];
      // A bool control can only ever hand back true/false — there is nothing to reject.
      if (f.kind === 'bool') return [];
      const v = this.values[f.key];
      const text = v === null || v === undefined ? '' : String(v);
      // Blank is LEGAL: it means "กลับไปใช้ค่าเริ่มต้น" and is sent as `null`, never as ''.
      if (!text.trim()) return [];
      if (f.kind === 'int' || f.kind === 'float') {
        const n = Number(text);
        if (!Number.isFinite(n)) return [NUMBER_ERROR];
        if (f.kind === 'int' && !Number.isInteger(n)) return [INT_ERROR];
        // `min`/`max` are OPT-IN per descriptor — a field without them keeps accepting anything numeric,
        // so this cannot start rejecting values that were legal before. Declared where the backend would
        // otherwise silently normalise the number away (see TaxPlanningSettingsV2BAL.NormalizeKnowledge*):
        // a box that accepts 0.9 and then behaves as if it were empty is worse than one that says no.
        if (typeof f.min === 'number' && n < f.min) return [RANGE_ERROR(f.min, f.max)];
        if (typeof f.max === 'number' && n > f.max) return [RANGE_ERROR(f.min, f.max)];
        return [];
      }
      // Knowledge: ค่าที่มาจาก picker เป็น JSON ถูกรูปเสมอ — ที่นี่ดักเฉพาะค่าเดิมใน DB ที่พังมาก่อน
      // (แก้มือผ่าน API / import bundle เก่า) ให้ขึ้นแดงและบล็อกบันทึก แทนที่จะถูกล้างเป็น null เงียบ ๆ
      if (f.kind === 'knowledgeCategories') {
        let parsed;
        try {
          parsed = JSON.parse(text);
        } catch (e) {
          return [JSON_ERROR];
        }
        if (!Array.isArray(parsed)) return [KNOWLEDGE_ARRAY_ERROR];
        if (parsed.some((t) => typeof t !== 'string')) return [KNOWLEDGE_TOKEN_ERROR];
        return [];
      }
      if (f.maxlength && text.length > f.maxlength) {
        return [LENGTH_ERROR(f.maxlength)];
      }
      if (NO_MARKER_KEYS.includes(f.key) && text.indexOf(QUICK_REPLY_MARKER) >= 0) {
        return [MARKER_ERROR];
      }
      // 🔴 ว่าง = ถูกกฎหมายเสมอ (ดักไปแล้วด้านบน) — ว่างแปลว่า "กลับไปใช้ชุดกฎเริ่มต้นในโค้ด" ไม่ใช่ "ไม่มีกฎ"
      if (JSON_ARRAY_KEYS.includes(f.key)) {
        let parsed;
        try {
          parsed = JSON.parse(text);
        } catch (e) {
          return [JSON_ERROR];
        }
        if (!Array.isArray(parsed)) return [JSON_ARRAY_ERROR];
      }
      return [];
    },
    /**
     * The value sent for one key. `null` = "no override — the runtime uses the built-in default",
     * which is what an untouched (or cleared) box means, so the form only ever stores real edits.
     * Numbers are coerced to real JS numbers — System.Text.Json rejects "5" for an `int?`,
     * which would surface as a 400 rather than a silent drop, but coercing keeps it clean.
     */
    payloadValue(key) {
      if (this.isAtDefault(key)) return null;
      const f = FIELD_BY_KEY[key];
      const v = this.values[key];
      if (f && f.kind === 'bool') return v === true;
      const text = v === null || v === undefined ? '' : String(v);
      // Knowledge: ส่งเป็น JSON string รูปมาตรฐานเสมอ (ลิสต์ว่างถูกดักไปแล้วที่ `isAtDefault` → null)
      if (f && f.kind === 'knowledgeCategories') {
        return stringifyCategoryTokens(parseCategoryTokens(text));
      }
      if (f && f.kind === 'int') return Math.trunc(Number(text));
      if (f && f.kind === 'float') return Number(text);
      return text;
    },
    async save() {
      // `defaultsError` gates the save too: without the defaults, `payloadValue` cannot tell an
      // untouched field from a real override, so writing anything back is unsafe.
      if (this.loading || this.saving || this.loadError || this.defaultsError || !this.editingId) return;
      this.saveError = '';
      this.saveMessage = '';
      if (this.versionCodeErrors.length) {
        this.tab = 'general';
        this.clearFilters();
        this.saveError = 'ยังบันทึกไม่ได้ — ใส่รหัสเวอร์ชันก่อน';
        return;
      }
      if (this.invalidKeys.length) {
        // Park the user on the first offending field's tab instead of a silent no-op, and drop the
        // filters so nothing can be hiding it.
        const firstBad = this.invalidKeys[0];
        const owner = this.tabs.find((t) => t.fields.some((f) => f.key === firstBad));
        if (owner) this.tab = owner.value;
        else if (EXTRA_FIELD_HOMES[firstBad]) this.tab = EXTRA_FIELD_HOMES[firstBad].tab;
        this.clearFilters();
        this.saveError = 'ยังบันทึกไม่ได้ — แก้ช่องที่มีข้อความสีแดงก่อน';
        return;
      }
      // DIRECT-ASSIGN endpoint: every one of the 87 keys must be present, an omitted key is
      // received as null and clears that override. versionCode/description are identity —
      // they are NOT part of that contract (null there = leave unchanged).
      const payload = {
        versionCode: String(this.identity.versionCode || '').trim(),
        description: String(this.identity.description || ''),
      };
      TAX_PLANNING_SETTINGS_V2_ALL_KEYS.forEach((k) => {
        payload[k] = this.payloadValue(k);
      });
      this.saving = true;
      try {
        const dto = await taxPlanningSettingsV2Api.saveVersion(this.editingId, payload);
        if (dto) this.applyDto(dto);
        else this.baseline = this.snapshot;   // nothing echoed back — the sent state IS the saved state
        this.saveMessage = 'บันทึกเวอร์ชันนี้แล้ว';
        this.$emit('saved', `บันทึกการตั้งค่า Tax Planning V2 เวอร์ชัน ${this.editingCode || ''} แล้ว`);
      } catch (e) {
        const message = (e && e.message) || 'บันทึกการตั้งค่าไม่สำเร็จ';
        this.saveError = message;
        this.$emit('error', message);
      } finally {
        this.saving = false;
      }
    },

    onDialogModel(open) {
      if (!open) this.close();
    },
    close() {
      this.$emit('update:modelValue', false);
    },
  },
};
</script>

<style scoped>
/**
 * Colours are taken from the Vuetify theme (`--v-theme-*` are "R, G, B" triplets), never
 * hard-coded — the console has no custom palette, so anything literal here would drift the day
 * one is added. The neutral ramp below is the only greyscale this file uses.
 */
/**
 * 🔴 Declared on BOTH cards. Each sub-dialog is teleported into its own overlay, so it is NOT a
 * descendant of `.tpsv2` — every `var(--tps-*)` inside one would resolve to nothing (no
 * background, no border, no text colour) if the block lived on `.tpsv2` alone.
 *
 * The ramp is the only greyscale in this file, and every step is used as-is — a colour that is
 * not on it is a bug, not a nuance. Ratios on white: ink 17.4:1 · ink-2 8.3:1 · ink-3 5.6:1.
 */
.tpsv2,
.tpsv2-sub {
  --tps-ink: rgba(var(--v-theme-on-surface), 0.94);
  --tps-ink-2: rgba(var(--v-theme-on-surface), 0.75);
  --tps-ink-3: rgba(var(--v-theme-on-surface), 0.66);
  --tps-line: rgba(var(--v-theme-on-surface), 0.1);
  --tps-line-soft: rgba(var(--v-theme-on-surface), 0.06);
  --tps-panel: rgba(var(--v-theme-on-surface), 0.02);
  --tps-panel-2: rgba(var(--v-theme-on-surface), 0.045);
  /* One horizontal rhythm for every band — header, toolbars, content, footer share this edge. */
  --tps-gutter: 20px;
}

.tpsv2 {
  /**
   * 🔴 The card scrolls itself instead of relying on `<v-dialog scrollable>`. `scrollable` makes
   * `.v-card-text` the one scroller, which cannot hold a section rail that stays put beside the
   * fields (and it is what squashed the old tab bar to a sliver: horizontal `.v-tabs` keeps
   * `flex-shrink: 1` and its `.v-slide-group` is `overflow: hidden`, so its automatic minimum
   * size resolves to 0). Here every chrome band is `flex: none` and only `.tpsv2-scroll` /
   * `.tpsv2-listscroll` scroll — the height is bounded below, not by the parent.
   */
  display: flex;
  flex-direction: column;
  /* Vuetify already gives the dialog card `overflow-y: auto`; leaving it on would wrap our own
     scrollers in a second one. The bands below own all the scrolling. */
  overflow: hidden;
}

/**
 * 🔴 The height must be set on `.v-overlay__content`, NOT on the card. Vuetify's
 * `.v-dialog > .v-overlay__content > .v-card { flex: 1 1 var(--v-card-height, 100%) }`
 * (VDialog.css:18-26) makes the card a flex item of the overlay, so a `height` declared on the
 * card is resolved away by the flex basis and the dialog silently re-sizes per section.
 * The overlay content is teleported to `.v-overlay-container`, but Vuetify copies this
 * component's scope id onto it (`useScopeId`), so the scoped `:deep()` below still matches.
 */
.tpsv2-overlay :deep(.v-overlay__content) { max-height: calc(100vh - 96px); }

/* Fixed height in the editor, so switching section does not resize the dialog under the pointer. */
.tpsv2-overlay--editor :deep(.v-overlay__content) { height: calc(100vh - 96px); }

@media (max-width: 600px) {
  .tpsv2-overlay :deep(.v-overlay__content) { max-height: calc(100vh - 32px); }
  .tpsv2-overlay--editor :deep(.v-overlay__content) { height: calc(100vh - 32px); }
}

/* ── title bar ─────────────────────────────────────────────────────────────── */
.tpsv2-head {
  flex: none;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px var(--tps-gutter);
}

.tpsv2-head--sub { padding: 14px var(--tps-gutter); }

.tpsv2-head-mark {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.16);
}

.tpsv2-head-text {
  flex: 1 1 auto;
  min-width: 0;
}

/* The global reset zeroes heading weight, so titles set their own. */
.tpsv2-head-title {
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1.35;
  letter-spacing: 0.005em;
}

.tpsv2-head-sub {
  font-size: 0.75rem;
  line-height: 1.4;
  opacity: 0.82;
}

.tpsv2-livetag {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
  font-size: 0.75rem;
  font-weight: 600;
}

.tpsv2-livedot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.22);
}

.tpsv2-livedot--solid {
  background: rgb(var(--v-theme-success));
  box-shadow: 0 0 0 3px rgba(var(--v-theme-success), 0.16);
}

/* ── level 1 · toolbar + list ──────────────────────────────────────────────── */
.tpsv2-listbar {
  flex: none;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 14px var(--tps-gutter);
  border-bottom: 1px solid var(--tps-line);
}

.tpsv2-listmeta {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  min-width: 0;
  font-size: 0.8rem;
  color: var(--tps-ink-2);
}

.tpsv2-listcount {
  font-weight: 600;
  color: var(--tps-ink);
}

.tpsv2-listactive {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

.tpsv2-listactive strong { font-weight: 600; color: var(--tps-ink); }
/* Warning orange on white is 2.4:1 — the ink ramp carries the text, the icon carries the state. */
.tpsv2-listactive.is-none { color: var(--tps-ink-2); }

.tpsv2-listscroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 18px var(--tps-gutter) 24px;
}

.tpsv2-hintline {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin-bottom: 16px;
  font-size: 0.8rem;
  line-height: 1.6;
  color: var(--tps-ink-3);
}

.tpsv2-hintline :deep(.v-icon) { margin-top: 2px; flex: none; }

.tpsv2-skeleton :deep(.v-skeleton-loader) { background: transparent; }

.tpsv2-table {
  border: 1px solid var(--tps-line);
  border-radius: 10px;
  overflow: hidden;
}

.tpsv2-table :deep(th) {
  font-weight: 600 !important;
  font-size: 0.75rem !important;
  letter-spacing: 0.01em;
  color: var(--tps-ink-3) !important;
  white-space: nowrap;
  background: var(--tps-panel);
}

.tpsv2-table :deep(tbody tr) { transition: background-color 160ms ease; }
.tpsv2-table :deep(tbody tr:hover) { background: var(--tps-panel-2); }
.tpsv2-table :deep(tbody tr:last-child td) { border-bottom: none; }

/* The live version reads as live at a glance — tint only; the chip carries the semantics. */
.tpsv2-row--active { background: rgba(var(--v-theme-success), 0.06); }
.tpsv2-table :deep(tr.tpsv2-row--active:hover) { background: rgba(var(--v-theme-success), 0.1); }

.tpsv2-vcode {
  display: inline-block;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
  cursor: pointer;
  text-align: start;
  border-radius: 4px;
  transition: color 150ms ease;
}

.tpsv2-vcode:hover { text-decoration: underline; }
.tpsv2-vcode:focus-visible { outline: 2px solid rgb(var(--v-theme-primary)); outline-offset: 2px; }

.tpsv2-vdesc {
  font-size: 0.8rem;
  line-height: 1.45;
  color: var(--tps-ink-3);
  max-width: 46ch;
}

.tpsv2-when { font-size: 0.8rem; color: var(--tps-ink-2); line-height: 1.45; }
.tpsv2-muted { font-size: 0.8rem; color: var(--tps-ink-3); }

/**
 * "ใช้งานอยู่" — a green-filled chip puts white on #4CAF50 (2.78:1) and shouts on a screen whose
 * job is to be trusted. Tint + ring + dot carry the state; the ink ramp carries the word.
 */
.tpsv2-statustag {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 3px 11px 3px 9px;
  border-radius: 999px;
  border: 1px solid rgba(var(--v-theme-success), 0.38);
  background: rgba(var(--v-theme-success), 0.1);
  color: var(--tps-ink);
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.tpsv2-warnline {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 0.85rem;
  color: var(--tps-ink);
}

.tpsv2-warnline strong { font-weight: 700; color: rgb(var(--v-theme-error)); }

.tpsv2-rowactions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
}

/* Hairline between "do something to this version" and "duplicate / destroy it". */
.tpsv2-rowactions-sep {
  width: 1px;
  height: 18px;
  margin: 0 5px;
  background: var(--tps-line);
}

.tpsv2-emptyrow:hover { background: transparent !important; }
.tpsv2-emptyrow td { padding: 0 !important; border-bottom: none !important; }
/* Inside the table the surrounding border already frames it — no second box. */
.tpsv2-emptyrow .tpsv2-empty { border: none; border-radius: 0; background: transparent; }

.tpsv2-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 52px 24px 56px;
  text-align: center;
  border: 1px dashed var(--tps-line);
  border-radius: 12px;
  background: var(--tps-panel);
}

.tpsv2-empty-icon { color: var(--tps-ink-3); }
.tpsv2-empty-title { font-size: 0.9rem; font-weight: 600; color: var(--tps-ink); }

.tpsv2-empty-text {
  max-width: 52ch;
  font-size: 0.8rem;
  line-height: 1.6;
  color: var(--tps-ink-3);
  margin-bottom: 4px;
}

/* ── level 2 · toolbar ─────────────────────────────────────────────────────── */
.tpsv2-toolbar {
  flex: none;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 10px var(--tps-gutter);
  border-bottom: 1px solid var(--tps-line);
  background: var(--tps-panel);
}

.tpsv2-search { max-width: 300px; flex: 1 1 200px; }
.tpsv2-search :deep(.v-field) { background: rgb(var(--v-theme-surface)); }
.tpsv2-clearfilter { font-size: 0.75rem; letter-spacing: normal; text-transform: none; }

/* ── level 2 · rail + scroller ─────────────────────────────────────────────── */
.tpsv2-split {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  align-items: stretch;
}

.tpsv2-rail {
  flex: none;
  width: 236px;
  padding: 10px 8px;
  border-inline-end: 1px solid var(--tps-line);
  background: var(--tps-panel);
  overflow-y: auto;
}

.tpsv2-railtab {
  justify-content: flex-start !important;
  min-height: 42px !important;
  padding: 0 10px !important;
  margin-bottom: 2px;
  border-radius: 8px;
  /* Vuetify upper-cases button text; "NODE PROMPT" next to Thai labels reads as shouting. */
  text-transform: none !important;
  letter-spacing: normal !important;
  font-size: 0.85rem !important;
  font-weight: 500 !important;
  color: var(--tps-ink-2);
}

.tpsv2-railtab :deep(.v-btn__content) {
  width: 100%;
  justify-content: flex-start;
  gap: 2px;
}

.tpsv2-railtab.v-tab--selected {
  background: rgba(var(--v-theme-primary), 0.09);
  font-weight: 600 !important;
}

/* Still legible (and still clickable) — a dimmed section is filtered, not disabled. */
.tpsv2-railtab.is-dimmed { opacity: 0.62; }

.tpsv2-railtab-label {
  flex: 1 1 auto;
  min-width: 0;
  text-align: start;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tpsv2-railtab-alert { flex: none; margin-inline-start: 4px; }

.tpsv2-scroll {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
}

.tpsv2-pane { padding: 18px var(--tps-gutter) 28px; }

/* ── level 2 · section note ────────────────────────────────────────────────── */
.tpsv2-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 18px;
  padding: 9px 10px 9px 12px;
  border-radius: 8px;
  background: var(--tps-panel);
  border: 1px solid var(--tps-line-soft);
  color: var(--tps-ink-3);
}

.tpsv2-note.is-warning {
  background: rgba(var(--v-theme-warning), 0.07);
  border-color: rgba(var(--v-theme-warning), 0.22);
}

.tpsv2-note :deep(.v-icon) { flex: none; margin-top: 2px; }

.tpsv2-note-text {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 0.8rem;
  line-height: 1.6;
}

.tpsv2-note-text.is-clamped {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.tpsv2-note-toggle { flex: none; margin-top: -2px; }

/* ── level 2 · "cannot save yet" summary ───────────────────────────────────── */
.tpsv2-invalid {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 18px;
  padding: 12px 14px;
  border-radius: 10px;
  background: rgba(var(--v-theme-error), 0.07);
  border: 1px solid rgba(var(--v-theme-error), 0.24);
}

.tpsv2-invalid :deep(.v-icon) { flex: none; margin-top: 1px; }
.tpsv2-invalid-body { min-width: 0; }

.tpsv2-invalid-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: rgb(var(--v-theme-error));
  margin-bottom: 7px;
}

.tpsv2-invalid-chips { display: flex; flex-wrap: wrap; gap: 6px; }

.tpsv2-jump {
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid rgba(var(--v-theme-error), 0.4);
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-error));
  font: inherit;
  font-size: 0.75rem;
  cursor: pointer;
  transition: background-color 150ms ease;
}

.tpsv2-jump:hover { background: rgba(var(--v-theme-error), 0.1); }
.tpsv2-jump:focus-visible { outline: 2px solid rgb(var(--v-theme-error)); outline-offset: 1px; }

/* ── level 2 · field blocks ────────────────────────────────────────────────── */
/* Same block vocabulary as the sibling TaxQuestionItemV2Dialog (.tq2-block). */
.tpsv2-group {
  padding: 16px;
  margin-bottom: 16px;
  border: 1px solid var(--tps-line);
  border-radius: 10px;
  background: rgba(var(--v-theme-on-surface), 0.012);
}

.tpsv2-group-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.tpsv2-group-title {
  flex: none;
  font-size: 0.9rem;
  font-weight: 700;
  color: rgb(var(--v-theme-primary));
  line-height: 1.4;
}

.tpsv2-group-rule {
  flex: 1 1 auto;
  height: 1px;
  background: var(--tps-line);
}

/**
 * Two columns of fields, laid out by CSS grid rather than v-row/v-col: the gutters must be wider
 * than the gaps INSIDE a field, or a two-line hint sits as close to the neighbouring column's
 * control as to its own. Within-field spacing is 4-5px, between fields 20px / 26px.
 * `is-wide` = the old `cols: 12` (every textarea) — it spans the full width.
 */
.tpsv2-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 26px;
  row-gap: 20px;
}

.tpsv2-fields--identity { grid-template-columns: minmax(0, 1fr) minmax(0, 2fr); }
.tpsv2-fields > .is-wide { grid-column: 1 / -1; }

@media (max-width: 959px) {
  .tpsv2-fields,
  .tpsv2-fields--identity { grid-template-columns: minmax(0, 1fr); }
}

.tpsv2-field { margin-bottom: 0; }

/* Only holds the label now — the 4px keeps the gap to the control below unchanged. */
.tpsv2-field-head {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.tpsv2-field-label {
  flex: 0 1 auto;
  min-width: 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--tps-ink);
  line-height: 1.5;
}

.tpsv2-field-label em {
  font-style: normal;
  color: rgb(var(--v-theme-error));
}

.tpsv2-hint {
  margin-top: 5px;
  font-size: 0.78rem;
  line-height: 1.6;
  color: var(--tps-ink-3);
  max-width: 76ch;
}

.tpsv2-field.is-invalid .tpsv2-field-label { color: rgb(var(--v-theme-error)); }

.tpsv2-nomatch {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 44px 20px;
  color: var(--tps-ink-3);
  font-size: 0.8rem;
}

/* ── level 2 · save status strip + footer ──────────────────────────────────── */
.tpsv2-status {
  flex: none;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 10px var(--tps-gutter);
  font-size: 0.8rem;
  border-top: 1px solid var(--tps-line);
}

.tpsv2-status.is-error {
  background: rgba(var(--v-theme-error), 0.09);
  color: rgb(var(--v-theme-error));
}

/* Success green on a green tint is 2.5:1. Tint + icon carry the state; the ink ramp carries
   the words, which is the part that has to be read. */
.tpsv2-status.is-ok {
  background: rgba(var(--v-theme-success), 0.11);
  color: var(--tps-ink);
}

.tpsv2-status.is-ok :deep(.v-icon) { color: rgb(var(--v-theme-success)); }

.tpsv2-foot {
  flex: none;
  gap: 8px;
  flex-wrap: wrap;
  padding: 12px var(--tps-gutter);
  border-top: 1px solid var(--tps-line);
}

.tpsv2-audit {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.75rem;
  color: var(--tps-ink-3);
  line-height: 1.5;
  min-width: 0;
}

.tpsv2-dirty {
  flex: none;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--tps-ink);
  background: rgba(var(--v-theme-warning), 0.22);
}

/* ── sub-dialogs ───────────────────────────────────────────────────────────── */
.tpsv2-sub { display: flex; flex-direction: column; }
.tpsv2-subbody { padding: 20px var(--tps-gutter) 8px; display: flex; flex-direction: column; gap: 14px; }
.tpsv2-sub .tpsv2-field { margin-bottom: 0; }
.tpsv2-confirm-lead { font-size: 0.85rem; color: var(--tps-ink); }

.tpsv2-confirm-target {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px 14px;
  border-radius: 9px;
  background: var(--tps-panel-2);
  border: 1px solid var(--tps-line);
}

.tpsv2-confirm-code { font-size: 0.9rem; font-weight: 700; color: var(--tps-ink); }

/* ── shared ────────────────────────────────────────────────────────────────── */
.tpsv2-alert { font-size: 0.8rem; line-height: 1.6; }

/* Warning band of the version list (import / restore that succeeded with something to read). */
.tpsv2-warntitle { font-weight: 600; }
.tpsv2-warnlist { margin: 4px 0 0; padding-left: 18px; font-size: 0.78rem; line-height: 1.65; }
.tpsv2-alert :deep(.v-alert__prepend) { align-self: flex-start; margin-inline-end: 10px; }

/**
 * A Vuetify tonal alert paints its text in the alert's own colour on a 12% tint of that SAME
 * colour: warning 2.14:1, success 2.49:1, info 3.1:1 — all below AA, and these carry the copy that
 * explains a failed catalog load or an unresolvable model key. Tint + icon keep the semantics;
 * the words move to the ink ramp. (Error is already ~7:1 and keeps its own colour.)
 */
.tpsv2-alert.text-warning,
.tpsv2-alert.text-success,
.tpsv2-alert.text-info { color: var(--tps-ink) !important; }

.tpsv2-alert.text-warning :deep(.v-alert__prepend .v-icon) { color: rgb(var(--v-theme-warning)); }
.tpsv2-alert.text-success :deep(.v-alert__prepend .v-icon) { color: rgb(var(--v-theme-success)); }
.tpsv2-alert.text-info :deep(.v-alert__prepend .v-icon) { color: rgb(var(--v-theme-info)); }

/* ── การ์ด Time Context ────────────────────────────────────────────────────── */
/**
 * ยกมาจาก `views/Flow.vue` (`.section-label` :32175 · `.switch-*` :32397, :32552) เพื่อให้การ์ดนี้
 * หน้าตาตรงกับ node AI Model — 🔴 CSS ของทั้งสองไฟล์เป็น `scoped` จึงไม่ inherit ข้ามไฟล์ ต้องคัดลอกมา
 * ต่างจากต้นฉบับ 2 อย่าง โดยตั้งใจ:
 *   1. สีที่ต้นฉบับ hard-code ไว้ (#1976D2 / rgba(25,118,210,…) / #1a1a1a / #666) เปลี่ยนมาใช้
 *      design token ของ dialog (`--v-theme-primary`, `--tps-*`) ไม่งั้นการ์ดจะเป็นสีเดียวที่ไม่ขยับ
 *      ตามธีมทั้งหน้า
 *   2. `margin-bottom` ของหัวข้อเป็น 0 — ระยะห่างมาจาก `.tpsv2-group-head` ที่ห่อมันอยู่แล้ว
 */
.section-label {
  flex: none;
  font-size: 14px;
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1.4;
  margin-bottom: 0;
  position: relative;
  padding-left: 12px;
}

.section-label::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 16px;
  background: rgb(var(--v-theme-primary));
  border-radius: 2px;
}

.switch-option-compact {
  border: 1px solid var(--tps-line);
  border-radius: 12px;
  background: rgb(var(--v-theme-surface));
  transition: border-color 0.3s ease, box-shadow 0.3s ease, background 0.3s ease;
}

.switch-option-compact:hover {
  border-color: rgba(var(--v-theme-primary), 0.3);
  box-shadow: 0 2px 8px rgba(var(--v-theme-on-surface), 0.08);
}

.switch-option-compact :deep(.v-switch) {
  flex: 0 0 auto;
  margin-left: 12px;
}

.switch-active-bg {
  background: linear-gradient(
    135deg,
    rgba(var(--v-theme-primary), 0.05) 0%,
    rgba(var(--v-theme-primary), 0.02) 100%
  ) !important;
  border-color: rgba(var(--v-theme-primary), 0.3) !important;
}

.switch-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--tps-ink);
  line-height: 1.2;
}

.switch-description {
  font-size: 11.5px;
  color: var(--tps-ink-3);
  margin-top: 3px;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

/* Stacked rail on narrow screens: the sections become a scrollable strip above the fields. */
@media (max-width: 959px) {
  .tpsv2-split { flex-direction: column; }
  .tpsv2-rail {
    width: 100%;
    /* `.v-tabs { height: var(--v-tabs-height) }` locks the horizontal bar to 48px with
       border-box sizing — vertical padding would eat 8px out of the tabs themselves. */
    height: auto;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 4px 8px;
    border-inline-end: none;
    border-bottom: 1px solid var(--tps-line);
  }
  .tpsv2-railtab { margin-bottom: 0; }
  .tpsv2-railtab-label { max-width: 14ch; }
  .tpsv2 { --tps-gutter: 16px; }
}

@media (prefers-reduced-motion: reduce) {
  .tpsv2 :deep(*) {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}
</style>
